import React, { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Customized } from "recharts";
import { useTheme } from "../lib/ThemeContext.jsx";
import {row, rowBetween, col, card, subText} from "../lib/styles.js";
import { formatDate, formatDateTime, getDateFormat } from "../lib/format.js";
import { LS } from "../lib/storage.js";
import {Label, Info, DateInp, BottomSheet, Btn, Tooltip as UITooltip} from "../components/ui.jsx";
import KpiHistoryModal from "../components/KpiHistoryModal.jsx";
import BatchArrivalDialog from "../components/BatchArrivalDialog.jsx";
import ExportModal from "../components/ExportModal.jsx";
import AddTransactionModal from "../components/AddTransactionModal.jsx";
import FieldChangeMarker from "../components/FieldChangeMarker.jsx";
import {FONT, RADIUS, SPRING, ORDER_COLORS} from "../lib/theme.js";
import { parsePlannerDate } from "./planner/plannerUtils.js";
import { aggregateLeadTimes, nextUnusedColor, ORDER_PALETTE } from "./orders/orderUtils.js";
import { useCached } from "../lib/computeCache.js";
import {
  fmt, fmtFull, mkKey, MONTH_NAMES, currSym, parseDate, getDaysInMonth,
  computeDailyData, computeDeliveryExpenses, generateOrderExpenses, generateDecisionRecurringItems,
  generateDecisionOneTimeItems, computeInventorySummary, computeStockTimeSeries, computeStockoutDate, computeBreakevenUnits, generateAdSpendExpenses, getPoolForMarketplace, getEffectivePool, synthesizePoolForSelection,
  generateRecurringItems, aggregateWeekly, aggregateMonthly,
  getNextPaymentDue, calcRunway, effectiveUnits, getMonthVars,
  payoutForMkt, effectivePayoutForMkt, getOrderTotalCt, detectAppIssues, analyzeAffordability, analyzeDecisionAffordability, computeHealthScore,
  getOrderCashEvents,
} from "../lib/calculations.js";

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const h = e => setMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return mobile;
}

// Mirror the Planner's DOT_PALETTE exactly so colors match
const DOT_PALETTE = [
  "#FF3B30","#FF9500","#FFCC00","#34C759","#00C7BE",
  "#8b5cf6","#5E5CE6","#BF5AF2","#FF2D55","#30D158",
  "#64D2FF","#FFD60A","#FF6961","#4CD964","#007AFF",
];
function plannerDotColor(decisions, d) {
  const idx = decisions.findIndex(x => x.id === d.id);
  return DOT_PALETTE[idx % DOT_PALETTE.length];
}

function decisionDateKey(d) {
  if (!d?.date) return null;
  const dt = d.date;
  if (dt.prec==="month"&&dt.month) return dt.month;
  if (dt.prec==="day"&&dt.day) { const p=dt.day.split("."); return p.length===3?`${p[2]}-${p[1].padStart(2,"0")}`:null; }
  if (dt.prec==="quarter"&&dt.quarter) { const [y,q]=dt.quarter.split("-Q"); return `${y}-${String((parseInt(q)-1)*3+1).padStart(2,"0")}`; }
  return null;
}

function DropMenu({ open, onClose, children, width=220, align="left" }) {
  const T = useTheme();
  const mob = typeof window !== "undefined" && window.innerWidth < 768;
  if (!open) return null;
  if (mob) return createPortal(
    <div onClick={e => e.target===e.currentTarget && onClose?.()} style={{ position:"fixed", inset:0, zIndex:9000, background:"rgba(0,0,0,0.3)" }}>
      <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:9001, background:T.card, border:`1px solid ${T.border}`, borderRadius:"16px 16px 0 0", boxShadow:"0 -10px 40px rgba(0,0,0,0.3)", maxHeight:"60vh", overflowY:"auto", animation:"sfSlideUp 0.2s ease both", padding:"8px 0" }}>
        <div style={{ width:40, height:4, borderRadius:2, background:T.border, margin:"4px auto 8px" }}/>
        {children}
      </div>
    </div>, document.body
  );
  return (
    <div style={{ position:"absolute", top:"calc(100% + 6px)", [align==="right"?"right":"left"]:0, zIndex:600, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, boxShadow:"0 20px 56px rgba(0,0,0,0.35)", width, overflow:"hidden", animation:"dashMenuIn 0.2s cubic-bezier(0.34,1.4,0.64,1) both" }}>
      {children}
    </div>
  );
}

// DropWrapper: wraps trigger + menu together — click outside BOTH closes, re-clicking trigger toggles.
// Desktop popup is portaled to document.body with position:fixed so it can't be clipped by parent
// overflow:hidden cards (e.g. the Next Payments KPI card containing the Overdue button).
function DropWrapper({ open, onClose, children, trigger, width=220, align="left" }) {
  const ref = useRef(null);
  const popupRef = useRef(null);
  const [popupPos, setPopupPos] = useState(null);
  const mob = typeof window !== "undefined" && window.innerWidth < 768;
  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (mob) return;
      if (ref.current && ref.current.contains(e.target)) return;
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [open, onClose, mob]);
  // Compute portal position relative to trigger; recompute on scroll/resize
  useEffect(() => {
    if (!open || mob || !ref.current) return;
    function compute() {
      const r = ref.current.getBoundingClientRect();
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = align === "right" ? r.right - width : r.left;
      left = Math.max(margin, Math.min(left, vw - width - margin));
      let top = r.bottom + 6;
      // If popup would go off bottom, flip above
      const estHeight = popupRef.current?.offsetHeight ?? 280;
      if (top + estHeight > vh - margin) {
        top = Math.max(margin, r.top - estHeight - 6);
      }
      setPopupPos({ top, left });
    }
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open, mob, align, width]);
  const T = useTheme();
  return (
    <div ref={ref} style={{ position:"relative" }}>
      {trigger}
      {open && mob ? createPortal(
        <div onClick={e => e.target===e.currentTarget && onClose?.()} style={{ position:"fixed", inset:0, zIndex:9000, background:"rgba(0,0,0,0.3)" }}>
          <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:9001, background:T.card, border:`1px solid ${T.border}`, borderRadius:"16px 16px 0 0", boxShadow:"0 -10px 40px rgba(0,0,0,0.3)", maxHeight:"60vh", overflowY:"auto", animation:"sfSlideUp 0.2s ease both", padding:"8px 0" }}>
            <div style={{ width:40, height:4, borderRadius:2, background:T.border, margin:"4px auto 8px" }}/>
            {children}
          </div>
        </div>, document.body
      ) : open && popupPos ? createPortal(
        <div ref={popupRef} style={{ position:"fixed", top:popupPos.top, left:popupPos.left, zIndex:9500, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, boxShadow:"0 20px 56px rgba(0,0,0,0.5)", width, maxHeight:"calc(100vh - 40px)", overflow:"auto", animation:"dashMenuIn 0.2s cubic-bezier(0.34,1.4,0.64,1) both" }}>
          {children}
        </div>, document.body
      ) : null}
    </div>
  );
}

function MenuSep() { const T=useTheme(); return <div style={{ height:1, background:T.border, margin:"2px 0" }}/>; }

function MItem({ label, selected, onClick, radio=true, color }) {
  const T = useTheme(); const c = color||T.blue;
  return (
    <button onClick={onClick} style={{ display:"flex", alignItems:"center", gap:9, width:"100%", padding:"8px 14px", background:selected?c+"15":"transparent", border:"none", cursor:"pointer", fontFamily:FONT, fontSize:13, color:selected?c:T.text, fontWeight:selected?700:400, transition:"background 0.1s", textAlign:"left" }}>
      {radio
        ? <div style={{ width:13,height:13,borderRadius:"50%",border:`2px solid ${selected?c:T.border}`,background:selected?c:"transparent",flexShrink:0,transition:"all 0.1s" }}/>
        : <div style={{ width:13,height:13,borderRadius:3,border:`2px solid ${selected?c:T.border}`,background:selected?c:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>{selected&&<span style={{ color:"#fff",fontSize:9,fontWeight:900 }}>✓</span>}</div>
      }
      {label}
    </button>
  );
}

function MSection({ label, children }) {
  const T = useTheme();
  return (
    <div>
      {label && <div style={{ fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:1,textTransform:"uppercase",padding:"8px 14px 3px" }}>{label}</div>}
      {children}
    </div>
  );
}

function CBtn({ label, active, onClick, pulse }) {
  const T = useTheme();
  return (
    <div style={{ position:"relative" }}>
      <button onClick={onClick} style={{ display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:RADIUS.md,background:active?T.accent+"18":"transparent",border:`1px solid ${active?T.accent:T.border}`,color:active?T.accent:T.text,fontSize:12,fontWeight:active?700:500,fontFamily:FONT,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.15s" }}>
        {label} <span style={{ fontSize:8,opacity:0.6,transition:"transform 0.2s",display:"inline-block",transform:active?"rotate(180deg)":"none" }}>▼</span>
      </button>
      {pulse && <div style={{ position:"absolute",top:-4,right:-4,width:14,height:14,borderRadius:"50%",background:T.orange,color:"#111",fontSize:8,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",animation:"dotPulse 2s ease-in-out infinite" }}>{pulse}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label, decisions, showDecisions, orders, orderDots, showOrders, onNavigate, decDots, dateFormat }) {
  const T = useTheme();
  if (!active||!payload?.length) return null;
  // ── Hardened against partial / undefined props ────────────────────────────
  // Recharts can pass weirdly-shaped payloads on edge cases (chart switching
  // view mode mid-hover, chartData regenerated, etc.). Every array prop gets
  // a `|| []` fallback so we never crash on .filter / .find / .map. Numbers
  // get isFinite checks so NaN never reaches `.toLocaleString()`.
  const safeDecisions = Array.isArray(decisions) ? decisions : [];
  const safeOrderDots = Array.isArray(orderDots) ? orderDots : [];
  const safeDecDots   = Array.isArray(decDots)   ? decDots   : [];
  const balRaw = (payload.find(p=>p.dataKey==="balance") || payload[0])?.value;
  const bal = Number.isFinite(Number(balRaw)) ? Number(balRaw) : 0;
  const hitDec = showDecisions
    ? safeDecisions.filter(d=>{ const k=decisionDateKey(d); return k&&(label||"").startsWith(k); })
    : [];
  const hitOrd = showOrders
    ? safeOrderDots.filter(x => x?.pt && (x.pt.label===label || x.pt.key===label))
    : [];
  // Income + expenses for the hovered period — available on all
  // aggregations (day/week/month) because both aggregateWeekly and
  // aggregateMonthly carry them through from dailyData. NaN-safe.
  const pt = payload[0]?.payload || {};
  const incomeRaw = Number(pt.income);
  const expensesRaw = Number(pt.expenses);
  const income = Number.isFinite(incomeRaw) ? incomeRaw : 0;
  const expenses = Number.isFinite(expensesRaw) ? expensesRaw : 0;
  const net = income - expenses;
  return (
    <div style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.md,padding:"10px 14px",boxShadow:"0 8px 24px rgba(0,0,0,0.25)",fontFamily:FONT,minWidth:180,maxWidth:280 }}>
      <div style={{ fontSize:11,color:T.textSub,marginBottom:3 }}>{payload[0]?.payload?.fullLabel || label}</div>
      <div style={{ fontSize:17,fontWeight:800,color:bal>=0?T.accent:T.red,marginBottom:4 }}>{bal<0?"-":""}{currSym()}{Math.round(Math.abs(bal)).toLocaleString()}</div>
      {/* Period cashflow — income + expenses for this week/month */}
      {(income > 0 || expenses > 0) && (
        <div style={{
          marginTop:2, marginBottom:6, padding:"6px 8px",
          background:T.surface, borderRadius:RADIUS.sm,
          display:"grid", gridTemplateColumns:"auto 1fr", gap:"2px 10px",
          fontSize:11, fontVariantNumeric:"tabular-nums",
        }}>
          <span style={{ color:T.textSub }}>Income</span>
          <span style={{ color:T.green, fontWeight:700, textAlign:"right" }}>
            +{currSym()}{Math.round(income).toLocaleString()}
          </span>
          <span style={{ color:T.textSub }}>Expenses</span>
          <span style={{ color:T.red, fontWeight:700, textAlign:"right" }}>
            -{currSym()}{Math.round(expenses).toLocaleString()}
          </span>
          <span style={{ color:T.textSub, fontWeight:600, borderTop:`1px dashed ${T.border}`, paddingTop:2 }}>Net</span>
          <span style={{ color: net >= 0 ? T.green : T.red, fontWeight:800, textAlign:"right", borderTop:`1px dashed ${T.border}`, paddingTop:2 }}>
            {net >= 0 ? "+" : "-"}{currSym()}{Math.round(Math.abs(net)).toLocaleString()}
          </span>
          {/* Blocked in Inventory — stock value + units at this chart point */}
          {(() => {
            const invVal = Number(pt.inventoryValue);
            const invUni = Number(pt.inventoryUnits);
            if (!Number.isFinite(invVal) || invVal === 0) return null;
            return <>
              <span style={{ color:T.textSub, paddingTop:4 }}>📦 Blocked in inv.</span>
              <span style={{ color:"#f59e0b", fontWeight:700, textAlign:"right", paddingTop:4 }}>
                {currSym()}{Math.round(invVal).toLocaleString()}
              </span>
              {Number.isFinite(invUni) && invUni > 0 && <>
                <span style={{ color:T.textSub, fontSize:10 }}></span>
                <span style={{ color:T.textSub, fontSize:10, textAlign:"right" }}>
                  {Math.round(invUni).toLocaleString()} units
                </span>
              </>}
            </>;
          })()}
        </div>
      )}
      {(()=>{
        const consRaw = payload.find(p=>p.dataKey==="conservative")?.value;
        const optRaw  = payload.find(p=>p.dataKey==="optimistic")?.value;
        const cons = Number.isFinite(Number(consRaw)) ? Number(consRaw) : null;
        const opt  = Number.isFinite(Number(optRaw))  ? Number(optRaw)  : null;
        if (cons==null&&opt==null) return null;
        return (
          <div style={{ display:"flex", gap:8, fontSize:12, marginBottom:hitDec.length||hitOrd.length?6:0 }}>
            {cons!=null&&<span style={{ color:T.red, fontWeight:600 }}>Conservative: {currSym()}{Math.round(cons).toLocaleString()}</span>}
            {opt!=null&&<span style={{ color:T.green, fontWeight:600 }}>Optimistic: {currSym()}{Math.round(opt).toLocaleString()}</span>}
          </div>
        );
      })()}
      {hitDec.map(d=>{
        const c2 = safeDecDots.find(dd=>dd?.d?.id===d.id)?.color || plannerDotColor(safeDecisions, d);
        const dt = d.date;
        const dateStr = dt?.prec==="day"?dt.day:dt?.prec==="month"?dt.month:dt?.prec==="quarter"?dt.quarter:"";
        const netImpact = (d.impacts||[]).reduce((s,imp)=>{
          const t = imp.type||"";
          if (t==="one_time_cost"||t==="new_expense") return s - (Number(imp.amount)||0);
          if (t==="cash_in") return s + (Number(imp.amount)||0);
          if (t==="loan") return s + (Number(imp.loanAmount)||0) - (Number(imp.repayment)||0);
          if (t==="income_pct"||t==="units_pct") return s; // % modifiers shown separately
          return s;
        },0);
        return (
          <div key={d.id} onClick={()=>onNavigate?.("planner",{decisionId:d.id})}
            style={{ marginTop:4,padding:"6px 9px",background:c2+"18",borderRadius:6,fontSize:11,color:c2,fontWeight:600,cursor:"pointer",border:`1px solid ${c2}33` }}>
            <div style={{ ...rowBetween(8),marginBottom:3 }}>
              <span style={{ display:"flex",alignItems:"center",gap:5 }}>
                <span style={{ fontSize:13 }}>{d.icon||"📌"}</span>
                <span style={{ fontSize:12,fontWeight:700 }}>{d.name}</span>
              </span>
              {netImpact!==0&&<span style={{ fontSize:12,fontWeight:800,color:netImpact>0?T.green:T.red }}>
                {netImpact>0?"+":"-"}{currSym()}{Math.round(Math.abs(netImpact)).toLocaleString()}
              </span>}
            </div>
            {dateStr&&<div style={{ fontSize:10,color:T.textSub,marginBottom:1 }}>📅 {/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? formatDate(dateStr, dateFormat) : dateStr}</div>}
            {d.notes&&<div style={{ fontSize:10,color:T.textSub,marginBottom:1,fontStyle:"italic",lineHeight:1.4 }}>{d.notes.slice(0,80)}{d.notes.length>80?"…":""}</div>}
            {d.impacts?.length>0&&<div style={{ display:"flex",gap:8,fontSize:10,color:T.textSub }}>
              <span>📋 {d.impacts.length} impact{d.impacts.length!==1?"s":""}</span>
            </div>}
            <div style={{ fontSize:9,color:T.textSub,marginTop:2 }}>Click to open in Planner</div>
          </div>
        );
      })}
      {hitOrd.map((entry)=>{
        // Defensive — Recharts can hand us partial entries during view changes
        if (!entry?.o) return null;
        const { o, col, totalCt, totalCost } = entry;
        const colText = col?.text || T.textSub;
        const totalCostNum = Number(totalCost);
        const safeTotalCost = Number.isFinite(totalCostNum) ? totalCostNum : 0;
        const safeTotalCt = Number.isFinite(Number(totalCt)) ? Number(totalCt) : 0;
        // Find first upcoming payment term
        const firstPt = (o.paymentTerms||[]).filter(pt=>!pt.paid&&pt.date).sort((a,b)=>String(a.date).localeCompare(String(b.date)))[0];
        const firstPayAmt = firstPt ? Math.round((Number(o.amount)||0)*(Number(firstPt.pct)||0)/100) : null;
        return (
          <div key={o.id} onClick={()=>onNavigate?.("orders",{expandId:o.id})}
            style={{ marginTop:4,padding:"6px 9px",background:colText+"18",borderRadius:6,fontSize:11,color:colText,fontWeight:600,cursor:"pointer" }}>
            <div style={{ ...rowBetween(8),marginBottom:3 }}>
              <span>📦 {o.number || "(no number)"}</span>
              <span style={{ fontSize:12,fontWeight:800 }}>{currSym()}{Math.round(safeTotalCost).toLocaleString()}</span>
            </div>
            {o.orderDate&&<div style={{ fontSize:10,color:T.textSub,marginBottom:1 }}>📅 {formatDate(o.orderDate, dateFormat)}</div>}
            <div style={{ display:"flex",gap:10,fontSize:10,color:T.textSub }}>
              {Number(o.unitCount)>0&&<span>📊 {Number(o.unitCount).toLocaleString()} units</span>}
              {safeTotalCt>0&&<span>📦 {safeTotalCt} containers</span>}
            </div>
            {firstPt&&<div style={{ fontSize:10,color:colText,marginTop:3,fontWeight:700 }}>
              Next: {currSym()}{(firstPayAmt||0).toLocaleString()} due {formatDate(firstPt.date, dateFormat)} ({firstPt.pct}%)
            </div>}
            <div style={{ fontSize:9,color:T.textSub,marginTop:2 }}>Click to open in Orders</div>
          </div>
        );
      })}
    </div>
  );
}

// Helper: get ms timestamp from any chart point (module-level so ChartWithMarkers can use it)
function ptMs(p) {
  if (p.year&&p.month&&p.day) return new Date(p.year,p.month-1,p.day).getTime();
  if (p.year&&p.month) return new Date(p.year,p.month-1,15).getTime();
  return Infinity;
}

// ── Animated chart with bounce markers overlay ────────────────────────────────
function ChartWithMarkers({ chartData, decDots, orderDots, showDec, showOrd, decisions, orders, yearKeys, T, onNavigate, todayKey, monthKeys, ghostLine, onGhostDecHover, onDecisionSelect, onDecisionClick, selectedOrderData, showBuffer, bufferThreshold, scenarioOpen, simOpen, simAnalysis, simReorderInfo, dateFormat }) {

  // ── Two independent enter keys — one per type ────────────────────────────
  const [decEnterKey, setDecEnterKey] = useState(0);
  const [ordEnterKey, setOrdEnterKey] = useState(0);
  const [exitDec, setExitDec]         = useState(false);
  const [exitOrd, setExitOrd]         = useState(false);
  const [hoveredId, setHoveredId]     = useState(null);
  const [hoveredEventIdx, setHoveredEventIdx] = useState(null);
  const hoveredEventRef = useRef(null);
  const setEventHover = (idx) => { hoveredEventRef.current = idx; setHoveredEventIdx(idx); };
  const [simHoveredIdx, setSimHoveredIdx] = useState(null);
  const [simHoverPos, setSimHoverPos] = useState(null);
  const [scales, setScales]           = useState(null);

  const prevShowDec   = useRef(showDec);
  const prevShowOrd   = useRef(showOrd);
  const prevChartData = useRef(null);
  const containerRef  = useRef(null);
  const scalesRef     = useRef(null);
  // Track which stems have played — keyed by id+stemIdx+enterKey so only
  // the matching type's stems reset when its toggle fires.
  const animatedStems = useRef(new Set());
  // Check OS "reduce motion" setting once — if on, skip all animations
  const reducedMotion = useRef(
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );

  const MARGIN = { top:16, right:20, left:8, bottom:28 };

  // chartData change → reset everything
  useEffect(() => {
    if (prevChartData.current === chartData) return;
    prevChartData.current = chartData;
    setScales(null); scalesRef.current = null;
    animatedStems.current.clear();
    setDecEnterKey(k => k+1);
    setOrdEnterKey(k => k+1);
    setExitDec(false); setExitOrd(false);
  }, [chartData]);

  // Planner toggle — only dec markers re-animate
  useEffect(() => {
    if (prevShowDec.current === showDec) return;
    if (prevShowDec.current && !showDec) {
      setExitDec(true);
    } else {
      setExitDec(false);
      // Clear only dec stems from the cache
      const next = new Set([...animatedStems.current].filter(k => !k.startsWith("dec-")));
      animatedStems.current = next;
      setDecEnterKey(k => k+1);
    }
    prevShowDec.current = showDec;
  }, [showDec]);

  // Orders toggle — only ord markers re-animate
  useEffect(() => {
    if (prevShowOrd.current === showOrd) return;
    if (prevShowOrd.current && !showOrd) {
      setExitOrd(true);
    } else {
      setExitOrd(false);
      const next = new Set([...animatedStems.current].filter(k => !k.startsWith("ord-")));
      animatedStems.current = next;
      setOrdEnterKey(k => k+1);
    }
    prevShowOrd.current = showOrd;
  }, [showOrd]);

  // ── Geometry constants ────────────────────────────────────────────────────
  const DEC_R    = 9;
  const decR = (d) => (d?.priority||"should")==="must"?12:(d?.priority||"should")==="nice"?6:9;
  const ORD_W    = 9;
  const ORD_H    = 15;
  const MIN_STEM = 50;
  const MAX_STEM = 95;
  const ICON_GAP = 10;
  const BADGE_THRESHOLD = 4;
  const ANIM_DUR = 0.48;   // icon fall duration (s)
  const STEM_DUR = 0.38;   // stem reveal duration (s) — slightly shorter than icon

  function maxAllowedDistMs() {
    if (!chartData.length) return 0;
    if (chartData.length === 1) return Infinity;
    const first = ptMs(chartData[0]), last = ptMs(chartData[chartData.length-1]);
    const avgGap = (last - first) / (chartData.length - 1);
    // Ensure enough tolerance for week/month views where dates may not align exactly
    return Math.max(avgGap * 2, 32 * 86400000);
  }

  function adaptiveY(lineY, markerHalf, offset) {
    const top20 = offset.top + offset.height * 0.20;
    if (lineY <= top20) {
      const ideal = lineY + MIN_STEM + markerHalf;
      return Math.min(offset.top + offset.height - markerHalf - 4,
                      Math.min(ideal, lineY + MAX_STEM + markerHalf));
    }
    const ideal = lineY - MIN_STEM - markerHalf;
    return Math.max(offset.top + markerHalf + 4,
                    Math.max(ideal, lineY - MAX_STEM - markerHalf));
  }

  function groupByX(markers, threshold=24) {
    if (!markers.length) return [];
    const sorted = [...markers].sort((a,b) => a.rawX - b.rawX);
    const groups = [[sorted[0]]];
    for (let i = 1; i < sorted.length; i++) {
      const last = groups[groups.length-1];
      if (sorted[i].rawX - last[0].rawX < threshold) last.push(sorted[i]);
      else groups.push([sorted[i]]);
    }
    return groups;
  }

  // ── Build ALL markers together (cross-type grouping) ──────────────────────
  const allMarkers = useMemo(() => {
    if (!scales) return [];
    const { xScale, yScale, offset } = scales;
    const maxDist = maxAllowedDistMs();
    const raw = [];

    if (showDec || exitDec) {
      decDots.forEach((item, mi) => {
        const rawX = xScale(item.pt.label) + (xScale.bandwidth ? xScale.bandwidth()/2 : 0);
        const lineY = yScale(item.pt.balance);
        if (isNaN(rawX) || isNaN(lineY)) return;
        if (rawX < offset.left - 2 || rawX > offset.left + offset.width + 2) return;
        // Skip if decision date is too far from its matched chart point
        const decDate = parsePlannerDate(item.d.date);
        if (decDate && Math.abs(decDate.getTime() - ptMs(chartData[item.ptIdx])) > maxDist) return;
        raw.push({ type:"dec", id:"dec-"+item.d.id, rawX, lineY,
                   color:item.color, item, half:decR(item?.d),
                   isExit:exitDec, enterKey:decEnterKey, delay:mi*65 });
      });
    }

    if (showOrd || exitOrd) {
      orderDots.forEach((item, mi) => {
        const rawX = xScale(item.pt.label) + (xScale.bandwidth ? xScale.bandwidth()/2 : 0);
        const lineY = yScale(item.pt.balance);
        if (isNaN(rawX) || isNaN(lineY)) return;
        if (rawX < offset.left - 2 || rawX > offset.left + offset.width + 2) return;
        if (item.o.orderDate) {
          const parts = item.o.orderDate.split(/[-./]/);
          if (parts.length >= 3) {
            const oMs = new Date(parts[2], parts[1]-1, parts[0]).getTime();
            if (!isNaN(oMs) && Math.abs(oMs - ptMs(chartData[item.ptIdx])) > maxDist) return;
          }
        }
        raw.push({ type:"ord", id:"ord-"+item.o.id, rawX, lineY,
                   color:item.col.text||"#8b5cf6", item, half:ORD_H/2,
                   isExit:exitOrd, enterKey:ordEnterKey, delay:mi*65 });
      });
    }

    if (!raw.length) return [];

    const groups = groupByX(raw, 32);
    const result = [];

    groups.forEach(group => {
      const x      = group[0].rawX;
      const lineY  = group[0].lineY;
      const isAbove = adaptiveY(lineY, group[0].half, offset) < lineY;
      const isBadge = group.length >= BADGE_THRESHOLD;

      if (isBadge) {
        const badgeR = 14;
        const badgeY = isAbove
          ? Math.max(offset.top + badgeR + 4, lineY - MIN_STEM - badgeR)
          : Math.min(offset.top + offset.height - badgeR - 4, lineY + MIN_STEM + badgeR);
        const clusterKey = "badge-" + group.map(m=>m.id).join("-");
        result.push({
          type:"badge", id:clusterKey,
          x, lineY, markerY:badgeY,
          color:group[0].color, count:group.length,
          items:group.map(m=>m.item), types:group.map(m=>m.type),
          isAbove, delay:group[0].delay,
          isExit:group.some(m=>m.isExit),
          enterKey:group[0].enterKey,
          stemFromY:lineY,
          stemToY: isAbove ? badgeY+badgeR : badgeY-badgeR,
          stemIdx:0,
        });
      } else {
        let prevMarkerY = null;
        let prevHalf    = 0;

        group.forEach((m, gi) => {
          let markerY, stemFromY, stemToY;

          if (gi === 0) {
            markerY   = adaptiveY(lineY, m.half, offset);
            stemFromY = lineY;
            stemToY   = isAbove ? markerY + m.half : markerY - m.half;
          } else {
            if (isAbove) {
              markerY   = prevMarkerY - prevHalf - ICON_GAP - m.half;
              stemFromY = prevMarkerY - prevHalf;
              stemToY   = markerY + m.half;
            } else {
              markerY   = prevMarkerY + prevHalf + ICON_GAP + m.half;
              stemFromY = prevMarkerY + prevHalf;
              stemToY   = markerY - m.half;
            }
          }

          prevMarkerY = markerY;
          prevHalf    = m.half;

          result.push({ ...m, x, markerY, stemFromY, stemToY, stemIdx:gi, isAbove });
        });
      }
    });

    // Assign exit delay: sort exit markers top-to-bottom (smallest markerY first = top of screen)
    // then stagger their disappearance with 55ms between each
    const exitMarkers = result.filter(m => m.isExit && m.stemIdx === 0);
    exitMarkers.sort((a, b) => a.markerY - b.markerY);
    // Cap total stagger so exit always finishes within 350ms regardless of marker count
    const exitCount = exitMarkers.length;
    const exitStep  = exitCount > 1 ? Math.min(55, 350 / (exitCount - 1)) : 0;
    exitMarkers.forEach((m, i) => {
      const delay = reducedMotion.current ? 0 : Math.round(i * exitStep);
      result.forEach(r => { if (r.id === m.id) r.exitDelay = delay; });
    });

    return result;
  }, [scales, chartData, decDots, orderDots, showDec, showOrd, exitDec, exitOrd, decEnterKey, ordEnterKey]);

  // ── Stem animation ───────────────────────────────────────────────────────
  // Enter: mask-reveal scaleY(0→1) from chart line toward icon (bottom to top)
  // Exit:  <g> opacity transition with staggered delay (top to bottom)
  //        Wrapping in <g> and animating opacity uses the GPU compositor.
  // All animations skipped instantly when OS reduce-motion is enabled.
  function StemSegment({ m }) {
    const rm          = reducedMotion.current;
    const stemKey     = m.id + "-" + m.stemIdx + "-" + m.enterKey;
    const alreadyDone = animatedStems.current.has(stemKey);
    const length      = Math.abs(m.stemToY - m.stemFromY);
    const DASH = 3.5; const GAP = 2.5;
    const top      = Math.min(m.stemFromY, m.stemToY);
    const bot      = Math.max(m.stemFromY, m.stemToY);
    const maskFromY = m.stemFromY;
    const growsUp   = m.stemToY < m.stemFromY;
    const maskId    = "msk" + stemKey.replace(/[^a-zA-Z0-9]/g,"_");
    const animId    = "mskA" + stemKey.replace(/[^a-zA-Z0-9]/g,"_");
    const isExit    = m.isExit;
    if (!alreadyDone && !isExit) {
      setTimeout(() => animatedStems.current.add(stemKey),
        (rm ? 0 : m.delay) + STEM_DUR * 1000 + 50);
    }

    const shouldAnimateEnter = !alreadyDone && !isExit && !rm;

    // ── EXIT PATH ─────────────────────────────────────────────────────────
    // Wrap in <g> and animate opacity 1→0 using a two-frame technique:
    // Mount with opacity:1, then use useEffect+rAF to set opacity:0 so the
    // browser sees a genuine change and fires the CSS transition.
    // This works even when React remounts the element (inside Customized).
    if (isExit) {
      // Exit: instant hide — no animation
      return null;
    }

    // ── ENTER PATH ────────────────────────────────────────────────────────
    return (
      <g>
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse">
            {shouldAnimateEnter ? (
              <rect
                x={m.x - 4} width={8}
                y={growsUp ? maskFromY - length : maskFromY}
                height={length}
                fill="white"
                style={{
                  transformOrigin: `${m.x}px ${maskFromY}px`,
                  transform: "scaleY(0)",
                  animation: `${animId} ${STEM_DUR}s ease-out forwards`,
                  animationDelay: m.delay + "ms",
                }}
              />
            ) : (
              <rect x={m.x-4} width={8} y={top} height={length} fill="white"/>
            )}
          </mask>
          {shouldAnimateEnter && (
            <style>{`@keyframes ${animId}{0%{transform:scaleY(0)}100%{transform:scaleY(1)}}`}</style>
          )}
        </defs>
        <line
          x1={m.x} y1={top} x2={m.x} y2={bot}
          stroke={m.color}
          strokeWidth={hoveredId===m.id ? 2 : 1.5}
          strokeOpacity={hoveredId===m.id ? 0.9 : 0.5}
          strokeDasharray={(DASH+","+GAP)}
          mask={"url(#"+maskId+")"}
        />
        {m.stemIdx === 0 && (
          <circle cx={m.x} cy={m.stemFromY} r={3} fill={m.color} fillOpacity={0.85}/>
        )}
      </g>
    );
  }

    // ── Icon renderer ─────────────────────────────────────────────────────────
  function renderIcon(m) {
    const offset    = scales?.offset || { height:400, top:16 };
    // Start from top chart edge regardless of where icon lands
    const enterFrom = offset.top - m.markerY - 20;
    const isExit    = m.isExit;
    // Each marker uses ITS OWN type's enterKey so toggling one type
    // doesn't re-animate the other
    const animKey   = m.enterKey + "-" + m.id;

    return (
      <g key={animKey}
        transform={"translate("+m.x+","+m.markerY+")"}
        style={{ pointerEvents:"all", cursor:"pointer" }}
        onClick={() => {
          if (m.type==="badge") { setHoveredId(hoveredId===m.id ? null : m.id); return; }
          if (m.type==="dec") { onDecisionClick?.(m.item.d.id, m.x, m.markerY); onDecisionSelect?.(m.item.d.id); return; }
          else if (m.type==="ord") onNavigate?.("orders",{expandId:m.item.o.id});
        }}
        onMouseEnter={() => { if (m.type!=="badge") { setHoveredId(m.id); if (m.type==="dec") onGhostDecHover?.(m.item.d.id); } }}
        onMouseLeave={() => { if (m.type!=="badge") { setHoveredId(null); onGhostDecHover?.(null); } }}
      >
        <g style={{
          animation: isExit || reducedMotion.current ? "none"
            : `chFadeSlide ${ANIM_DUR}s cubic-bezier(0.25,0.46,0.45,0.94) both`,
          animationDelay: reducedMotion.current ? "0ms" : m.delay+"ms",
          "--from": enterFrom+"px",
          opacity:   isExit ? 0 : undefined,
          transition: isExit && !reducedMotion.current ? `opacity 0.3s ease ${m.exitDelay || 0}ms` : undefined,
        }}>
          {m.type==="badge" ? (
            <>
              <circle r={14} fill={m.color} stroke="#fff" strokeWidth={2}
                style={{ filter:"drop-shadow(0 2px 8px "+m.color+"aa)" }}/>
              <text y={5} textAnchor="middle" fontSize={10} fontWeight={700} fill="#fff"
                style={{ pointerEvents:"none", userSelect:"none" }}>{m.count}</text>
            </>
          ) : m.type==="dec" ? (
            <>
              <circle r={decR(m.item?.d)} fill={m.color} stroke="#fff" strokeWidth={m.item?.d?.priority==="must"?3:2}
                style={{ filter:"drop-shadow(0 2px 6px "+m.color+"88)" }}/>
              <text y={4} textAnchor="middle" fontSize={9}
                style={{ pointerEvents:"none", userSelect:"none" }}>{m.item.d.icon||""}</text>
            </>
          ) : (
            <polygon
              points={"0,"+(ORD_H/2)+" -"+ORD_W+","+(-(ORD_H/2))+" "+ORD_W+","+(-(ORD_H/2))}
              fill={m.color} stroke="#fff" strokeWidth={1.5}
              style={{ filter:"drop-shadow(0 2px 6px "+m.color+"88)" }}
            />
          )}
          {hoveredId===m.id && (
            <circle r={m.type==="dec"?DEC_R+5:m.type==="badge"?19:ORD_W+4}
              fill="none" stroke={m.color} strokeWidth={1.5} strokeOpacity={0.45}/>
          )}
        </g>
      </g>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ position:"relative", width:"100%", height:"100%" }}>
      <style>{`@keyframes chFadeSlide{0%{transform:translateY(var(--from));opacity:0}40%{opacity:1}100%{transform:translateY(0px);opacity:1}}`}</style>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={MARGIN}>
          <defs>
            <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={T.accent} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={T.accent} stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="consGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={T.red} stopOpacity={0.15}/>
              <stop offset="95%" stopColor={T.red} stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="optGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={T.green} stopOpacity={0.15}/>
              <stop offset="95%" stopColor={T.green} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border} strokeOpacity={0.5}/>
          <XAxis dataKey="label" tick={{ fontSize:10, fill:T.text, fontFamily:FONT }}
            tickLine={false} axisLine={{ stroke:T.border }}/>
          <YAxis tickFormatter={v=>fmt(v)} tick={{ fontSize:10, fill:T.text, fontFamily:FONT }}
            tickLine={false} axisLine={false} width={58}/>
          <Tooltip content={props=><ChartTooltip {...props} decisions={decisions}
            showDecisions={showDec} orders={orders} orderDots={orderDots}
            showOrders={showOrd} onNavigate={onNavigate} decDots={decDots}
            dateFormat={dateFormat}/>}/>
          <ReferenceLine y={0} stroke={T.red} strokeDasharray="4 4" strokeOpacity={0.5}/>
          {showBuffer && bufferThreshold > 0 && (
            <ReferenceLine y={bufferThreshold} stroke={T.red} strokeDasharray="8 4" strokeWidth={1.5} strokeOpacity={0.7}
              label={{ value:`Buffer ${currSym()}${fmt(bufferThreshold)}`, position:"insideTopRight", fill:T.red, fontSize:10, fontWeight:700, fontFamily:FONT }}/>
          )}
          {yearKeys.map(({label:yl,key})=>(
            <ReferenceLine key={yl} x={key} stroke={T.orange} strokeWidth={2}
              strokeDasharray="6 3" strokeOpacity={0.7}
              label={{ value:yl, position:"insideTopLeft", fill:T.orange,
                       fontSize:11, fontWeight:700, fontFamily:FONT, dy:20 }}/>
          ))}
          {/* Today vertical line — shown for jan1 and all-time views */}
          {todayKey && (
            <ReferenceLine x={todayKey} stroke={T.green} strokeWidth={1.5}
              strokeDasharray="4 3" strokeOpacity={0.8}
              label={{ value:"Today", position:"insideTopLeft", fill:T.green,
                       fontSize:10, fontWeight:700, fontFamily:FONT, dy:20 }}/>
          )}
          {/* Month start labels — shown in day/week granularity */}
          {(monthKeys||[]).map(({label:ml, key:mk}) => (
            <ReferenceLine key={mk} x={mk} stroke={T.border} strokeWidth={1}
              strokeOpacity={0.4}
              label={{ value:ml, position:"insideTopLeft", fill:T.textSub,
                       fontSize:9, fontFamily:FONT, fontWeight:600 }}/>
          ))}
          {scenarioOpen && <Area type="monotone" dataKey="conservative" stroke={T.red} strokeWidth={2} strokeDasharray="6 3"
            fill="url(#consGrad)" dot={false} activeDot={false}/>}
          {scenarioOpen && <Area type="monotone" dataKey="optimistic" stroke={T.green} strokeWidth={2} strokeDasharray="6 3"
            fill="url(#optGrad)" dot={false} activeDot={false}/>}
          <Area type="monotone" dataKey="balance" stroke={T.accent} strokeWidth={2.5}
            fill="url(#balGrad)" dot={false}
            activeDot={{ r:5, fill:T.accent, stroke:"#fff", strokeWidth:2 }}/>

          {/* Customized: fires inside Recharts SVG with exact scales */}
          <Customized component={({ xAxisMap, yAxisMap, offset }) => {
            const xAxis = xAxisMap && (xAxisMap[0] || Object.values(xAxisMap)[0]);
            const yAxis = yAxisMap && (yAxisMap[0] || Object.values(yAxisMap)[0]);
            if (!xAxis?.scale || !yAxis?.scale) return null;
            const next = { xScale:xAxis.scale, yScale:yAxis.scale, offset };
            const cur  = scalesRef.current;
            if (!cur
              || cur.offset.left   !== offset.left
              || cur.offset.width  !== offset.width
              || cur.offset.height !== offset.height) {
              scalesRef.current = next;
              setTimeout(() => setScales(next), 0);
            }
            return (
              <g>
                {/* Stems rendered inside Recharts SVG — unrestricted by clip */}
                {allMarkers.map(m => (
                  <StemSegment
                    key={"stem-"+m.id+"-"+m.stemIdx+"-"+m.enterKey}
                    m={m}
                  />
                ))}
                {/* Ghost dashed line — counterfactual cashflow without hovered decision */}
                {ghostLine && ghostLine.points.length > 1 && (() => {
                  const xs = xAxis.scale;
                  const ys = yAxis.scale;
                  const bw = xs.bandwidth ? xs.bandwidth()/2 : 0;
                  // Determine color: green if ghost is higher (decision costs you), red if ghost is lower (decision earns you)
                  const lastPt = ghostLine.points[ghostLine.points.length-1];
                  const isGhostHigher = lastPt.ghostBalance > lastPt.actualBalance;
                  const ghostColor = isGhostHigher ? "#10b981" : "#ef4444"; // green = "better without it" / red = "worse without it"
                  const ghostColorFill = isGhostHigher ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)";
                  const ghostColorLine = isGhostHigher ? "rgba(16,185,129,0.55)" : "rgba(239,68,68,0.55)";

                  const ghostChartBottom = offset.top + offset.height;
                  const clampGY = y => Math.max(offset.top, Math.min(y, ghostChartBottom));
                  const pathD = ghostLine.points.map((p, i) => {
                    const x = xs(p.label) + bw;
                    const y = clampGY(ys(p.ghostBalance));
                    return (i === 0 ? "M" : "L") + x.toFixed(1) + "," + y.toFixed(1);
                  }).join(" ");
                  // Build hatched area between actual (purple) and ghost line
                  const actualPathD = ghostLine.points.map((p, i) => {
                    const x = xs(p.label) + bw;
                    const y = clampGY(ys(p.actualBalance));
                    return (i === 0 ? "M" : "L") + x.toFixed(1) + "," + y.toFixed(1);
                  }).join(" ");
                  const ghostReverse = [...ghostLine.points].reverse().map((p) => {
                    const x = xs(p.label) + bw;
                    const y = clampGY(ys(p.ghostBalance));
                    return "L" + x.toFixed(1) + "," + y.toFixed(1);
                  }).join(" ");
                  const decHatchPath = actualPathD + " " + ghostReverse + " Z";
                  const decHatchId = "decHatch_" + (isGhostHigher ? "green" : "red");

                  return (
                    <g style={{ animation:"sfFadeIn 0.3s ease both" }}>
                      {/* Diagonal hatch pattern between actual and ghost lines */}
                      <defs>
                        <pattern id={decHatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                          <line x1="0" y1="0" x2="0" y2="6" stroke={ghostColor} strokeWidth="1" strokeOpacity="0.3"/>
                        </pattern>
                      </defs>
                      <path d={decHatchPath} fill={`url(#${decHatchId})`} stroke="none"/>
                      {/* Ghost dashed line */}
                      <path d={pathD} fill="none"
                        stroke={ghostColorLine} strokeWidth="2"
                        strokeDasharray="6,4"
                        strokeLinecap="round" />
                      {/* Start dot */}
                      <circle cx={xs(ghostLine.points[0].label)+bw} cy={ys(ghostLine.points[0].ghostBalance)}
                        r="4" fill={ghostColor} stroke="#0d0f1a" strokeWidth="2" opacity="0.8"/>
                      {/* End delta label */}
                      {(() => {
                        const delta = lastPt.ghostBalance - lastPt.actualBalance;
                        const ex = xs(lastPt.label)+bw;
                        const ey = ys(lastPt.ghostBalance);
                        if (Math.abs(delta) < 1) return null;
                        return (
                          <text x={ex-4} y={ey-10} fill={ghostColor} fontSize="10"
                            fontWeight="800" fontFamily="Plus Jakarta Sans,system-ui" textAnchor="end">
                            {delta>0?"+":"−"}€{Math.abs(Math.round(delta)).toLocaleString()}
                          </text>
                        );
                      })()}
                    </g>
                  );
                })()}
                {/* ── Simulator overlay: dashed line + vertical lines ── */}
                {simOpen && simAnalysis?.analysis?.payments && (() => {
                  const xs = xAxis.scale;
                  const ys = yAxis.scale;
                  const bw = xs.bandwidth ? xs.bandwidth()/2 : 0;
                  // Build modified balance line — find closest chart label for each payment
                  const findLabel = (date) => { if(!date) return null; const ms=date.getTime(); let best=null,bestD=Infinity; for(const cd of chartData){let cMs=0;if(cd.year&&cd.month&&cd.day)cMs=new Date(cd.year,cd.month-1,cd.day).getTime();else if(cd.year&&cd.month)cMs=new Date(cd.year,cd.month-1,15).getTime();if(!cMs)continue;const d=Math.abs(cMs-ms);if(d<bestD){bestD=d;best=cd.label||cd.shortLabel||cd.name;}} return best; };
                  const simPayByLabel = {};
                  let firstPayLabel = null;
                  for (const p of simAnalysis.analysis.payments) { if(!p.date) continue; const lab=findLabel(p.date); if(lab){simPayByLabel[lab]=(simPayByLabel[lab]||0)+p.amount; if(!firstPayLabel) firstPayLabel=lab;} }
                  // Modified balance trajectory (only from first payment onward)
                  let cumDeduct = 0;
                  let started = false;
                  const modPoints = chartData.filter(d=>!d.isBeforeToday).map(d => {
                    const lab = d.label||d.shortLabel||d.name;
                    if (lab===firstPayLabel) started=true;
                    if (started) cumDeduct += (simPayByLabel[lab]||0);
                    return { label:lab, balance:d.balance-(started?cumDeduct:0) };
                  });
                  // Lowest point AFTER first payment
                  const afterFirst = modPoints.filter((_,i) => { let s=false; for(let j=0;j<=i;j++){if(modPoints[j].label===firstPayLabel)s=true;} return s; });
                  let lowestIdx = 0;
                  for (let i=1;i<afterFirst.length;i++){if(afterFirst[i].balance<afterFirst[lowestIdx].balance)lowestIdx=i;}
                  const lowestMod = afterFirst[lowestIdx];
                  // Orange dashed line
                  const startIdx = modPoints.findIndex(p=>p.label===firstPayLabel);
                  const linePoints = startIdx>=0?modPoints.slice(startIdx):modPoints;
                  const chartBottom = offset.top + offset.height;
                  const pathD = linePoints.map((p,i) => { const x=xs(p.label); if(x==null)return""; const y=Math.min(ys(p.balance), chartBottom); return(i===0?"M":"L")+(x+bw).toFixed(1)+","+y.toFixed(1); }).filter(Boolean).join(" ");
                  // Build hatched area between original and modified balance
                  const origPoints = linePoints.map(p => { const cd = chartData.find(d=>(d.label||d.shortLabel||d.name)===p.label); return cd ? { label:p.label, balance:cd.balance } : null; }).filter(Boolean);
                  // Create closed path: original line forward, then modified line backward
                  let hatchPath = "";
                  if (origPoints.length > 1 && linePoints.length > 1) {
                    const clampY = y => Math.max(offset.top, Math.min(y, chartBottom));
                    const fwd = origPoints.map((p,i) => { const x=xs(p.label); if(x==null) return""; return(i===0?"M":"L")+(x+bw).toFixed(1)+","+clampY(ys(p.balance)).toFixed(1); }).filter(Boolean).join(" ");
                    const bwd = [...linePoints].reverse().map((p,i) => { const x=xs(p.label); if(x==null) return""; return"L"+(x+bw).toFixed(1)+","+clampY(ys(p.balance)).toFixed(1); }).filter(Boolean).join(" ");
                    hatchPath = fwd + " " + bwd + " Z";
                  }
                  return (
                    <g style={{ animation:"sfFadeIn 0.3s ease both" }} clipPath="url(#simClip)">
                      {/* Clip + hatch pattern definitions */}
                      <defs>
                        <clipPath id="simClip">
                          <rect x={offset.left} y={offset.top} width={offset.width} height={offset.height}/>
                        </clipPath>
                        <pattern id="simHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                          <line x1="0" y1="0" x2="0" y2="6" stroke="#FF9500" strokeWidth="1" strokeOpacity="0.25"/>
                        </pattern>
                      </defs>
                      {/* Hatched fill between original and modified lines */}
                      {hatchPath && <path d={hatchPath} fill="url(#simHatch)" stroke="none"/>}
                      {/* Orange dashed modified balance line */}
                      {pathD && <path d={pathD} fill="none" stroke="#FF9500" strokeWidth="2" strokeDasharray="6,4" strokeLinecap="round" opacity="0.7"/>}
                      {/* Red vertical line — lowest balance after first payment */}
                      {lowestMod && (()=>{const x=xs(lowestMod.label);if(x==null||x+bw<offset.left||x+bw>offset.left+offset.width)return null;return<g>
                        <line x1={x+bw} x2={x+bw} y1={offset.top} y2={offset.top+offset.height} stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4,3"/>
                        <text x={x+bw+4} y={offset.top+28} fill="#ef4444" fontSize="8" fontWeight="700" fontFamily="Plus Jakarta Sans,system-ui">Lowest: {currSym()}{Math.round(lowestMod.balance).toLocaleString()}</text>
                      </g>;})()}
                      {/* Orange vertical line — next reorder date (only if within visible chart range) */}
                      {simReorderInfo?.reorderDate && (()=>{
                        const rLab = findLabel(simReorderInfo.reorderDate);
                        if(!rLab) return null;
                        const x=xs(rLab); if(x==null) return null;
                        // Skip if outside chart area (date beyond visible range)
                        if (x+bw < offset.left || x+bw > offset.left+offset.width) return null;
                        return <g>
                          <line x1={x+bw} x2={x+bw} y1={offset.top} y2={offset.top+offset.height} stroke="#FF9500" strokeWidth="2"/>
                          <text x={x+bw+4} y={offset.top+38} fill="#FF9500" fontSize="8" fontWeight="700" fontFamily="Plus Jakarta Sans,system-ui">
                            Reorder: {simReorderInfo.reorderUnits>0?simReorderInfo.reorderUnits.toLocaleString()+" units":""}
                          </text>
                          {simReorderInfo.reorderCost > 0 && <text x={x+bw+4} y={offset.top+48} fill="#FF9500" fontSize="7" fontWeight="600" fontFamily="Plus Jakarta Sans,system-ui">
                            ~{currSym()}{simReorderInfo.reorderCost.toLocaleString()}
                          </text>}
                        </g>;
                      })()}
                    </g>
                  );
                })()}
              </g>
            );
          }}/>
        </AreaChart>
      </ResponsiveContainer>

      {/* Icons — clipped to plot area */}
      {scales && allMarkers.length > 0 && (
        <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%",
                      pointerEvents:"none", overflow:"hidden" }}>
          <clipPath id="ch-clip">
            <rect x={scales.offset.left} y={scales.offset.top}
                  width={scales.offset.width} height={scales.offset.height}/>
          </clipPath>
          <g clipPath="url(#ch-clip)" style={{ pointerEvents:"all" }}>
            {allMarkers.map(m => renderIcon(m))}
          </g>
        </svg>
      )}

      {/* Individual order diamonds + hit areas — fully outside Customized to prevent re-renders */}
      {scales && selectedOrderData && selectedOrderData.events.length > 0 && (() => {
        const evColor = selectedOrderData.col?.text || T.accent;
        const DIAMOND_ORANGE = "#f97316"; // High-contrast color for diamonds + amounts (screenshot readability)
        const TYPE_ICONS = { payment_term:"💳", shipping:"🚢", customs:"🛃", inspection:"🔍" };
        const bw = scales.xScale.bandwidth ? scales.xScale.bandwidth()/2 : 0;
        const offset = scales.offset;
        const DIA_R = 7;
        const DIA_GAP = 5;
        const STEM_MIN = 22;

        // Compute raw positions
        const items = selectedOrderData.events.map((ev, ei) => {
          if (!ev.chartPt) return null;
          const cx = scales.xScale(ev.chartPt.label) + bw;
          const cy = scales.yScale(ev.chartPt.balance);
          if (isNaN(cx) || isNaN(cy)) return null;
          if (cx < offset.left - 5 || cx > offset.left + offset.width + 5) return null;
          return { ev, ei, cx, cy };
        }).filter(Boolean);

        // Group by X position (within 12px)
        const groups = [];
        const used = new Set();
        for (let i = 0; i < items.length; i++) {
          if (used.has(i)) continue;
          const group = [items[i]];
          used.add(i);
          for (let j = i + 1; j < items.length; j++) {
            if (used.has(j)) continue;
            if (Math.abs(items[i].cx - items[j].cx) < 12) {
              group.push(items[j]);
              used.add(j);
            }
          }
          groups.push(group);
        }

        // Layout each group — stack vertically like dots/triangles
        const laid = [];
        for (const group of groups) {
          const x = group[0].cx;
          const lineY = group[0].cy;
          const isAbove = lineY > offset.top + offset.height * 0.5;
          const isSolo = group.length === 1;

          group.forEach((item, gi) => {
            let diaY;
            if (gi === 0) {
              diaY = isAbove ? lineY - STEM_MIN - DIA_R : lineY + STEM_MIN + DIA_R;
            } else {
              const prev = laid[laid.length - 1];
              diaY = isAbove ? prev.diaY - DIA_R * 2 - DIA_GAP : prev.diaY + DIA_R * 2 + DIA_GAP;
            }
            laid.push({ ...item, x, lineY, diaY, isAbove, isSolo, isFirst: gi === 0, isLast: gi === group.length - 1, groupSize: group.length });
          });
        }

        return (
          <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", overflow:"visible" }}>
            {laid.map(({ ev, ei, x, lineY, diaY, isAbove, isSolo, isFirst, isLast, groupSize }) => {
              const amtStr = currSym() + Math.round(ev.amount).toLocaleString();
              const isPaid = ev.paid;
              const fillColor = isPaid ? T.green : ev.overdue ? "#ff3b30" : DIAMOND_ORANGE;

              return (
                <g key={`dia-${ei}`}>
                  {/* Stem from chart line — only for first diamond */}
                  {isFirst && (
                    <line x1={x} y1={lineY} x2={x} y2={isAbove ? diaY + DIA_R : diaY - DIA_R}
                      stroke={evColor} strokeWidth={1.5} strokeOpacity={isPaid?0.35:0.5}
                      strokeDasharray={isPaid?"3,2":"none"} />
                  )}
                  {/* Connecting stem between stacked diamonds */}
                  {!isFirst && (
                    <line x1={x} y1={isAbove ? diaY + DIA_R * 2 + DIA_GAP : diaY - DIA_R * 2 - DIA_GAP}
                      x2={x} y2={isAbove ? diaY + DIA_R : diaY - DIA_R}
                      stroke={evColor} strokeWidth={1} strokeOpacity={0.25} strokeDasharray="2,2" />
                  )}
                  {/* Diamond */}
                  <g transform={`translate(${x},${diaY})`} style={{ opacity:isPaid?0.4:ev.overdue?0.9:1 }}>
                    <rect x={-5} y={-5} width={10} height={10} transform="rotate(45)"
                      fill={fillColor} stroke="#0d0f1a" strokeWidth={1.5} rx={1}/>
                  </g>
                  {/* Type icon — beside each diamond */}
                  <text x={x + 14} y={diaY + 4} fontSize={9} fill={DIAMOND_ORANGE} style={{ opacity:0.9 }}>
                    {TYPE_ICONS[ev.type]||"📌"}
                  </text>
                  {/* Amount label — shown for EVERY diamond. Solo: above/below. Stacked: inline-right (no overlap) */}
                  {isSolo ? (
                    <text x={x} y={isAbove ? diaY - DIA_R - 8 : diaY + DIA_R + 14}
                      textAnchor="middle" fontSize={9} fontWeight={700}
                      fontFamily="Plus Jakarta Sans,system-ui" fill={fillColor}
                      style={{ textDecoration:isPaid?"line-through":"none" }}>
                      -{amtStr}
                    </text>
                  ) : (
                    <text x={x + 26} y={diaY + 4} fontSize={9} fontWeight={700}
                      fontFamily="Plus Jakarta Sans,system-ui" fill={fillColor}
                      style={{ textDecoration:isPaid?"line-through":"none" }}>
                      -{amtStr}
                    </text>
                  )}
                  {/* Hit area per diamond */}
                  <rect x={x-14} y={diaY - DIA_R - 4} width={56} height={DIA_R * 2 + 8}
                    fill="transparent" style={{ pointerEvents:"all", cursor:"pointer" }}
                    onMouseEnter={() => setEventHover(ei)}
                    onMouseLeave={() => setEventHover(null)}/>
                </g>
              );
            })}
          </svg>
        );
      })()}

      {/* ── Sim order diamonds — same pattern as real order diamonds ── */}
      {scales && simOpen && simAnalysis?.analysis?.payments?.length > 0 && (() => {
        const bw = scales.xScale.bandwidth ? scales.xScale.bandwidth()/2 : 0;
        const sOffset = scales.offset;
        const DIA_R = 7, DIA_GAP = 5, STEM_MIN = 22;
        // Find closest chartData point — works for day/week/month views
        const findChartPt = (date) => {
          if (!date) return null;
          const ms = date.getTime();
          let best = null, bestD = Infinity;
          for (const cd of chartData) {
            let cMs = 0;
            if (cd.year && cd.month && cd.day) cMs = new Date(cd.year, cd.month-1, cd.day).getTime();
            else if (cd.year && cd.month) cMs = new Date(cd.year, cd.month-1, 15).getTime(); // month view: use mid-month
            if (cMs === 0) continue;
            const d = Math.abs(cMs - ms);
            if (d < bestD) { bestD = d; best = cd; }
          }
          return best;
        };
        // Compute modified balances for diamond Y positioning (orange line, not purple)
        const simPayByLabel = {};
        for (const p of simAnalysis.analysis.payments) {
          if (!p.date) continue;
          const pt = findChartPt(p.date);
          if (!pt) continue;
          const lab = pt.label||pt.shortLabel||pt.name;
          simPayByLabel[lab] = (simPayByLabel[lab]||0) + p.amount;
        }
        let cumDeduct = 0;
        let firstHit = false;
        const modBalByLabel = {};
        for (const cd of chartData) {
          if (cd.isBeforeToday) continue;
          const lab = cd.label||cd.shortLabel||cd.name;
          if (simPayByLabel[lab]) firstHit = true;
          if (firstHit) cumDeduct += (simPayByLabel[lab]||0);
          modBalByLabel[lab] = cd.balance - (firstHit ? cumDeduct : 0);
        }

        const items = simAnalysis.analysis.payments.map((p,i) => {
          if (!p.date) return null;
          const chartPt = findChartPt(p.date);
          if (!chartPt) return null;
          const lab = chartPt.label||chartPt.shortLabel||chartPt.name;
          const cx = scales.xScale(lab);
          if (cx == null || isNaN(cx)) return null;
          const modBal = modBalByLabel[lab] ?? chartPt.balance;
          const rawCy = scales.yScale(modBal);
          if (isNaN(rawCy) || cx+bw < sOffset.left-5 || cx+bw > sOffset.left+sOffset.width+5) return null;
          const cy = Math.max(sOffset.top, Math.min(rawCy, sOffset.top + sOffset.height));
          return { p, i, cx: cx+bw, cy, lab };
        }).filter(Boolean);

        // Group by X
        const groups = []; const used = new Set();
        for (let i=0;i<items.length;i++){if(used.has(i))continue;const g=[items[i]];used.add(i);for(let j=i+1;j<items.length;j++){if(!used.has(j)&&Math.abs(items[i].cx-items[j].cx)<12){g.push(items[j]);used.add(j);}}groups.push(g);}

        const laid = [];
        for (const group of groups) {
          const x = group[0].cx, lineY = group[0].cy;
          const isAbove = lineY > sOffset.top + sOffset.height * 0.5;
          const chartTop = sOffset.top + DIA_R + 2;
          const chartBot = sOffset.top + sOffset.height - DIA_R - 2;
          group.forEach((item, gi) => {
            let diaY = gi===0 ? (isAbove?lineY-STEM_MIN-DIA_R:lineY+STEM_MIN+DIA_R)
              : (isAbove?laid[laid.length-1].diaY-DIA_R*2-DIA_GAP:laid[laid.length-1].diaY+DIA_R*2+DIA_GAP);
            diaY = Math.max(chartTop, Math.min(diaY, chartBot));
            laid.push({...item, x, lineY, diaY, isAbove, isSolo:group.length===1, isFirst:gi===0, isLast:gi===group.length-1, groupSize:group.length});
          });
        }

        return (
          <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", overflow:"hidden" }}>
            {laid.map(({ p, i, x, lineY, diaY, isAbove, isSolo, isFirst, isLast, groupSize }) => {
              const amtStr = currSym() + Math.round(p.amount).toLocaleString();
              return (
                <g key={`sim-dia-${i}`}>
                  {isFirst && <line x1={x} y1={lineY} x2={x} y2={isAbove?diaY+DIA_R:diaY-DIA_R} stroke="#FF9500" strokeWidth={1.5} strokeOpacity={0.5}/>}
                  {!isFirst && <line x1={x} y1={isAbove?diaY+DIA_R*2+DIA_GAP:diaY-DIA_R*2-DIA_GAP} x2={x} y2={isAbove?diaY+DIA_R:diaY-DIA_R} stroke="#FF9500" strokeWidth={1} strokeOpacity={0.25} strokeDasharray="2,2"/>}
                  <g transform={`translate(${x},${diaY})`}>
                    <rect x={-5} y={-5} width={10} height={10} transform="rotate(45)" fill="#FF9500" stroke="#0d0f1a" strokeWidth={1.5} rx={1}/>
                  </g>
                  <text x={x+14} y={diaY+4} fontSize={9} fill="#FF9500" style={{ opacity:0.8 }}>{p.label?.includes("🚢")?"🚢":"💳"}</text>
                  {isSolo ? (
                    <text x={x} y={isAbove?diaY-DIA_R-8:diaY+DIA_R+14} textAnchor="middle" fontSize={9} fontWeight={700} fontFamily="Plus Jakarta Sans,system-ui" fill="#FF9500">-{amtStr}</text>
                  ) : (
                    <text x={x+26} y={diaY+4} fontSize={9} fontWeight={700} fontFamily="Plus Jakarta Sans,system-ui" fill="#FF9500">-{amtStr}</text>
                  )}
                  {/* Hit area for hover tooltip */}
                  <rect x={x-14} y={diaY-DIA_R-4} width={56} height={DIA_R*2+8}
                    fill="transparent" style={{ pointerEvents:"all", cursor:"pointer" }}
                    onMouseEnter={(e)=>{ setSimHoveredIdx(i); setSimHoverPos({ x:e.clientX, y:e.clientY }); }}
                    onMouseLeave={()=>{ setSimHoveredIdx(null); setSimHoverPos(null); }}
                    onMouseMove={(e)=>setSimHoverPos({ x:e.clientX, y:e.clientY })}/>
                </g>
              );
            })}
          </svg>
        );
      })()}

      {/* Sim diamond hover tooltip */}
      {simHoveredIdx !== null && simHoverPos && simAnalysis?.analysis?.payments && (() => {
        const p = simAnalysis.analysis.payments[simHoveredIdx];
        if (!p) return null;
        const dateStr = p.date ? `${String(p.date.getDate()).padStart(2,"0")}.${String(p.date.getMonth()+1).padStart(2,"0")}.${p.date.getFullYear()}` : "—";
        const isShipping = p.label?.includes("🚢");
        const cardW = 240;
        const left = simHoverPos.x + 20 + cardW > window.innerWidth ? simHoverPos.x - cardW - 16 : simHoverPos.x + 20;
        const top = Math.max(simHoverPos.y - 60, 8);
        return (
          <div style={{
            position:"fixed", left, top, zIndex:9999,
            background:T.card, border:`1px solid #FF950044`,
            borderRadius:RADIUS.lg, boxShadow:`0 16px 48px rgba(0,0,0,0.35), 0 0 0 1px #FF950022`,
            padding:"12px 14px", width:cardW, fontFamily:FONT,
            animation:"dashMenuIn 0.15s ease both", pointerEvents:"none",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <span style={{ fontSize:16 }}>{isShipping ? "🚢" : "💳"}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#FF9500" }}>Simulated Order</div>
                <div style={{ fontSize:11, color:T.textSub }}>{isShipping ? "Shipping / Freight" : "Payment Term"}</div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ color:T.textSub }}>Label</span>
                <span style={{ fontWeight:700, color:T.text }}>{p.label || "Payment"}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ color:T.textSub }}>Amount</span>
                <span style={{ fontWeight:700, color:T.red }}>-{currSym()}{Math.round(p.amount).toLocaleString()}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ color:T.textSub }}>Date</span>
                <span style={{ fontWeight:600, color:T.text }}>{dateStr}</span>
              </div>
              {p.balanceBefore != null && <>
                <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:4, display:"flex", justifyContent:"space-between" }}>
                  <span style={{ color:T.textSub }}>Balance before</span>
                  <span style={{ fontWeight:600, color:T.text }}>{currSym()}{Math.round(p.balanceBefore).toLocaleString()}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ color:T.textSub }}>Balance after</span>
                  <span style={{ fontWeight:700, color:p.aboveBuffer ? T.green : T.red }}>{currSym()}{Math.round(p.balanceAfter).toLocaleString()}</span>
                </div>
                {!p.aboveBuffer && <div style={{ fontSize:11, color:T.red, fontWeight:700, textAlign:"center", marginTop:2 }}>
                  ⚠️ {currSym()}{Math.round(p.shortfall).toLocaleString()} below buffer
                </div>}
              </>}
            </div>
          </div>
        );
      })()}

      {/* Hover tooltip card — same rich design for both dots and triangles */}
      {hoveredId && scales && (() => {
        const m = allMarkers.find(x => x.id === hoveredId);
        if (!m) return null;
        const cw   = containerRef.current?.clientWidth || 400;
        const cardW = 250;
        const left = m.x + 18 + cardW > cw ? m.x - cardW - 14 : m.x + 18;
        const top  = Math.max(m.markerY - 60, 8);

        const cardStyle = {
          position:"absolute", left, top,
          background:T.card, border:`1px solid ${T.border}`,
          borderRadius:RADIUS.md, padding:"10px 14px",
          fontSize:11, pointerEvents:"none", zIndex:50,
          boxShadow:"0 8px 28px rgba(0,0,0,0.35)",
          fontFamily:FONT, width:cardW,
          animation:"sfFadeIn 0.15s ease both",
        };

        if (m.type === "badge") return (
          <div style={{...cardStyle, pointerEvents:"auto"}}>
            <div style={{ fontWeight:700, color:m.color, marginBottom:6 }}>
              {m.count} events at this point
            </div>
            {m.items.slice(0,8).map((item,i) => {
              const isDec = m.types[i]==="dec";
              const label = isDec
                ? (item.d?.icon||"📌")+" "+(item.d?.name||"Decision")
                : "📦 "+(item.o?.number||"Order");
              return (
                <button key={i} onClick={()=>{ setHoveredId(null); onNavigate(isDec?"planner":"orders"); }}
                  style={{ display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",
                    color:T.textSub,fontSize:11,padding:"4px 6px",borderRadius:4,cursor:"pointer",fontFamily:FONT,
                    transition:"background 0.1s" }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.accent+"18"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {label}
                </button>
              );
            })}
            {m.count>8 && <div style={{ color:T.textSub, fontSize:9, padding:"2px 6px" }}>+{m.count-8} more</div>}
          </div>
        );

        if (m.type === "dec") {
          const d = m.item.d;
          return (
            <div style={cardStyle}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                <span style={{ fontSize:14 }}>{d.icon||"📌"}</span>
                <span style={{ fontSize:12, fontWeight:700, color:m.color }}>{d.name}</span>
              </div>
              <div style={{ fontSize:12, color:T.textSub }}>Click for details</div>
            </div>
          );
        }

        // Order triangle
        const o = m.item.o;
        const totalCost = m.item.totalCost || 0;
        const firstPt = (o.paymentTerms||[])
          .filter(pt=>!pt.paid&&pt.date)
          .sort((a,b)=>a.date.localeCompare(b.date))[0];
        const firstPayAmt = firstPt ? Math.round((o.amount||0)*(firstPt.pct||0)/100) : null;
        return (
          <div style={cardStyle}>
            <div style={{ fontSize:10, color:T.textSub, marginBottom:4 }}>
              {o.orderDate && `📅 ${o.orderDate}`}
            </div>
            <div style={{ marginBottom:6, padding:"7px 10px",
              background:m.color+"18", borderRadius:6,
              border:`1px solid ${m.color}44` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                <span style={{ fontSize:12, fontWeight:700, color:m.color }}>📦 {o.number}</span>
                <span style={{ fontSize:13, fontWeight:800, color:m.color }}>
                  {currSym()}{Math.round(totalCost).toLocaleString()}
                </span>
              </div>
              <div style={{ display:"flex", gap:10, fontSize:12, color:T.textSub }}>
                {o.unitCount>0 && <span>📊 {o.unitCount.toLocaleString()} units</span>}
                {m.item.totalCt>0 && <span>📦 {m.item.totalCt} containers</span>}
              </div>
              {firstPt && (
                <div style={{ fontSize:10, color:m.color, marginTop:4, fontWeight:700 }}>
                  Next: {currSym()}{firstPayAmt?.toLocaleString()} due {firstPt.date} ({firstPt.pct}%)
                </div>
              )}
            </div>
            <div style={{ fontSize:9, color:T.textSub }}>Click to open in Orders</div>
          </div>
        );
      })()}

      {/* ── Individual order event hover tooltip ── */}
      {hoveredEventIdx !== null && selectedOrderData && scales && (() => {
        const ev = selectedOrderData.events[hoveredEventIdx];
        if (!ev || !ev.chartPt) return null;
        const xScale = scales.xScale;
        const yScale = scales.yScale;
        const bw = xScale.bandwidth ? xScale.bandwidth()/2 : 0;
        const cx = xScale(ev.chartPt.label) + bw;
        const cy = yScale(ev.chartPt.balance);
        if (isNaN(cx) || isNaN(cy)) return null;

        // Use container's bounding rect for fixed positioning
        const containerRect = containerRef.current?.getBoundingClientRect() || { left:0, top:0, width:400 };
        const cardW = 260;
        const screenX = containerRect.left + cx;
        const screenY = containerRect.top + cy;
        const left = screenX + 20 + cardW > window.innerWidth ? screenX - cardW - 16 : screenX + 20;
        const top = Math.max(screenY - 80, 8);

        const order = selectedOrderData.order;
        const oc = selectedOrderData.col;
        const TYPE_ICONS = { payment_term:"💳", shipping:"🚢", customs:"🛃", inspection:"🔍" };
        const TYPE_LABELS = { payment_term:"Payment Term", shipping:"Shipping / Freight", customs:"Customs & Duty", inspection:"Quality Inspection" };

        // Find batch info
        const batch = ev.batchId ? (order.containerBatches||[]).find(b=>b.id===ev.batchId) : null;
        const batchNum = batch ? (order.containerBatches||[]).indexOf(batch)+1 : null;

        // Find payment term info
        const pt = typeof ev.ptIdx === "number" && ev.ptIdx >= 0 ? (order.paymentTerms||[])[ev.ptIdx] : null;
        const ptNum = typeof ev.ptIdx === "number" && ev.ptIdx >= 0 ? ev.ptIdx + 1 : null;

        const dateStr = ev.date ? formatDate(ev.date, dateFormat) : "—";
        const paidAtStr = ev.paid && ev.paidAt ? formatDateTime(ev.paidAt, dateFormat) : null;

        // Ordinal helper
        const ordinal = n => n===1?"1st":n===2?"2nd":n===3?"3rd":`${n}th`;

        return (
          <div style={{
            position:"fixed", left, top, zIndex:9999,
            background:T.card, border:`1px solid ${oc?.text||T.accent}44`,
            borderRadius:RADIUS.lg, boxShadow:`0 16px 48px rgba(0,0,0,0.35), 0 0 0 1px ${oc?.text||T.accent}22`,
            padding:"14px 16px", width:cardW, fontFamily:FONT,
            animation:"dashMenuIn 0.15s ease both", pointerEvents:"none",
          }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ fontSize:16 }}>{TYPE_ICONS[ev.type]||"📌"}</span>
              <div>
                <div style={{ fontSize:14, fontWeight:800, color:oc?.text||T.accent }}>{order.number||"Order"}</div>
                <div style={{ fontSize:12, color:T.textSub }}>{TYPE_LABELS[ev.type]||ev.type}</div>
              </div>
            </div>

            {/* Amount */}
            <div style={{ fontSize:18, fontWeight:800, color:ev.paid?T.textSub:T.red, marginBottom:8, textDecoration:ev.paid?"line-through":"none" }}>
              −{currSym()}{Math.round(ev.amount).toLocaleString()}
            </div>

            {/* Details grid */}
            <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"4px 12px", fontSize:11 }}>
              <span style={{ color:T.textSub }}>📅 Date</span>
              <span style={{ color:T.text, fontWeight:600 }}>{dateStr}</span>

              {pt && <>
                <span style={{ color:T.textSub }}>📋 Belongs to</span>
                <span style={{ color:T.text, fontWeight:600 }}>
                  {pt.pct}%{pt.label ? ` — ${pt.label}` : ""}{pt.triggerType && pt.triggerType !== "manual" ? ` (${(pt.triggerType||"").replace(/_/g," ")}${pt.daysAfter ? ` +${pt.daysAfter} days` : ""})` : ""}
                </span>
              </>}

              {batchNum && <>
                <span style={{ color:T.textSub }}>📦 Batch</span>
                <span style={{ color:T.text, fontWeight:600 }}>Batch {batchNum}{batch?.containers ? ` · ${batch.containers} containers` : ""}</span>
              </>}

              {batch?.departureDate && <>
                <span style={{ color:T.textSub }}>🚢 Departure</span>
                <span style={{ color:T.text }}>{formatDate(batch.departureDate, dateFormat)}</span>
              </>}

              {batch?.arrivalDate && <>
                <span style={{ color:T.textSub }}>📍 Arrival</span>
                <span style={{ color:T.text }}>{formatDate(batch.arrivalDate, dateFormat)}</span>
              </>}

              <span style={{ color:T.textSub }}>📊 Status</span>
              <span style={{ color:ev.paid?T.green:ev.overdue?"#ff3b30":T.orange, fontWeight:600 }}>{ev.paid?"✅ Paid":ev.overdue?"🔴 Overdue":"⏳ Upcoming"}</span>

              {paidAtStr && <>
                <span style={{ color:T.textSub }}>⏱ Paid at</span>
                <span style={{ color:T.green, fontWeight:600 }}>{paidAtStr}</span>
              </>}
            </div>

            {ev.label && (
              <div style={{ marginTop:8, fontSize:10, color:T.textSub, borderTop:`1px solid ${T.border}`, paddingTop:6 }}>
                {ev.label}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}


// ── GlowRow — table row with violet focus glow on hover ──────────────────────
function GlowRow({ children, T, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        borderBottom: `1px solid ${T.border}`,
        background: hovered ? T.accent+"14" : T.card,
        boxShadow: hovered
          ? "inset 3px 0 0 "+T.accent+", 0 0 0 1px "+T.accent+"22"
          : "none",
        transition: "background 0.15s ease, box-shadow 0.15s ease",
        cursor: onClick ? "pointer" : "default",
      }}
    >{children}</tr>
  );
}

// ── BalancePanel — Today's balance, overdue, upcoming payments ────────────────
// Currently unused (Dashboard renders the balance section inline). Kept
// around as a reusable extraction for the day it gets wired back in.
// `can` is accepted as a prop but optional — when undefined, short-circuit
// falls through to "allow" so the component still renders.
function BalancePanel({ availableToday, months, orders, recurringExpenses, visibleYears, monthOps, onNavigate, can }) {
  const T = useTheme();
  const TODAY = useMemo(() => new Date(), []);
  const [payRange, setPayRange] = useState(() => LS.get(LS.PAY_RANGE, "month"));
  const [addTxOpen, setAddTxOpen] = useState(false);
  const [txForm, setTxForm] = useState({ what:"", amount:"", type:"expense", date:"" });
  const [txSaved, setTxSaved] = useState(false);

  function setPayRangePersist(r) { setPayRange(r); LS.set(LS.PAY_RANGE, r); }

  const upcomingPayments = useMemo(() => {
    const cutoff = new Date(TODAY);
    cutoff.setDate(cutoff.getDate() + (payRange === "week" ? 7 : 30));
    const payments = [];
    const orderExps = generateOrderExpenses(orders || []);
    for (const [, exps] of Object.entries(orderExps)) {
      for (const exp of exps) {
        const d = parseDate(exp.date);
        if (d && d >= TODAY && d <= cutoff) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          if (!(months[mKey]?.orderExpPaid || {})[exp.id])
            payments.push({ ...exp, dueDate: d, label: exp.what });
        }
      }
    }
    const recExps = generateRecurringItems(
      (recurringExpenses || []).filter(r => !r.isIncome),
      visibleYears || [TODAY.getFullYear()]
    );
    for (const [, items] of Object.entries(recExps)) {
      for (const item of items) {
        const d = parseDate(item.date);
        if (d && d >= TODAY && d <= cutoff) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          const ov = (months[mKey]?.recurringOverrides || {})[item.recurringId] || {};
          if (!ov.paid && !ov.deleted) payments.push({ ...item, dueDate: d, label: item.what });
        }
      }
    }
    return payments.sort((a,b) => a.dueDate - b.dueDate).slice(0, 6);
  }, [orders, months, recurringExpenses, visibleYears, TODAY, payRange]);

  const overduePayments = useMemo(() => {
    const payments = [];
    const orderExps = generateOrderExpenses(orders || []);
    for (const [, exps] of Object.entries(orderExps)) {
      for (const exp of exps) {
        const d = parseDate(exp.date);
        if (d && d < TODAY) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          if (!(months[mKey]?.orderExpPaid || {})[exp.id])
            payments.push({ ...exp, dueDate: d, label: exp.what });
        }
      }
    }
    const recExps = generateRecurringItems(
      (recurringExpenses || []).filter(r => !r.isIncome),
      visibleYears || [TODAY.getFullYear()]
    );
    for (const [, items] of Object.entries(recExps)) {
      for (const item of items) {
        const d = parseDate(item.date);
        if (d && d < TODAY) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          const ov = (months[mKey]?.recurringOverrides || {})[item.recurringId] || {};
          if (!ov.paid && !ov.deleted) payments.push({ ...item, dueDate: d, label: item.what });
        }
      }
    }
    return payments.sort((a,b) => b.dueDate - a.dueDate).slice(0, 4);
  }, [orders, months, recurringExpenses, visibleYears, TODAY]);

  function submitTx() {
    if (can && !can("transactions","create")) return;
    if (!txForm.what || !txForm.amount) return;
    const amt = parseFloat(txForm.amount);
    if (isNaN(amt) || amt <= 0) return;
    const d = txForm.date ? (() => { const p=txForm.date.split("."); return p.length===3 ? new Date(parseInt(p[2]),parseInt(p[1])-1,parseInt(p[0])) : new Date(txForm.date); })() : TODAY;
    const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
    if (txForm.type === "expense") {
      monthOps?.addExpense(mKey, { what: txForm.what, amount: amt, date: txForm.date || null });
    } else {
      monthOps?.addIncomeItem(mKey, { what: txForm.what, amount: amt });
    }
    setTxForm({ what:"", amount:"", type:"expense", date:"" });
    setTxSaved(true);
    setTimeout(() => setTxSaved(false), 2000);
    setTimeout(() => setAddTxOpen(false), 600);
  }

  const fmtAmt = n => {
    const s = currSym();
    if (Math.abs(n) >= 1000) return s + (n/1000).toFixed(1) + "K";
    return s + Math.round(n).toLocaleString();
  };

  const inp = { padding:"7px 10px", borderRadius:RADIUS.sm, border:`1px solid ${T.border}`, background:T.surface, color:T.text, fontFamily:FONT, fontSize:13, outline:"none", width:"100%" };

  return (
    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8, flexShrink:0 }}>

      {/* Today's Balance card */}
      <div style={{ background:`linear-gradient(135deg, ${T.accent}22 0%, ${T.teal}12 100%)`, border:`1px solid ${T.accent}30`, borderRadius:RADIUS.lg, padding:"14px 16px", minWidth:180, flex:"1 1 180px" }}>
        <div style={{ fontSize:10, fontWeight:700, color:T.accent, letterSpacing:0.8, textTransform:"uppercase", marginBottom:4 }}>Today's Balance</div>
        <div data-tour="available-today" style={{ fontSize:26, fontWeight:800, color: availableToday >= 0 ? T.green : T.red, letterSpacing:-0.8, lineHeight:1, marginBottom:10 }}>
          {fmtAmt(availableToday || 0)}
        </div>
        {/* Quick add transaction */}
        {(!can || can("transactions","create")) && <>
        <button onClick={() => setAddTxOpen(o=>!o)} style={{ width:"100%", padding:"6px 10px", borderRadius:RADIUS.sm, background: addTxOpen ? T.accent+"22" : T.accent+"12", border:`1px solid ${T.accent}44`, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.accent }}>+ Add Transaction</span>
          <span style={{ fontSize:9, color:T.accent, opacity:0.7, transition:"transform 0.15s", transform:addTxOpen?"rotate(180deg)":"none" }}>▼</span>
        </button>
        {addTxOpen && (
          <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ display:"flex", gap:4 }}>
              {["expense","income"].map(t => (
                <button key={t} onClick={() => setTxForm(f=>({...f,type:t}))} style={{ flex:1, padding:"5px 0", borderRadius:RADIUS.sm, background: txForm.type===t ? (t==="expense"?T.red:T.green)+"22" : "transparent", border:`1px solid ${txForm.type===t ? (t==="expense"?T.red:T.green) : T.border}`, color: txForm.type===t ? (t==="expense"?T.red:T.green) : T.textSub, fontFamily:FONT, fontSize:11, fontWeight:600, cursor:"pointer", textTransform:"capitalize" }}>{t}</button>
              ))}
            </div>
            <input placeholder="Description" value={txForm.what} onChange={e=>setTxForm(f=>({...f,what:e.target.value}))} style={inp}/>
            <input type="number" placeholder="Amount" value={txForm.amount} onChange={e=>setTxForm(f=>({...f,amount:e.target.value}))} style={inp}/>
            <DateInp value={txForm.date} onChange={v=>setTxForm(f=>({...f,date:v}))} placeholder="DD.MM.YYYY"/>
            <button onClick={submitTx} style={{ padding:"8px", borderRadius:RADIUS.sm, border:"none", background: txSaved ? T.green : `linear-gradient(135deg, ${T.accent}, ${T.teal})`, color:"#fff", fontFamily:FONT, fontSize:13, fontWeight:700, cursor:"pointer", transition:"background 0.3s ease" }}>
              {txSaved ? "✓ Saved!" : "Save"}
            </button>
          </div>
        )}
        </>}
      </div>

      {/* Overdue payments */}
      {overduePayments.length > 0 && (
        <div style={{ background:T.red+"12", border:`1px solid ${T.red}44`, borderRadius:RADIUS.lg, padding:"14px 16px", minWidth:160, flex:"1 1 160px" }}>
          <div style={{ fontSize:10, fontWeight:700, color:T.red, letterSpacing:0.8, textTransform:"uppercase", marginBottom:8 }}>⚠ Overdue ({overduePayments.length})</div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            {overduePayments.map((p,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 7px", background:T.red+"10", borderRadius:RADIUS.sm, border:`1px solid ${T.red}22` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:T.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.label}</div>
                  <div style={{ fontSize:10, color:T.red, opacity:0.8 }}>{p.dueDate.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"2-digit"})}</div>
                </div>
                <div style={{ fontSize:12, fontWeight:700, color:T.red, flexShrink:0, marginLeft:8 }}>{fmtAmt(p.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming payments */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", minWidth:200, flex:"2 1 200px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
          <div style={{ fontSize:10, fontWeight:700, color:T.textSub, letterSpacing:0.8, textTransform:"uppercase" }}>Payments Due</div>
          <div style={{ display:"flex", gap:3 }}>
            {["week","month"].map(r => (
              <button key={r} onClick={() => setPayRangePersist(r)} style={{ background: payRange===r ? T.accent : "transparent", color: payRange===r ? "#fff" : T.textSub, border:`1px solid ${payRange===r ? T.accent : T.border}`, borderRadius:RADIUS.pill, padding:"2px 8px", fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:FONT, textTransform:"capitalize" }}>{r}</button>
            ))}
          </div>
        </div>
        {upcomingPayments.length === 0 ? (
          <div style={{ fontSize:12, color:T.textSub, textAlign:"center", padding:"10px 0" }}>No payments in the next {payRange}</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            {upcomingPayments.map((p,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 9px", background:T.surface, borderRadius:RADIUS.sm, border:`1px solid ${T.border}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:T.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.label}</div>
                  <div style={{ fontSize:12, color:T.textSub }}>{p.dueDate.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:T.red, flexShrink:0, marginLeft:8 }}>{fmtAmt(p.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

export default function Dashboard({
  marketplaces, months, recurringIncome, recurringExpenses,
  orders, deliveryCreditLimit, incomeOverrides, availableToday, availableTodayHistory=[],
  initialStock=0, initialCostPerUnit=0, initialStockDate="", initialStockHistory=[],
  setInitialStock,
  visibleYears, setAvailableToday, modifiers=[], fbmSettings=[], partners=[], decisions=[],
  setIncomeOverride, clearIncomeOverride, monthOps, onNavigate,
  recurringIncomeOps, recurringExpenseOps, setPartners, onAddDecision,
  weekStartDay = 1,
  can, isOwner,
  inventoryPools=[], poolOps,
  dataKey = "main",
  orderOps,
  account,
  pastBalances,  // Map<"YYYY-MM-DD", balance> — frozen past balances from kpi_snapshots
}) {
  const T = useTheme();
  const isMobile = useIsMobile();
  const dateFormat = getDateFormat(months?._appSettings);
  // Animated digit display for KPI values
  const animVal = (val, color, size=24) => String(val).split("").map((d,i) =>
    <span key={i} style={{ display:"inline-block", fontSize:size, fontWeight:700, color, animation:`sfDigitUp 0.45s cubic-bezier(0.22,1,0.36,1) both`, animationDelay:`${i*55}ms` }}>{d}</span>
  );
  const TODAY = useMemo(() => new Date(), []);

  const [view,         setView]         = useState(() => { const v = LS.get(LS.DASH_VIEW, "month"); return v === "day" ? "week" : v; });
  const [chartFrom,    setChartFrom]    = useState(() => LS.get(LS.DASH_FROM,   "today"));
  const [viewPeriod,   setViewPeriod]   = useState(() => { const v = LS.get(LS.DASH_PERIOD, "all"); return v === "30d" ? "90d" : v; });
  const [activePanel,  setActivePanel]  = useState(() => LS.get(LS.DASH_PANEL,  "chart"));
  const [showDec,      setShowDec]      = useState(() => LS.get(LS.DASH_DEC,    "true") !== "false");
  const [showOrd,      setShowOrd]      = useState(() => LS.get(LS.DASH_ORD,    "true") !== "false");
  const [selectedOrderId, setSelectedOrderId] = useState(() => LS.get("sf_dash_sel_order", "all"));
  const [orderDropOpen, setOrderDropOpen] = useState(false);
  const [incomeOvOpen, setIncomeOvOpen] = useState(false);
  const [incomeOvMonth, setIncomeOvMonth] = useState("");
  const [incomeOvAmount, setIncomeOvAmount] = useState("");

  // Persist order selection
  useEffect(() => { LS.set("sf_dash_sel_order", selectedOrderId); }, [selectedOrderId]);

  // ── Order Simulator ──
  const [simOpen, setSimOpen] = useState(() => { try { return localStorage.getItem("sf_sim_open")==="true"; } catch { return false; } });
  const [simTab, setSimTab] = useState("order"); // order | schedule | impact
  // pt2Days defaults to "" instead of 0 so the input is empty (placeholder
  // visible) until the user types — fixes the leading-zero bug where the
  // displayed "0" couldn't be cleared, producing values like "0900".
  const simOrderDefault = {
    amount:"", units:"",
    paymentTerms:[
      { label:"Deposit", pct:30, triggerType:"days_after_order", daysAfter:0, date:"" },
      { label:"Balance", pct:70, triggerType:"days_after_arrival", daysAfter:0, date:"" },
    ],
    containerLines:[{ type:"40hc", qty:1 }],
    containerBatches:[{ containers:1, departureDate:"", arrivalDate:"", inStockMode:"days", inStockDays:7 }],
    shippingCostPerContainer:"", shippingPaymentDays:"", shippingPaymentTrigger:"arrival",
    // Legacy fields for simAnalysis compat
    pt1Pct:30, pt2Pct:70, pt1Date:"", arrivalDate:"", pt2Days:"", inStockDays:"", sellMonths:{},
  };
  const [simOrder, setSimOrder] = useState(() => { try { const s=localStorage.getItem("sf_sim_order"); if(!s) return simOrderDefault; const p=JSON.parse(s); if(!Array.isArray(p.paymentTerms)||!Array.isArray(p.containerBatches)) return simOrderDefault; return {...simOrderDefault,...p}; } catch { return simOrderDefault; } });
  const simReset = () => { setSimOrder(simOrderDefault); setSimCoverMonths(""); setSimLeadTime(""); try { localStorage.removeItem("sf_sim_order"); localStorage.removeItem("sf_sim_cover"); localStorage.removeItem("sf_sim_lead"); localStorage.setItem("sf_sim_open","false"); } catch {} setSimOpen(false); };
  // Helpers for array fields
  const simPatchTerm = (i, patch) => setSimOrder(s => {
    const terms = [...s.paymentTerms]; terms[i] = { ...terms[i], ...patch };
    // Sync legacy fields for simAnalysis
    const pt1Pct = terms[0]?.pct || 0;
    const pt2Pct = terms[1]?.pct || 0;
    const pt1Date = terms[0]?.date || "";
    return { ...s, paymentTerms: terms, pt1Pct, pt2Pct, pt1Date };
  });
  const simAddTerm = () => setSimOrder(s => ({
    ...s, paymentTerms: [...s.paymentTerms, { label:`Term ${s.paymentTerms.length+1}`, pct:0, triggerType:"days_after_arrival", daysAfter:0, date:"" }],
  }));
  const simRemoveTerm = (i) => setSimOrder(s => ({
    ...s, paymentTerms: s.paymentTerms.filter((_,j) => j !== i),
  }));
  const simPatchBatch = (i, patch) => setSimOrder(s => {
    const batches = [...s.containerBatches]; batches[i] = { ...batches[i], ...patch };
    // Sync legacy arrivalDate from first batch
    const arrivalDate = batches[0]?.arrivalDate || "";
    const inStockDays = batches[0]?.inStockDays || "";
    return { ...s, containerBatches: batches, arrivalDate, inStockDays };
  });
  const simPatchLine = (i, patch) => setSimOrder(s => {
    const lines = [...s.containerLines]; lines[i] = { ...lines[i], ...patch };
    return { ...s, containerLines: lines };
  });
  // Persist sim state to localStorage
  useEffect(() => { try { localStorage.setItem("sf_sim_open", simOpen?"true":"false"); } catch {} }, [simOpen]);
  useEffect(() => { try { localStorage.setItem("sf_sim_order", JSON.stringify(simOrder)); } catch {} }, [simOrder]);
  // Reorder planner fields (inside sim)
  const [simCoverMonths, setSimCoverMonths] = useState(() => { try { return localStorage.getItem("sf_sim_cover")||""; } catch { return ""; } });
  const [simLeadTime, setSimLeadTime] = useState(() => { try { return localStorage.getItem("sf_sim_lead")||""; } catch { return ""; } });
  useEffect(() => { try { localStorage.setItem("sf_sim_cover", simCoverMonths); } catch {} }, [simCoverMonths]);
  useEffect(() => { try { localStorage.setItem("sf_sim_lead", simLeadTime); } catch {} }, [simLeadTime]);
  // ── Scenario Planning ──
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [scenarioSales, setScenarioSales] = useState("");
  const [scenarioExpenses, setScenarioExpenses] = useState("");
  // Per-product mode: each adjustment targets a month + marketplace + product
  const [scenarioMode, setScenarioMode] = useState("brand"); // "brand" | "product"
  const [productAdjustments, setProductAdjustments] = useState([]);
  // Staging area for the "add adjustment" form
  const [adjMonth, setAdjMonth] = useState("");
  const [adjMktId, setAdjMktId] = useState("");
  const [adjProdId, setAdjProdId] = useState("");
  const [adjPct, setAdjPct] = useState("");

  const [selMkts,      setSelMkts]      = useState(() => LS.getJson(LS.DASH_MKTS, "all"));
  const [balanceGlow,  setBalanceGlow]  = useState(false);

  useEffect(() => {
    // Only glow once per browser session — sessionStorage clears on browser close / new login
    if (!sessionStorage.getItem("sf_balance_glowed")) {
      sessionStorage.setItem("sf_balance_glowed", "1");
      const t = setTimeout(() => {
        setBalanceGlow(true);
        setTimeout(() => setBalanceGlow(false), 2200);
      }, 600);
      return () => clearTimeout(t);
    }
  }, []);

  const sv  = v => { setView(v);        LS.set(LS.DASH_VIEW,   v); };
  const scf = v => { setChartFrom(v);   LS.set(LS.DASH_FROM,   v); };
  const svp = v => { setViewPeriod(v);  LS.set(LS.DASH_PERIOD, v); };
  const sap = v => { setActivePanel(v); LS.set(LS.DASH_PANEL,  v); };
  const ssd = v => { setShowDec(v);     LS.setBool(LS.DASH_DEC, v); };
  const sso = v => { setShowOrd(v);     LS.setBool(LS.DASH_ORD, v); };
  const ssm = v => { setSelMkts(v);     LS.setJson(LS.DASH_MKTS, v); };

  const [mktOpen,    setMktOpen]    = useState(false);
  const [viewOpen,   setViewOpen]   = useState(false);
  const [alertOpen,  setAlertOpen]  = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  useEffect(() => {
    if (!chartFullscreen) return;
    const onKey = e => { if (e.key === "Escape") setChartFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chartFullscreen]);
  const [addTxOpen, setAddTxOpen] = useState(false);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [payDueOpen,  setPayDueOpen]  = useState(false);
  const [payRangeD,   setPayRangeD]   = useState(() => LS.get(LS.PAY_RANGE, "month"));

  const activeMods = useMemo(() => {
    const k = mkKey(TODAY.getFullYear(), TODAY.getMonth()+1);
    return (modifiers||[]).filter(m => { if (m.type!=="payout"&&m.type!=="units") return false; const ks=Array.isArray(m.monthKeys)&&m.monthKeys.length>0?m.monthKeys:m.monthKey?[m.monthKey]:[]; return ks.includes(k); });
  }, [modifiers, TODAY]);

  const allRecurring = useMemo(() => [...recurringIncome,...recurringExpenses,...(partners||[]).map(p=>({...p,isIncome:false,type:p.type||"calendar"}))], [recurringIncome,recurringExpenses,partners]);
  const deliveryExp  = useCached("dash_deliveryExp", () => computeDeliveryExpenses(months,marketplaces,deliveryCreditLimit,fbmSettings), [months,marketplaces,deliveryCreditLimit,fbmSettings]);
  const orderExp     = useMemo(() => generateOrderExpenses(orders), [orders]);
  const baseRecItems  = useMemo(() => generateRecurringItems(allRecurring,visibleYears), [allRecurring,visibleYears]);
  const decRecItems   = useMemo(() => generateDecisionRecurringItems(decisions||[],visibleYears), [decisions,visibleYears]);
  const decOneItems   = useMemo(() => generateDecisionOneTimeItems(decisions||[],visibleYears), [decisions,visibleYears]);
  const adSpendItems  = useMemo(() => generateAdSpendExpenses(marketplaces,months,visibleYears), [marketplaces,months,visibleYears]);
  const recItems      = useMemo(() => {
    const merged = {...baseRecItems};
    for (const [k,v] of Object.entries(decRecItems)) {
      merged[k] = [...(merged[k]||[]), ...v];
    }
    for (const [k,v] of Object.entries(decOneItems)) {
      merged[k] = [...(merged[k]||[]), ...v];
    }
    for (const [k,v] of Object.entries(adSpendItems)) {
      merged[k] = [...(merged[k]||[]), ...v];
    }
    return merged;
  }, [baseRecItems, decRecItems, decOneItems, adSpendItems]);

  // filtMkts — the selected marketplaces with their REAL pctOfTotal preserved.
  // No renormalization: eBay at 30% stays 30% when selected alone. Adding
  // Amazon (70%) makes them sum to 100%. This ensures the chart ADDS
  // marketplace cashflows correctly instead of inflating a single selection
  // to look like the whole brand.
  //
  // filtMktsNorm — the same set but renormalized so pctOfTotal sums to 1.
  // Used ONLY for per-unit metrics (Blended Payout/Unit) where the user
  // expects the weighted average for the selection, not a fractional value.
  const filtMkts = useMemo(() => {
    const sel = (selMkts==="all"||!Array.isArray(selMkts)||!selMkts.length)
      ? marketplaces
      : marketplaces.filter(m=>selMkts.includes(m.id));
    return sel.length ? sel : marketplaces;
  }, [selMkts, marketplaces]);
  const filtMktsNorm = useMemo(() => {
    const tot = filtMkts.reduce((s,m)=>s+(Number(m.pctOfTotal)||0),0);
    if (tot > 0 && Math.abs(tot - 1) > 0.01) {
      return filtMkts.map(m=>({...m,pctOfTotal:tot>0?(Number(m.pctOfTotal)||0)/tot:1/filtMkts.length}));
    }
    return filtMkts;
  }, [filtMkts]);

  const [showBuffer, setShowBuffer] = useState(() => !!(months?._appSettings?.bufferThreshold));
  const bufferThreshold = Number(months?._appSettings?.bufferThreshold) || 0;

  const dailyDataRaw = useCached("dash_dailyData", () => computeDailyData({ marketplaces:filtMkts, months, deliveryExpenses:deliveryExp, orderExpenses:orderExp, recurringItems:recItems, incomeOverrides, availableToday, today:TODAY, visibleYears, chartFrom, modifiers }), [filtMkts,months,deliveryExp,orderExp,recItems,incomeOverrides,availableToday,TODAY,visibleYears,chartFrom,modifiers]);

  // Freeze past balances from kpi_snapshots. For each daily point BEFORE
  // today, if we have a snapshot for that date, overwrite `.balance` with
  // the snapshot value. Keeps the past purple line stable regardless of
  // changes to targets/orders/modifiers made today. Falls back to the
  // computed value when no snapshot exists (fresh accounts, missed cron).
  const dailyData = useMemo(() => {
    if (!pastBalances || !(pastBalances instanceof Map) || pastBalances.size === 0) return dailyDataRaw;
    return dailyDataRaw.map(d => {
      if (!d.isBeforeToday) return d;
      const dateStr = `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
      const snap = pastBalances.get(dateStr);
      return snap != null ? { ...d, balance: snap, _fromSnapshot: true } : d;
    });
  }, [dailyDataRaw, pastBalances]);

  // Simulated order analysis — runs whenever simOrder has an amount
  // (no longer gated on simOpen since the sim now lives inline in the megasim KPI card)
  const simAnalysis = useMemo(() => {
    const amt = parseFloat(simOrder.amount) || 0;
    if (amt <= 0) return null;
    const terms = simOrder.paymentTerms || [];
    const batch0 = (simOrder.containerBatches || [])[0] || {};
    const arrDate = batch0.arrivalDate ? parseDate(batch0.arrivalDate) : null;
    const depDate = batch0.departureDate ? parseDate(batch0.departureDate) : null;
    const inStockDays = parseInt(batch0.inStockDays) || 0;
    const inStockDate = arrDate ? new Date(arrDate.getTime() + inStockDays * 86400000) : null;
    const todayDate = new Date();
    const todayStr = `${String(todayDate.getDate()).padStart(2,"0")}.${String(todayDate.getMonth()+1).padStart(2,"0")}.${todayDate.getFullYear()}`;

    // Build cash events from ALL payment terms
    const cashEvents = [];
    for (let i = 0; i < terms.length; i++) {
      const t = terms[i];
      const pct = Number(t.pct) || 0;
      if (pct <= 0) continue;
      const payAmt = amt * pct / 100;
      let payDate = null;
      if (i === 0) {
        // First term: always use its date field
        payDate = t.date ? parseDate(t.date) : null;
      } else if (t.triggerType === "manual" && t.date) {
        payDate = parseDate(t.date);
      } else if (t.triggerType === "days_after_arrival" && arrDate) {
        payDate = new Date(arrDate.getTime() + (Number(t.daysAfter) || 0) * 86400000);
      } else if (t.triggerType === "days_after_departure" && depDate) {
        payDate = new Date(depDate.getTime() + (Number(t.daysAfter) || 0) * 86400000);
      } else if (t.triggerType === "days_after_order") {
        const orderDate = terms[0]?.date ? parseDate(terms[0].date) : todayDate;
        payDate = new Date(orderDate.getTime() + (Number(t.daysAfter) || 0) * 86400000);
      }
      if (payDate) {
        const ds = `${String(payDate.getDate()).padStart(2,"0")}.${String(payDate.getMonth()+1).padStart(2,"0")}.${payDate.getFullYear()}`;
        cashEvents.push({ label: `${t.label || `Payment ${i+1}`} (${pct}%)`, amount: payAmt, date: payDate, dateStr: ds, type: "payment_term" });
      }
    }
    // Add shipping cost events per batch — trigger: arrival or departure + days
    const shipCostPerCT = parseFloat(simOrder.shippingCostPerContainer) || 0;
    const shipPayDays = parseInt(simOrder.shippingPaymentDays) || 0;
    const shipTrigger = simOrder.shippingPaymentTrigger || "arrival";
    if (shipCostPerCT > 0) {
      for (const batch of (simOrder.containerBatches || [])) {
        const batchArr = batch.arrivalDate ? parseDate(batch.arrivalDate) : null;
        const batchDep = batch.departureDate ? parseDate(batch.departureDate) : null;
        const triggerDate = shipTrigger === "departure" ? batchDep : batchArr;
        if (!triggerDate) continue;
        const ct = Number(batch.containers) || 0;
        if (ct <= 0) continue;
        const shipAmt = shipCostPerCT * ct;
        const shipDate = new Date(triggerDate.getTime() + shipPayDays * 86400000);
        const ds = `${String(shipDate.getDate()).padStart(2,"0")}.${String(shipDate.getMonth()+1).padStart(2,"0")}.${shipDate.getFullYear()}`;
        cashEvents.push({ label: `🚢 Shipping (${ct} CT)`, amount: shipAmt, date: shipDate, dateStr: ds, type: "shipping" });
      }
    }
    const units = parseInt(simOrder.units) || 0;
    const analysis = cashEvents.length > 0 ? analyzeAffordability({ dailyData, cashEvents, bufferThreshold, marketplaces: filtMkts, months, orderUnits: units, inStockDate }) : null;
    const weightedPayout = filtMkts.reduce((s,m) => s + (m.pctOfTotal||0) * effectivePayoutForMkt(m, { months }, 30), 0);
    return { cashEvents, analysis, inStockDate, weightedPayout: Math.round(weightedPayout*100)/100, totalCost: Math.round(amt) };
  }, [simOrder, dailyData, bufferThreshold, filtMkts, months]);

  const weeklyData  = useMemo(() => aggregateWeekly(dailyData, weekStartDay), [dailyData, weekStartDay]);
  const monthlyData = useMemo(() => aggregateMonthly(dailyData,visibleYears), [dailyData,visibleYears]);

  const chartData = useMemo(() => {
    const base = view==="day"?dailyData.map(d=>({...d,label:d.shortLabel})):view==="week"?weeklyData:monthlyData;
    if (!base.length) return [];
    const ty=TODAY.getFullYear(), tm=TODAY.getMonth()+1, td=TODAY.getDate();
    const pd = d => d.year&&d.month&&d.day?new Date(d.year,d.month-1,d.day):d.year&&d.month?new Date(d.year,d.month-1,1):new Date(d.year||ty,0,1);
    let f=base;
    if (chartFrom==="today") { const s=new Date(ty,tm-1,td); f=base.filter(d=>pd(d)>=s); }
    else if (chartFrom==="jan1") { const s=new Date(ty,0,1); f=base.filter(d=>pd(d)>=s); }
    if (viewPeriod!=="all") {
      const c=new Date(ty,tm-1,td);
      if (viewPeriod==="30d") c.setDate(c.getDate()+30);
      else if (viewPeriod==="90d") c.setDate(c.getDate()+90);
      else if (viewPeriod==="6m") c.setMonth(c.getMonth()+6);
      else if (viewPeriod==="1y") c.setFullYear(c.getFullYear()+1);
      else if (viewPeriod==="2y") c.setFullYear(c.getFullYear()+2);
      f=f.filter(d=>pd(d)<=c);
    }
    return f.length?f:base.slice(-1);
  }, [dailyData,weeklyData,monthlyData,view,chartFrom,viewPeriod,TODAY]);

  // Scenario: single compound line showing the NET effect of the user's
  // sales % + expenses % inputs on the cumulative balance. No mirroring —
  // the user entered ONE scenario, they see ONE line.
  //
  // Logic:
  //   salesFactor = scenarioSales / 100 (negative = less income, positive = more)
  //   expFactor = scenarioExpenses / 100 (positive = more expenses, negative = less)
  //   Each day: incDelta = income × salesFactor (change in income)
  //             expDelta = expenses × expFactor (change in expenses — more expenses = worse)
  //   Net daily impact = incDelta - expDelta
  //   Cumulative impact compounds day by day → scenario balance = baseline + cumImpact
  //
  // Color: if scenario line is BELOW baseline → red ("worse"). ABOVE → green ("better").
  // Uses the "conservative" dataKey for red, "optimistic" for green — only ONE renders.
  // Pre-index per-product adjustments by month key for O(1) lookup in the
  // daily loop. Each month key maps to an array of { mktId, prodId, pct }.
  const _adjByMonth = useMemo(() => {
    if (!productAdjustments.length) return null;
    const map = {};
    for (const a of productAdjustments) {
      (map[a.monthKey] || (map[a.monthKey] = [])).push(a);
    }
    return map;
  }, [productAdjustments]);

  const scenarioData = useMemo(() => {
    if (!scenarioOpen || !chartData.length) return chartData;

    // ── Brand-wide factors (always applied, even in product mode) ─────
    const salesFactor = scenarioMode === "brand" ? (parseInt(scenarioSales) || 0) / 100 : 0;
    const expFactor = scenarioMode === "brand" ? (parseInt(scenarioExpenses) || 0) / 100 : 0;
    const hasProductAdj = scenarioMode === "product" && _adjByMonth;

    let cumImpact = 0;
    // Determine overall direction for color (conservative vs optimistic)
    let totalImpact = 0;
    for (const d of chartData) {
      if (d.isBeforeToday) continue;
      let dayDelta = (d.income || 0) * salesFactor - (d.expenses || 0) * expFactor;
      // Per-product adjustments: additive (marketplace total grows)
      if (hasProductAdj) {
        const dayKey = d.key || mkKey(d.year, d.month);
        const adjs = _adjByMonth[dayKey];
        if (adjs) {
          for (const a of adjs) {
            const mkt = marketplaces.find(m => m.id === a.mktId);
            if (!mkt) continue;
            const prod = (mkt.products || []).find(p => p.id === a.prodId);
            if (!prod) continue;
            // Product's fraction of total brand income
            const prodShare = (mkt.pctOfTotal || 0) * (prod.pctOfMarket || 0);
            dayDelta += (d.income || 0) * prodShare * (a.salesPct / 100);
          }
        }
      }
      totalImpact += dayDelta;
    }
    const isWorse = totalImpact < 0;

    cumImpact = 0;
    return chartData.map(d => {
      if (d.isBeforeToday) {
        return { ...d, conservative: isWorse ? d.balance : undefined, optimistic: isWorse ? undefined : d.balance };
      }
      let dayDelta = (d.income || 0) * salesFactor - (d.expenses || 0) * expFactor;
      if (hasProductAdj) {
        const dayKey = d.key || mkKey(d.year, d.month);
        const adjs = _adjByMonth[dayKey];
        if (adjs) {
          for (const a of adjs) {
            const mkt = marketplaces.find(m => m.id === a.mktId);
            const prod = mkt ? (mkt.products || []).find(p => p.id === a.prodId) : null;
            if (!mkt || !prod) continue;
            const prodShare = (mkt.pctOfTotal || 0) * (prod.pctOfMarket || 0);
            dayDelta += (d.income || 0) * prodShare * (a.salesPct / 100);
          }
        }
      }
      cumImpact += dayDelta;
      const scenarioBalance = Math.round(d.balance + cumImpact);
      return {
        ...d,
        conservative: isWorse ? scenarioBalance : undefined,
        optimistic: isWorse ? undefined : scenarioBalance,
      };
    });
  }, [scenarioOpen, scenarioMode, scenarioSales, scenarioExpenses, chartData, _adjByMonth, marketplaces]);

  // finalChartData is declared after inventoryTimeSeries (see below, ~line 2260)

  const yearKeys = useMemo(() => {
    const seen=new Set(), ks=[];
    for (const d of chartData) { const y=d.year; if (y&&!seen.has(y)) { seen.add(y); if (ks.length>0) ks.push({label:String(y),key:d.label||d.key}); else ks.push(null); } }
    return ks.filter(Boolean);
  }, [chartData]);

  // Key for "today" line — find chart data point matching or containing today
  const todayKey = useMemo(() => {
    if (chartFrom === "today") return null; // no line needed for "from today" view
    const ty = TODAY.getFullYear(), tm = TODAY.getMonth()+1, td = TODAY.getDate();
    const todayMs = new Date(ty, tm-1, td).getTime();
    // Find the data point that best represents today
    let bestLabel = null;
    for (let i = 0; i < chartData.length; i++) {
      const d = chartData[i];
      if (!d.year) continue;
      const dMs = new Date(d.year, (d.month||1)-1, d.day||1).getTime();
      const nextD = chartData[i+1];
      const nextMs = nextD ? new Date(nextD.year, (nextD.month||1)-1, nextD.day||1).getTime() : Infinity;
      // Point "contains" today if today falls between this point and the next
      if (dMs <= todayMs && todayMs < nextMs) { bestLabel = d.label; break; }
      // Exact match (month view)
      if (d.year === ty && d.month === tm && (view === "month" || !d.day)) { bestLabel = d.label; break; }
    }
    return bestLabel;
  }, [chartData, chartFrom, TODAY, view]);

  // Month start keys — one entry per month boundary visible in chart
  const monthKeys = useMemo(() => {
    if (view !== "day" && view !== "week") return []; // month view already shows month names
    const seen = new Set();
    const keys = [];
    for (const d of chartData) {
      if (!d.year || !d.month) continue;
      const mk = d.year + "-" + d.month;
      if (!seen.has(mk)) {
        seen.add(mk);
        keys.push({ label: MONTH_NAMES[d.month-1].slice(0,3) + " " + String(d.year).slice(2), key: d.label });
      }
    }
    return keys;
  }, [chartData, view]);


  const decDots = useMemo(() => {
    if (!showDec||!decisions.length) return [];
    return decisions.map((d,di) => {
      const k = decisionDateKey(d);
      if (!k) return null;
      const [ky, km] = k.split("-").map(Number);
      // Use exact day for day-precision decisions, day 15 for month/quarter
      const exactDay = d.date?.prec==="day"&&d.date?.day ? parseInt(d.date.day.split(".")[0])||15 : 15;
      const targetMs = new Date(ky, km-1, exactDay).getTime();
      // Find closest point in chartData by date — works across all views
      let bestIdx = -1, bestDist = Infinity;
      chartData.forEach((p, idx) => {
        const dist = Math.abs(ptMs(p) - targetMs);
        if (dist < bestDist) { bestDist = dist; bestIdx = idx; }
      });
      if (bestIdx < 0) return null;
      const pt = chartData[bestIdx];
      const color = plannerDotColor(decisions, d);
      return { d, pt, ptIdx: bestIdx, color };
    }).filter(Boolean);
  }, [decisions, chartData, showDec]);

  // ── Ghost line: counterfactual cashflow when hovering a decision ─────────
  const [ghostDecId, setGhostDecId] = useState(null);
  const [selectedDecId, setSelectedDecId] = useState(null);
  const [selectedDecPos, setSelectedDecPos] = useState(null); // {x, y} pixel position relative to viewport
  const activeGhostId = selectedDecId || ghostDecId;

  const ghostLine = useMemo(() => {
    if (!activeGhostId || !chartData.length) return null;
    const dec = decisions.find(d => d.id === activeGhostId);
    if (!dec) return null;

    // Find which chart point the decision starts at
    const decKey = decisionDateKey(dec);
    if (!decKey) return null;
    const [dy, dm] = decKey.split("-").map(Number);
    const decMs = new Date(dy, dm - 1, 1).getTime();

    // Compute monthly impact of this decision
    // 1. Recurring expenses created by this decision
    const linkedExpenses = recurringExpenses.filter(r => r.decisionId === activeGhostId);
    const monthlyExpenseImpact = linkedExpenses.reduce((s, r) => s + (r.amount || 0), 0);

    // 2. Modifiers created by this decision (per-month income impact from targets × payout)
    const linkedMods = (modifiers || []).filter(m => m.decisionId === activeGhostId);
    let monthlyIncomeImpact = 0;
    const curKey = mkKey(TODAY.getFullYear(), TODAY.getMonth()+1);
    const curTarget = months[curKey]?.target || 0;
    const wPayout = marketplaces.reduce((s,mkt) => s + (Number(mkt.pctOfTotal)||0) * effectivePayoutForMkt(mkt, { months }, 30), 0);
    for (const mod of linkedMods) {
      if (mod.type === "payout") monthlyIncomeImpact += curTarget * wPayout * (mod.value || 0);
      else if (mod.type === "units") monthlyIncomeImpact += curTarget * (mod.value || 0) * wPayout;
    }

    // 3. One-time entries (search months for items with this decisionId)
    let oneTimeCosts = 0, oneTimeIncome = 0;
    for (const key of Object.keys(months)) {
      const m = months[key];
      if (m?.expenses) for (const e of m.expenses) { if (e.decisionId === activeGhostId) oneTimeCosts += e.amount || 0; }
      if (m?.incomeItems) for (const e of m.incomeItems) { if (e.decisionId === activeGhostId) oneTimeIncome += e.amount || 0; }
    }

    // Net monthly delta: removing the decision means removing its expenses (balance goes UP)
    // and removing its income effects (balance goes DOWN)
    const netMonthlyDelta = monthlyExpenseImpact - monthlyIncomeImpact;

    // Total impact over visible period
    const startIdx = chartData.findIndex(d => {
      const dMs = new Date(d.year || dy, (d.month || dm) - 1, d.day || 1).getTime();
      return dMs >= decMs;
    });
    if (startIdx < 0) return null;

    const monthsAffected = chartData.length - startIdx;
    const totalExpenseImpact = (monthlyExpenseImpact * monthsAffected) + oneTimeCosts;
    const totalIncomeImpact = (monthlyIncomeImpact * monthsAffected) + oneTimeIncome;

    // Build ghost balance points (what balance would be WITHOUT this decision)
    let cumulativeDelta = 0;
    const points = [];
    for (let i = startIdx; i < chartData.length; i++) {
      // Each period, the delta accumulates
      if (i > startIdx || (view === "month")) cumulativeDelta += netMonthlyDelta;
      // One-time: apply at the decision month
      if (i === startIdx) cumulativeDelta += (oneTimeCosts - oneTimeIncome);

      points.push({
        idx: i,
        label: chartData[i].label,
        ghostBalance: chartData[i].balance + cumulativeDelta,
        actualBalance: chartData[i].balance,
      });
    }

    return {
      points,
      startIdx,
      totalIncome: Math.round(totalIncomeImpact),
      totalCost: Math.round(totalExpenseImpact),
      netImpact: Math.round(totalIncomeImpact - totalExpenseImpact),
      monthlyNet: Math.round(monthlyIncomeImpact - monthlyExpenseImpact),
      decisionName: dec.name,
      decisionIcon: dec.icon || "📌",
    };
  }, [activeGhostId, chartData, decisions, recurringExpenses, modifiers, months, view]);

  const currentKey = mkKey(TODAY.getFullYear(), TODAY.getMonth()+1);
  const currentMV  = useMemo(() => getMonthVars(currentKey,marketplaces,months), [currentKey,marketplaces,months]);
  // Blended payout uses filtMktsNorm (renormalized) so selecting eBay alone
  // shows eBay's per-unit payout, not 30% of it. This is a per-unit metric.
  const blended    = filtMktsNorm.reduce((s,m)=>s+(Number(m.pctOfTotal)||0)*effectivePayoutForMkt(m, { months }, 30),0);
  const hasDeductions = currentMV.marketplaces.some(m => m.products?.some(p => p.deductAdCostFromProjection || p.deductRefundFromProjection));
  const endOfYear  = monthlyData.find(m=>m.year===TODAY.getFullYear()&&m.month===12);
  const nextPay    = useMemo(() => getNextPaymentDue(months,deliveryExp,orderExp,recItems,TODAY), [months,deliveryExp,orderExp,recItems,TODAY]);
  const runway     = useMemo(() => calcRunway(dailyData,TODAY,bufferThreshold), [dailyData,TODAY,bufferThreshold]);
  // Today's calculated balance from the chart (same value you see hovering over today's date)
  const todayBalance = useMemo(() => {
    const ty = TODAY.getFullYear(), tm = TODAY.getMonth()+1, td = TODAY.getDate();
    const entry = dailyData.find(d => d.year === ty && d.month === tm && d.day === td);
    return entry ? entry.balance : availableToday;
  }, [dailyData, TODAY, availableToday]);
  const autoUnits  = effectiveUnits(currentKey,months,TODAY);
  const shrinkagePct = months?._targetSettings?.shrinkagePct ?? 2;
  const _globalInvSummary = useCached("dash_invSummary", () => computeInventorySummary(marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct), [marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct]);
  const _globalStockout = useMemo(() => computeStockoutDate({ currentStock: _globalInvSummary.totalUnits, orders, months, today: TODAY, shrinkagePct }), [_globalInvSummary.totalUnits, orders, months, TODAY, shrinkagePct]);
  // Pool-aware version when marketplace is filtered. Uses synthesizePoolForSelection
  // so a multi-marketplace selection (or "all") returns a virtual aggregate pool
  // instead of falling back to the first pool. This is the keystone fix for the
  // "Money in Inventory always shows Amazon" bug — when 'all' is selected we
  // pass every marketplace ID, so all pools that touch any of them contribute.
  //
  // When NO real inventory pools exist, we still synthesize a virtual pool from
  // the marketplaces themselves (with marketplaceIds = the selection). This is
  // critical: without it, Money in Inventory falls through to the legacy global
  // path and ignores marketplace-level / per-product overrides. The synthetic
  // pool gives effectivePoolStockAnchor a hook to apply the cascade.
  const activePool = useMemo(() => {
    const isFiltered = Array.isArray(selMkts) && selMkts.length > 0;
    const selectedIds = !isFiltered
      ? marketplaces.map(m => m.id)
      : selMkts;
    if (inventoryPools.length > 0) {
      const synth = synthesizePoolForSelection(inventoryPools, selectedIds, marketplaces);
      if (synth) return synth;
      // Selected marketplace(s) not in any pool — fall through to virtual pool
      // so the per-marketplace stock cascade still works (API stock, manual
      // override, per-product). Without this, the KPI shows the global brand
      // stock instead of the selected marketplace's stock.
    }
    // No matching pools — fabricate an empty placeholder pool whose
    // marketplaceIds covers the selection. effectivePoolStockAnchor will fall
    // through tier 0/1 (no overrides) and tier 2 (per-marketplace cascade).
    if (selectedIds.length === 0) return null;
    return {
      id: "_virtual_no_pool",
      name: "Virtual aggregate",
      marketplaceIds: selectedIds,
      orderIds: [],
      initialStock: 0,
      initialStockDate: null,
      initialCostPerUnit: 0,
      stockOverridden: false,
      _synthetic: true,
    };
  }, [inventoryPools, selMkts, marketplaces]);
  const inventorySummary = useMemo(() => {
    if (!activePool) return _globalInvSummary;
    return computeInventorySummary(marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct, activePool);
  }, [activePool, _globalInvSummary, marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct]);
  // Target scale: what fraction of the brand's monthly target does this
  // marketplace/pool selection represent? Used by stockout projection so
  // it depletes stock at the selection's sell rate, not the full brand rate.
  const _targetScale = useMemo(() => {
    if (!activePool || activePool.id === "_virtual_no_pool") {
      return filtMkts.reduce((s, m) => s + (Number(m.pctOfTotal) || 0), 0) || 1;
    }
    const poolMktIds = new Set(activePool.marketplaceIds || []);
    return marketplaces.filter(m => poolMktIds.has(m.id)).reduce((s, m) => s + (Number(m.pctOfTotal) || 0), 0) || 1;
  }, [activePool, filtMkts, marketplaces]);
  const stockoutDate = useMemo(() => {
    if (!activePool) return _globalStockout;
    return computeStockoutDate({ currentStock: inventorySummary.totalUnits, orders, months, today: TODAY, shrinkagePct, pool: activePool, targetScale: _targetScale });
  }, [activePool, _globalStockout, inventorySummary.totalUnits, orders, months, TODAY, shrinkagePct, _targetScale]);

  // ── Inventory time series — daily stock projection for chart tooltip ────
  // Projects stock (units + dollar value) at each calendar day, using the
  // same pool/marketplace-aware resolution as the "Money in Inventory" KPI.
  // Sensitive to the marketplace selector via `activePool`.
  const inventoryTimeSeries = useCached("dash_invTimeSeries", () => {
    if (!activePool && !initialStock) return null;
    const years = visibleYears.slice().sort();
    return computeStockTimeSeries({
      initialStock, initialCostPerUnit, initialStockDate,
      orders, months, today: TODAY, shrinkagePct,
      pool: activePool, marketplaces,
      startYear: years[0] || TODAY.getFullYear(),
      endYear: years[years.length - 1] || TODAY.getFullYear(),
    });
  }, [activePool, initialStock, initialCostPerUnit, initialStockDate, orders, months, TODAY, shrinkagePct, marketplaces, visibleYears]);

  // ── finalChartData — combines scenario + inventory enrichment ──────────
  // Placed here (after inventoryTimeSeries) because it needs both chartData
  // (declared above) and inventoryTimeSeries (depends on activePool).
  // Replaces the earlier `const finalChartData = ...` at line 2008.
  const finalChartData = useMemo(() => {
    const base = scenarioOpen ? scenarioData : chartData;
    if (!inventoryTimeSeries || !base.length) return base;
    return base.map(d => {
      const dk = `${d.year}-${d.month}-${d.day}`;
      const snap = inventoryTimeSeries.get(dk);
      return snap
        ? { ...d, inventoryValue: snap.value, inventoryUnits: snap.units }
        : d;
    });
  }, [scenarioOpen, scenarioData, chartData, inventoryTimeSeries]);

  // Sim reorder date — when stock drops below target after sim order arrives
  const simReorderInfo = useMemo(() => {
    if (!simOpen || !simAnalysis) return null;
    const units = parseInt(simOrder.units) || 0;
    const arrDate = (simOrder.containerBatches||[])[0]?.arrivalDate ? parseDate((simOrder.containerBatches||[])[0].arrivalDate) : null;
    if (!arrDate || units <= 0) return null;
    const currentStock = inventorySummary?.totalUnits || 0;
    const ltAgg = aggregateLeadTimes(orders||[]);
    const effectiveLT = parseInt(simLeadTime) || ltAgg.avg || 45;
    let stk = currentStock + units;
    for (let d = 0; d <= 365; d++) {
      const dt = new Date(arrDate.getTime() + d * 86400000);
      const k = mkKey(dt.getFullYear(), dt.getMonth()+1);
      const target = months[k]?.target || 0;
      const dim = new Date(dt.getFullYear(), dt.getMonth()+1, 0).getDate();
      stk -= target / dim;
      if (stk <= 0) {
        const reorderDate = new Date(dt.getTime() - effectiveLT * 86400000);
        const coverN = parseInt(simCoverMonths) || 0;
        let reorderUnits = 0;
        if (coverN > 0) {
          let ry = dt.getFullYear(), rm = dt.getMonth()+1;
          for (let i = 0; i < coverN; i++) {
            reorderUnits += months[mkKey(ry, rm)]?.target || target;
            rm++; if (rm > 12) { rm = 1; ry++; }
          }
        }
        const cogPerUnit = inventorySummary?.weightedCostPerUnit || 0;
        return { reorderDate, stockoutDate: dt, reorderUnits, reorderCost: Math.round(reorderUnits * cogPerUnit), leadTime: effectiveLT };
      }
    }
    return null;
  }, [simOpen, simAnalysis, simOrder, inventorySummary, orders, months, simLeadTime, simCoverMonths]);
  // Break-even uses filtered marketplaces so it reflects the selected marketplace view
  const breakeven = useMemo(() => computeBreakevenUnits(currentKey, filtMkts, months, deliveryExp, orderExp, recItems, { fbmSettings, inventorySummary, today: TODAY, bufferThreshold }), [currentKey, filtMkts, months, deliveryExp, orderExp, recItems, fbmSettings, inventorySummary, bufferThreshold]);
  // Filtered payout for the selected marketplace view
  const filtWeightedPayout = useMemo(() => filtMktsNorm.reduce((s,m)=>s+(Number(m.pctOfTotal)||0)*effectivePayoutForMkt(m, { months }, 30),0), [filtMktsNorm, months]);
  const curM       = months[currentKey]||{target:0};
  // Filtered target for the selected marketplace view
  const filtTarget = useMemo(() => {
    if (selMkts==="all"||!Array.isArray(selMkts)||!selMkts.length) return curM.target||0;
    // Sum marketplace portions for selected marketplaces
    return selMkts.reduce((s,id)=>{
      const mkt = marketplaces.find(m=>m.id===id);
      if (!mkt) return s;
      const ov = curM[`mktTarget_${id}`];
      return s + (ov != null ? ov : Math.round((curM.target||0)*(mkt.pctOfTotal||0)));
    }, 0);
  }, [selMkts, marketplaces, curM]);
  const todayStr   = `${TODAY.getFullYear()}-${TODAY.getMonth()+1}-${TODAY.getDate()}`;
  const hasUnitOv  = curM.unitsOverrideDate===todayStr && curM.unitsOverride!=null;
  const hasDelta   = curM.unitsOverrideDelta != null;
  const [overrideDeltaPrompt, setOverrideDeltaPrompt] = useState(null);
  const [showBalanceHistory, setShowBalanceHistory] = useState(false);
  const [editInitialSheet, setEditInitialSheet] = useState(false);
  const [editInitialStockSheet, setEditInitialStockSheet] = useState(false);
  const [stockUnitsInput, setStockUnitsInput] = useState("");
  const [stockCostInput, setStockCostInput] = useState("");
  const [balanceInput, setBalanceInput] = useState("");
  const [historyLimit, setHistoryLimit] = useState(50);
  // Raw auto-calculated units (without delta) for prompt comparison
  const rawAutoUnits = useMemo(() => {
    const dim = new Date(TODAY.getFullYear(), TODAY.getMonth()+1, 0).getDate();
    return Math.round((curM.target || 0) * ((dim - TODAY.getDate()) / dim));
  }, [curM.target, TODAY]);
  const issues     = useMemo(() => detectAppIssues(marketplaces,months,orders,visibleYears), [marketplaces,months,orders,visibleYears]);
  const errCt      = issues.filter(i=>i.severity==="error").length;
  const alertCt    = issues.length + activeMods.length;

  // ── Overdue + upcoming payments for pill icons ────────────────────────────
  const overduePayments = useMemo(() => {
    const today = new Date();
    const payments = [];
    const oExps = generateOrderExpenses(orders||[]);
    for (const [,exps] of Object.entries(oExps)) {
      for (const exp of exps) {
        const d = parseDate(exp.date);
        if (d && d < today) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          if (!(months[mKey]?.orderExpPaid||{})[exp.id]) payments.push({...exp, dueDate:d, label:exp.what});
        }
      }
    }
    const rItems = generateRecurringItems((recurringExpenses||[]).filter(r=>!r.isIncome), visibleYears||[today.getFullYear()]);
    for (const [,items] of Object.entries(rItems)) {
      for (const item of items) {
        const d = parseDate(item.date);
        if (d && d < today) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          const ov = (months[mKey]?.recurringOverrides||{})[item.id||item.recurringId]||{};
          if (!ov.paid && !ov.deleted) payments.push({...item, dueDate:d, label:item.what});
        }
      }
    }
    return payments.sort((a,b) => b.dueDate - a.dueDate).slice(0,8);
  }, [orders, months, recurringExpenses, visibleYears]);

  const upcomingPayments = useMemo(() => {
    const today = new Date();
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() + (payRangeD === "week" ? 7 : 30));
    const payments = [];
    const oExps = generateOrderExpenses(orders||[]);
    for (const [,exps] of Object.entries(oExps)) {
      for (const exp of exps) {
        const d = parseDate(exp.date);
        if (d && d >= today && d <= cutoff) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          if (!(months[mKey]?.orderExpPaid||{})[exp.id]) payments.push({...exp, dueDate:d, label:exp.what});
        }
      }
    }
    const rItems = generateRecurringItems((recurringExpenses||[]).filter(r=>!r.isIncome), visibleYears||[today.getFullYear()]);
    for (const [,items] of Object.entries(rItems)) {
      for (const item of items) {
        const d = parseDate(item.date);
        if (d && d >= today && d <= cutoff) {
          const mKey = mkKey(d.getFullYear(), d.getMonth()+1);
          const ov = (months[mKey]?.recurringOverrides||{})[item.id||item.recurringId]||{};
          if (!ov.paid && !ov.deleted) payments.push({...item, dueDate:d, label:item.what});
        }
      }
    }
    return payments.sort((a,b) => a.dueDate - b.dueDate).slice(0,8);
  }, [orders, months, recurringExpenses, visibleYears, payRangeD]);

  const sortedOrders = useMemo(() => [...orders].sort((a,b)=>a.number.localeCompare(b.number,undefined,{numeric:true})), [orders]);
  const oCM        = useMemo(() => { const m={}; sortedOrders.forEach((o,i)=>{m[o.id]=ORDER_COLORS[i%ORDER_COLORS.length];}); return m; }, [sortedOrders]);
  const orderSum   = useMemo(() => sortedOrders.map(o=>{ const col=oCM[o.id]||ORDER_COLORS[0],ct=getOrderTotalCt(o),val=o.amount||0; const ae=Object.entries(orderExp).flatMap(([mk,es])=>es.filter(e=>e.orderId===o.id).map(e=>({...e,mk}))); const paid=ae.reduce((s,e)=>s+((months[e.mk]?.orderExpPaid||{})[e.id]?e.amount:0),0); return {o,col,val,ct,paid,rem:val-paid,noDate:!o.orderDate}; }).filter(s=>s.val>0||s.noDate), [sortedOrders,orderExp,months,oCM]);

  // Order dots — always finds closest point across all views
  const orderDots = useMemo(() => {
    if (!showOrd || !orders.length) return [];
    return orders.filter(o => o.orderDate).map(o => {
      const parts = o.orderDate.split(".");
      if (parts.length !== 3) return null;
      const oy = parseInt(parts[2]), om = parseInt(parts[1]), od = parseInt(parts[0]);
      const targetMs = new Date(oy, om-1, od).getTime();
      let bestIdx = -1, bestDist = Infinity;
      chartData.forEach((p, idx) => {
        const dist = Math.abs(ptMs(p) - targetMs);
        if (dist < bestDist) { bestDist = dist; bestIdx = idx; }
      });
      if (bestIdx < 0) return null;
      const pt = chartData[bestIdx];
      const col = oCM[o.id] || ORDER_COLORS[0];
      const totalCt = getOrderTotalCt(o);
      const totalShip = totalCt*(o.shippingCostPerContainer||0)+(o.shippingCostTotal||0);
      return { o, pt, ptIdx: bestIdx, col, totalCt, totalCost:(o.amount||0)+totalShip };
    }).filter(Boolean);
  }, [orders, chartData, showOrd, oCM]);

  // ── Individual order cash events for chart overlay ──
  const selectedOrderData = useMemo(() => {
    if (selectedOrderId === "all" || !showOrd) return null;
    const order = orders.find(o => o.id === selectedOrderId);
    if (!order) return null;
    const { events, warnings } = getOrderCashEvents(order);
    const today = new Date();
    // Map events to chart positions and resolve paid status
    const mapped = events.map(ev => {
      const evMs = ev.date.getTime();
      let bestIdx = -1, bestDist = Infinity;
      chartData.forEach((p, idx) => {
        const dist = Math.abs(ptMs(p) - evMs);
        if (dist < bestDist) { bestDist = dist; bestIdx = idx; }
      });
      if (bestIdx < 0) return null;

      // Resolve paid status from months data
      const mKey = mkKey(ev.date.getFullYear(), ev.date.getMonth()+1);
      const monthData = months[mKey] || {};
      const orderExpPaid = monthData.orderExpPaid || {};
      const orderExpPaidAt = monthData.orderExpPaidAt || {};

      // Build expense ID matching generateOrderExpenses format
      let expId = null;
      if (ev.type === "payment_term" && ev.ptIdx === 0) {
        expId = `ord_${order.id}_pt0`;
      } else if (ev.type === "payment_term" && ev.batchId) {
        expId = `ord_${order.id}_pt${ev.ptIdx}_cb${ev.batchId}`;
      } else if (ev.type === "shipping" && ev.batchId) {
        expId = `ord_${order.id}_ship_cb${ev.batchId}`;
      } else if (ev.type === "customs" && ev.batchId) {
        expId = `ord_${order.id}_duty_cb${ev.batchId}`;
      } else if (ev.type === "inspection") {
        expId = `ord_${order.id}_inspection`;
      }

      const isPaid = !!order.completed || (expId && !!orderExpPaid[expId]);
      const paidAt = isPaid && expId && orderExpPaidAt[expId] ? orderExpPaidAt[expId] : null;
      const isOverdue = !isPaid && ev.date < today;

      return { ...ev, paid: isPaid, paidAt, overdue: isOverdue, expId, ptIdx: bestIdx, chartPt: chartData[bestIdx] };
    }).filter(Boolean);
    const col = oCM[order.id] || ORDER_COLORS[0];
    return { order, events: mapped, warnings, col };
  }, [selectedOrderId, orders, chartData, showOrd, oCM, months]);

  function handlePDF() {
    const w=window.open("","_blank"); if (!w) return;
    // Clone SVG and force black text/lines for print
    const svgEl = document.querySelector(".recharts-surface");
    let svg = "";
    if (svgEl) {
      const clone = svgEl.cloneNode(true);
      // Force all text to black
      clone.querySelectorAll("text").forEach(t => { t.setAttribute("fill","#333"); t.style.fill="#333"; });
      // Force grid lines to light gray
      clone.querySelectorAll(".recharts-cartesian-grid line").forEach(l => l.setAttribute("stroke","#e5e5e5"));
      svg = clone.outerHTML;
    }
    const tbl=document.getElementById("ch-monthly-table")?.outerHTML||"";
    w.document.write(`<!DOCTYPE html><html><head><title>CashHorizon Report</title><style>body{font-family:-apple-system,sans-serif;padding:24px;color:#111;background:#fff}h2{font-size:20px;font-weight:800;margin:0 0 4px;color:#111}p{font-size:12px;color:#666;margin:0 0 20px}.chart{width:100%;margin-bottom:28px;page-break-inside:avoid}.chart svg{width:100%!important;height:auto!important;max-height:280px}table{width:100%;border-collapse:collapse;font-size:12px}th{padding:7px 10px;font-size:10px;text-transform:uppercase;color:#888;border-bottom:2px solid #e5e5e5;font-weight:600}th:first-child,td:first-child{text-align:left}td{padding:7px 10px;text-align:right;border-bottom:1px solid #f0f0f0;color:#111}td:first-child{font-weight:600}@media print{@page{margin:15mm}}</style></head><body><h2>CashHorizon — Cashflow Report</h2><p>Generated ${new Date().toLocaleDateString()} · ${view.toUpperCase()} · ${chartFrom==="today"?"From Today":chartFrom==="jan1"?"From Jan 1st":"All Time"}</p><div class="chart">${svg}</div>${tbl}<script>window.onload=()=>window.print();</script></body></html>`);
    w.document.close();
  }

  const mktLbl = selMkts==="all"||!Array.isArray(selMkts)||!selMkts.length ? "All Marketplaces" : marketplaces.filter(m=>selMkts.includes(m.id)).map(m=>m.name).join(", ")||"All Marketplaces";
  const viewLbl = `${view==="week"?"Week":"Month"} · ${chartFrom==="today"?"Today":chartFrom==="jan1"?"Jan 1":"All"} · ${viewPeriod==="all"?"All":viewPeriod}`;

  // Reusable warning triangle for KPIs that have insufficient data to compute.
  // Renders nothing when `condition` is false. When true, shows a pulsing ⚠️
  // pinned to the BOTTOM-LEFT of the parent card via absolute positioning,
  // with a hover tooltip explaining what's missing. The card itself needs
  // `position:relative` for the absolute positioning to anchor correctly —
  // every KPI card already has padding so the icon doesn't overlap content.
  const NoDataWarn = ({ condition, reason }) => {
    if (!condition) return null;
    return (
      <UITooltip content={reason}>
        <span style={{
          position:"absolute", left:8, bottom:6,
          fontSize:14, cursor:"help",
          animation:"sfPulse 2s ease infinite",
          textShadow:`0 0 8px ${T.orange}`,
          zIndex:2,
        }}>⚠️</span>
      </UITooltip>
    );
  };
  // KPI history modal — opens when the user clicks the small 📊 button on a
  // KPI card. Shows daily snapshots from the kpi_snapshots table written by
  // the cron job at midnight UTC. The user only sees end-of-day final values.
  const [kpiHistory, setKpiHistory] = useState(null); // string kpiId or null
  const KpiHistoryBtn = ({ kpiId }) => (
    <UITooltip content="View daily history (snapshots taken at midnight UTC)">
      <button onClick={() => setKpiHistory(kpiId)}
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          padding: "0 4px", fontSize: 12, color: T.textSub, lineHeight: 1,
          marginLeft: 2, opacity: 0.7,
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = 1}
        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
        📊
      </button>
    </UITooltip>
  );
  // Pre-compute the "no data" reasons once so we can drop them on KPIs.
  const _noMkts = !marketplaces || marketplaces.length === 0;
  const _noProducts = (marketplaces||[]).every(m => (m.products||[]).length === 0);
  const _noStockData = (inventorySummary?.totalUnits || 0) === 0;
  const _noTargetThisMonth = (curM?.target || 0) === 0;
  const _noFiltTarget = (filtTarget || 0) === 0;
  const _noPayoutData = !blended || blended === 0;
  const _noRunwayData = runway === null;
  const _noNextPayments = !nextPay || (Array.isArray(nextPay) ? nextPay.length === 0 : !nextPay.amount);
  // Cash-to-Cash needs at least one of: stock data, payout delay, or order
  // lead-time. When ALL three are absent the cycle is meaningless and the
  // warning fires. (Previously the calc fabricated 90 days from a missing
  // lead-time so the user saw phantom production/shipping numbers.)
  const _noOrderLeadTime = aggregateLeadTimes(orders).avg == null;
  const _noCashCycleData = (inventorySummary?.daysOfStock || 0) === 0
    && (inventorySummary?.avgPayoutDelay || 0) === 0
    && _noOrderLeadTime;
  // Hoisted Cash-to-Cash days — used by both the Cash-to-Cash KPI card and
  // the Health Score (which now penalizes long cycles). Mirrors the inline
  // calc inside the C2C card. No fabrication: stages collapse to 0 when no
  // order data exists. Only the avgPayoutDelay default flows through.
  const cashToCashDays = (() => {
    const sellDays = inventorySummary.daysOfStock || 0;
    const payoutDays = inventorySummary.avgPayoutDelay || 0;
    const leadAgg = aggregateLeadTimes(orders);
    const hasLead = leadAgg.avg != null;
    const avgLeadDays = hasLead ? leadAgg.avg : 0;
    const prodDays = hasLead ? Math.round(avgLeadDays * 0.6) : 0;
    const shipDays = hasLead ? Math.round(avgLeadDays * 0.4) : 0;
    const prepDays = hasLead ? 7 : 0;
    return prodDays + shipDays + prepDays + sellDays + payoutDays;
  })();

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:0 }}>
      <style>{`
        @keyframes dashMenuIn{from{opacity:0;transform:translateY(-10px) scale(0.95)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes dotPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.3)}}
      `}</style>

      {/* ── KPI cards grid — fixed-left layout, megasim swaps the right side ──
           Left column (cols 1-3) is IMMUTABLE in both states:
             Row 1: cashToCash (spans 3 cols)
             Row 2: actions | blended | simulator
           Right side (cols 4-7) swaps:
             Default: 6 small KPIs (health bal run inv stk units) + payments tall col 7
             Sim open: megasim spans cols 4-7 across both rows */}
      <style>{`
        @keyframes kpiFadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .ch-kpi-grid{
          display:grid;
          grid-template-columns:repeat(7,1fr);
          grid-template-rows:minmax(80px, max-content) minmax(80px, max-content);
          gap:8px;margin-bottom:10px;flex-shrink:0;font-size:90%;
          /* Row 1: C2C(2) | Blended | Balance | Inventory | Reserve | Payments
             Row 2: Actions | Simulator | Health | Runway | Stockout | Units | Payments */
          grid-template-areas:
            "ctc ctc bld bal inv res pay"
            "act sim hea run stk units pay";
        }
        .ch-kpi-grid.sim-open{
          grid-template-areas:
            "ctc ctc mega mega mega mega mega"
            "act sim mega mega mega mega mega";
        }
        @media(max-width:1200px){
          .ch-kpi-grid{
            grid-template-columns:repeat(4,1fr);
            grid-template-areas:
              "ctc ctc bld bal"
              "act sim hea run"
              "pay pay inv stk"
              "units res . .";
          }
          .ch-kpi-grid.sim-open{
            grid-template-areas:
              "ctc ctc mega mega"
              "act sim mega mega"
              "mega mega mega mega"
              "mega mega mega mega";
          }
        }
        @media(max-width:768px){
          .ch-kpi-grid{
            grid-template-columns:repeat(2,1fr);
            grid-template-areas:
              "ctc ctc"
              "act sim"
              "bld bal"
              "hea run"
              "pay pay"
              "inv stk"
              "units res";
          }
          .ch-kpi-grid.sim-open{
            grid-template-areas:
              "ctc ctc"
              "act sim"
              "mega mega"
              "mega mega"
              "mega mega"
              "mega mega";
          }
        }
        .ch-kpi-grid>div{min-height:70px;text-align:center;display:flex;flex-direction:column;position:relative}
        .ch-kpi-grid .kpi-content{flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center}
        /* Grid-area assignments — direct mapping, no swaps */
        [data-kpi-id="cashToCash"]{grid-area:ctc}
        [data-kpi-id="actions"]{grid-area:act}
        [data-kpi-id="health"]{grid-area:hea}
        [data-kpi-id="blended"]{grid-area:bld}
        [data-kpi-id="simulator"]{grid-area:sim}
        [data-kpi-id="balance"]{grid-area:bal}
        [data-kpi-id="inventory"]{grid-area:inv}
        [data-kpi-id="reserve"]{grid-area:res}
        [data-kpi-id="payments"]{grid-area:pay}
        [data-kpi-id="runway"]{grid-area:run}
        [data-kpi-id="stockout"]{grid-area:stk}
        [data-kpi-id="units"]{grid-area:units}
        [data-kpi-id="reserve"]{grid-area:res}
        [data-kpi-id="megasim"]{grid-area:mega;animation:kpiFadeIn 0.32s ease both}
        /* When sim is open: hide ONLY the right-side small KPIs that megasim replaces.
           Left side (ctc/actions/blended/simulator) stays mounted and visible.
           !important is required because some cards (e.g. payments) have inline
           display:flex which would otherwise win the specificity battle. */
        /* Hide ALL KPIs except C2C, Actions, Simulator when sim is open.
           Megasim panel fills cols 3-7 across both rows. */
        .ch-kpi-grid.sim-open [data-kpi-id="blended"],
        .ch-kpi-grid.sim-open [data-kpi-id="health"],
        .ch-kpi-grid.sim-open [data-kpi-id="balance"],
        .ch-kpi-grid.sim-open [data-kpi-id="inventory"],
        .ch-kpi-grid.sim-open [data-kpi-id="reserve"],
        .ch-kpi-grid.sim-open [data-kpi-id="payments"],
        .ch-kpi-grid.sim-open [data-kpi-id="runway"],
        .ch-kpi-grid.sim-open [data-kpi-id="stockout"],
        .ch-kpi-grid.sim-open [data-kpi-id="units"]{display:none !important}
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="blended"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="health"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="balance"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="inventory"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="reserve"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="payments"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="runway"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="stockout"],
        .ch-kpi-grid:not(.sim-open) [data-kpi-id="units"]{animation:kpiFadeIn 0.32s ease both}
      `}</style>
      <div data-tour="kpi-cards" className={`ch-kpi-grid${simOpen ? " sim-open" : ""}`}>

        {/* Business Health Score */}
        {(()=>{
          const _sod = stockoutDate?.daysAway ?? null;
          const _ct = curM.target||0;
          const score = computeHealthScore({
            runway, stockoutDaysAway: _sod,
            breakeven, todayBalance, bufferThreshold, curTarget: _ct,
            cashToCashDays,
          });
          const color = score >= 70 ? T.green : score >= 40 ? T.orange : T.red;
          const label = score >= 70 ? "Healthy" : score >= 40 ? "Caution" : "At Risk";
          // Compute individual penalties for tooltip breakdown
          const _runwayPen = runway===Infinity||runway===null?0:runway>90?0:runway>60?5:runway>30?15:runway>14?22:runway>7?27:30;
          const _stockPen = _sod===null||_sod===undefined?0:_sod>90?0:_sod>60?5:_sod>30?12:_sod>14?18:25;
          const _beCov = breakeven&&breakeven.minUnits!==Infinity&&_ct>0?_ct/breakeven.minUnits:2;
          const _bePen = _beCov>=1.5?0:_beCov>=1.2?5:_beCov>=1?10:_beCov>=0.8?18:25;
          const _eb = bufferThreshold||0;
          const _balPen = todayBalance>_eb*3?0:todayBalance>_eb*2?3:todayBalance>_eb?8:todayBalance>0?14:20;
          const _c2cPen = cashToCashDays==null||cashToCashDays<=0?0:cashToCashDays<=60?0:cashToCashDays<=90?5:cashToCashDays<=120?10:cashToCashDays<=180?15:cashToCashDays<=365?20:25;
          const _totalPen = _runwayPen+_stockPen+_bePen+_balPen+_c2cPen;
          return (
            <div data-kpi-id="health" style={{ background:T.card, border:`1px solid ${color}44`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow, textAlign:"center" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                <span style={{ fontSize:12, fontWeight:700, color:T.textSub, letterSpacing:0.4, textTransform:"uppercase", textAlign:"left" }}>Health</span>
                <Info tip="healthScore" tipVars={{
                  runwayPen: _runwayPen, runwayVal: runway===Infinity?"365+":runway+"d",
                  stockPen: _stockPen, stockVal: _sod==null?"n/a":_sod+"d",
                  bePen: _bePen, beCov: Math.round(_beCov*100),
                  balPen: _balPen, balVal: fmtFull(todayBalance),
                  c2cPen: _c2cPen, c2cVal: (cashToCashDays||0)+"d",
                  totalPen: _totalPen, score,
                }}/>
              </div>
              <div className="kpi-content">
                <div style={{ letterSpacing:-1, lineHeight:1 }}>{animVal(String(score), color, 32)}</div>
                <div style={{ fontSize:12, fontWeight:700, color, marginTop:4, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
              </div>
            </div>
          );
        })()}

        {/* Today's Balance */}
        <div data-kpi-id="balance" style={{ background:T.card, border:`1px solid ${T.border}`,
          borderRadius:RADIUS.lg, padding:"14px 16px",
          animation: balanceGlow ? "sfOrangeGlow 0.7s ease-in-out 3" : "none",
        }}>
          <style>{`@keyframes sfOrangeGlow{0%,100%{box-shadow:${T.shadow}}50%{box-shadow:0 0 0 4px #f9731688,0 0 24px #f9731666}}`}</style>
          <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
            <Label tip="availableToday" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Available Today</Label>
            <FieldChangeMarker fieldKey="brand.main.availableToday" />
            <span data-field-key="brand.main.availableToday" />
            <KpiHistoryBtn kpiId="todayBalance" />
          </div>
          <div className="kpi-content">
            <div style={{ letterSpacing:-0.5 }}>{animVal(fmtFull(todayBalance), todayBalance>=0?T.green:T.red)}</div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:5, marginTop:4, flexWrap:"nowrap" }}>
            {(!can || can("settings","edit")) && <button onClick={()=>{ setBalanceInput(""); setEditInitialSheet(true); }} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.pill, padding:"2px 8px", fontSize:9, color:T.textSub, cursor:"pointer", fontFamily:FONT, fontWeight:600, whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:3 }}>
              🔧 Adjust <Info tip="availableToday"/>
            </button>}
            <button onClick={()=>setShowBalanceHistory(true)} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.pill, padding:"2px 8px", fontSize:9, color:T.textSub, cursor:"pointer", fontFamily:FONT, fontWeight:600, whiteSpace:"nowrap" }}>
              📋 History{availableTodayHistory.length > 0 ? ` (${availableTodayHistory.length})` : ""}
            </button>
          </div>
          </div>
        </div>

        {/* Actions card — Issues circle + Add Transaction */}
        <div data-kpi-id="actions" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8 }}>
          {/* Issues circle — doubled size */}
          <DropWrapper open={alertOpen} onClose={()=>setAlertOpen(false)} width={280}
            trigger={<button onClick={()=>{ setAlertOpen(o=>!o); setMktOpen(false); setViewOpen(false); }} style={{ width:45, height:45, borderRadius:"50%", background:alertCt>0?(errCt>0?T.red+"18":T.orange+"18"):T.surface, border:`2px solid ${alertCt>0?(errCt>0?T.red:T.orange):T.border}`, color:alertCt>0?(errCt>0?T.red:T.orange):T.textSub, fontSize:18, fontWeight:900, fontFamily:FONT, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
              !{alertCt>0&&<div style={{ position:"absolute",top:-3,right:-3,width:16,height:16,borderRadius:"50%",background:errCt>0?T.red:T.orange,color:"#fff",fontSize:9,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",animation:"dotPulse 2s ease-in-out infinite" }}>{alertCt}</div>}
            </button>}>
            <MSection label="Issues">
              {issues.length===0?<div style={{ padding:"8px 14px",fontSize:12,color:T.green }}>No issues found</div>
                :issues.slice(0,8).map((iss,i)=>(
                  <button key={i} onClick={()=>{ if(iss.navPage){ onNavigate?.(iss.navPage,{highlightId:iss.navHighlight,highlightType:"issue",ts:Date.now()}); setAlertOpen(false); } }}
                    style={{ display:"block",width:"100%",padding:"9px 14px",textAlign:"left",fontSize:12,color:iss.severity==="error"?T.red:T.orange,lineHeight:1.4,background:"transparent",border:"none",fontFamily:FONT,cursor:iss.navPage?"pointer":"default",borderBottom:`1px solid ${T.border}` }}
                    onMouseEnter={e=>{if(iss.navPage)e.currentTarget.style.background=T.surfaceHi;}} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    {iss.severity==="error"?"🔴":"🟡"} <strong>{iss.label}</strong>
                    {iss.detail&&<div style={{ fontSize:11,color:T.textSub,marginTop:2 }}>{iss.detail}</div>}
                  </button>
                ))
              }
            </MSection>
            <MenuSep/>
            <MSection label="Applied Modifiers">
              {activeMods.length===0?<div style={{ padding:"8px 14px",...subText(T) }}>No active modifiers</div>
                :activeMods.slice(0,5).map((m,i)=><div key={i} style={{ padding:"7px 14px",fontSize:12,color:T.orange }}>⚙️ {m.name} · {Math.round((m.value||0)*100)}%</div>)}
            </MSection>
          </DropWrapper>
          {/* Add Transaction */}
          {(!can || can("calendar","edit")) &&
            <button onClick={()=>setAddTxOpen(true)} style={{ width:"100%", background:T.accent, border:"none", borderRadius:RADIUS.sm, padding:"8px 10px", color:"#fff", fontSize:12, fontWeight:700, fontFamily:FONT, cursor:"pointer" }}>+ Add Transaction</button>
          }
        </div>

        {/* Blended payout */}
        <div data-kpi-id="blended" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
            <Label tip="blendedPayout" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Blended Payout/Unit</Label>
            <NoDataWarn condition={_noPayoutData} reason={_noMkts ? "No marketplaces configured. Add a marketplace on the Marketplaces page to start tracking blended payout." : _noProducts ? "Marketplaces have no products yet. Add a product or click 🧪 Try sample data on a marketplace card." : "Products have payout = 0. Open Marketplaces and enter the per-unit payout for at least one product."} />
            <KpiHistoryBtn kpiId="blendedPayout" />
          </div>
          <div className="kpi-content">
            <UITooltip content={filtMkts.map(m => `${m.name}: ${currSym()}${effectivePayoutForMkt(m, { months }, 30).toFixed(2)}/unit (${Math.round((m.pctOfTotal||0)*100)}%)`).join(" · ")}>
              <div style={{ cursor:"help" }}>
                {animVal(fmtFull(blended), T.green)}
                {hasDeductions && <div style={{ fontSize:11, color:T.accent, marginTop:1 }}>after deductions</div>}
              </div>
            </UITooltip>
          </div>
        </div>

        {/* ── MEGA SIM — 3-column inline panel: Cost & Payments | Shipping | Feedback ── */}
        {simOpen && (()=>{
          const amt = parseFloat(simOrder.amount) || 0;
          const hasMinInputs = amt > 0;
          const ready = hasMinInputs && simOrder.paymentTerms[0]?.date && simOrder.containerBatches[0]?.arrivalDate;
          const verdict = simAnalysis?.analysis?.overall?.canAfford;
          const ptSum = simOrder.paymentTerms.reduce((s,t)=>(s+(Number(t.pct)||0)),0);
          const ipS = { background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:12, fontWeight:600, padding:"5px 7px", outline:"none", fontFamily:FONT, boxSizing:"border-box" };
          const dateS = { padding:"5px 6px", fontSize:11, width:80 }; // compact DateInp to match ipS height
          const lbl = (text) => <div style={{ fontSize:9, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.4, marginBottom:2 }}>{text}</div>;
          const todayStr = `${String(TODAY.getDate()).padStart(2,"0")}.${String(TODAY.getMonth()+1).padStart(2,"0")}.${TODAY.getFullYear()}`;

          // Create order handler
          const handleCreateOrder = () => {
            if (!orderOps?.add) return;
            const _uid = () => Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);
            const color = nextUnusedColor(orders, ORDER_PALETTE);
            const mktIds = selMkts === "all" || !Array.isArray(selMkts) || !selMkts.length
              ? (marketplaces.length > 0 ? [marketplaces[0].id] : [])
              : [...selMkts];
            const newId = orderOps.add({
              number: `ORD-${String((orders||[]).length+1).padStart(3,"0")}`,
              orderDate: todayStr,
              amount: amt,
              color,
              marketplaceIds: mktIds,
              unitCount: parseInt(simOrder.units)||0,
              paymentTerms: simOrder.paymentTerms.map((t,i) => ({
                id: _uid(),
                label: t.label || (i===0 ? "Deposit" : `Payment ${i+1}`),
                pct: Number(t.pct)||0,
                triggerType: i===0 ? "days_after_order" : (t.triggerType || "days_after_arrival"),
                daysAfter: Number(t.daysAfter)||0,
                date: t.date || (i===0 ? todayStr : ""),
              })),
              containerLines: simOrder.containerLines.map(l => ({
                id: _uid(), type: l.type || "40hc", qty: Number(l.qty)||1, cbmOverride: null,
              })),
              containerBatches: simOrder.containerBatches.map(b => ({
                id: _uid(),
                containers: Number(b.containers)||1,
                departureDate: b.departureDate || "",
                arrivalDate: b.arrivalDate || "",
                inStockDate: "",
                inStockMode: b.inStockMode || "days",
                inStockDays: Number(b.inStockDays)||7,
              })),
              shippingCostPerContainer: parseFloat(simOrder.shippingCostPerContainer)||0,
              shippingPaymentDays: parseInt(simOrder.shippingPaymentDays)||0,
              shippingPaymentTrigger: simOrder.shippingPaymentTrigger || "arrival",
              totalContainers: simOrder.containerLines.reduce((s,l)=>s+(Number(l.qty)||0),0),
            });
            setSimOpen(false);
            simReset();
          };

          return (
          <div data-kpi-id="megasim" style={{
            background:T.card, border:`1px solid ${T.orange}44`, borderRadius:RADIUS.lg,
            boxShadow:`0 4px 20px ${T.orange}15`,
            display:"flex", flexDirection:"column",
            overflow:"hidden", height:"100%", minHeight:0,
          }}>
            {/* Header bar */}
            <div style={{ flexShrink:0, display:"flex", alignItems:"center", gap:8, borderBottom:`1px solid ${T.border}`, padding:"6px 12px" }}>
              <span style={{ fontSize:16 }}>🧪</span>
              <span style={{ fontSize:12, fontWeight:700, color:T.orange }}>Order Simulator</span>
              <span style={{ flex:1 }}/>
              <div style={{ padding:"2px 8px", borderRadius:RADIUS.pill,
                background:(verdict===true?T.green:verdict===false?T.red:T.textMuted)+"18",
                border:`1px solid ${(verdict===true?T.green:verdict===false?T.red:T.textMuted)}55`,
                color:verdict===true?T.green:verdict===false?T.red:T.textMuted,
                fontSize:10, fontWeight:800 }}>
                {!hasMinInputs?"Idle":!ready?"Need dates":verdict?"✅ Affordable":"❌ Not Affordable"}
              </div>
              <button onClick={simReset} title="Reset" style={{ background:"transparent", border:"none", color:T.textSub, cursor:"pointer", fontSize:12, fontFamily:FONT }}>↺</button>
              <UITooltip content={!ready?"Fill in all fields first":ptSum!==100?`Payment terms must sum to 100% (currently ${ptSum}%)`:verdict===false?"⚠️ This order may exceed your buffer — consider adjusting dates or amounts":"Ready to create"}>
              <button onClick={handleCreateOrder} disabled={!ready || ptSum!==100}
                style={{ padding:"4px 12px", borderRadius:RADIUS.sm, fontSize:11, fontWeight:700, fontFamily:FONT, cursor:ready&&ptSum===100?"pointer":"not-allowed",
                  background:ready&&ptSum===100?(verdict===false?"linear-gradient(135deg,#FF9500,#FF6B00)":"linear-gradient(135deg,#8b5cf6,#06b6d4)"):"transparent",
                  color:ready&&ptSum===100?"#fff":T.textMuted, border:ready&&ptSum===100?(verdict===false?`1px solid ${T.orange}`:"none"):`1px solid ${T.border}`,
                  opacity:ready&&ptSum===100?1:0.5, whiteSpace:"nowrap" }}>
                Create Order →
              </button>
              </UITooltip>
            </div>

            {/* 3-column body — fixed height, scroll per column */}
            <div style={{ flex:1, display:"grid", gridTemplateColumns:"35% 30% 35%", gap:0, minHeight:0, overflow:"hidden" }}>

              {/* ── Col 1: Cost & Payments ── */}
              <div style={{ borderRight:`1px solid ${T.border}`, padding:"8px 8px", overflowY:"auto" }}>
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  <div style={{ display:"flex", gap:5 }}>
                    <div style={{ flex:1 }}>
                      {lbl(`Amount (${currSym()})`)}
                      <input type="number" value={simOrder.amount} placeholder="20000" onChange={e=>setSimOrder(s=>({...s, amount:e.target.value}))} style={{ ...ipS, width:"100%", maxWidth:100 }}/>
                    </div>
                    <div style={{ flex:1 }}>
                      {lbl("Units")}
                      <input type="number" value={simOrder.units} placeholder="1000" onChange={e=>setSimOrder(s=>({...s, units:e.target.value}))} style={{ ...ipS, width:"100%", maxWidth:70 }}/>
                    </div>
                    <div style={{ flex:1 }}>
                      {lbl("Containers")}
                      <input type="number" value={simOrder.containerLines[0]?.qty??""} placeholder="1" onChange={e=>simPatchLine(0,{qty:e.target.value===""?null:Number(e.target.value)||0})} style={{ ...ipS, width:"100%", maxWidth:50 }}/>
                    </div>
                  </div>
                  {/* Unscheduled containers warning */}
                  {(()=>{
                    const totalCT = (simOrder.containerLines||[]).reduce((s,l)=>s+(Number(l.qty)||0),0);
                    const scheduledCT = (simOrder.containerBatches||[]).reduce((s,b)=>s+(Number(b.containers)||0),0);
                    if (totalCT > 0 && scheduledCT < totalCT) return <div style={{ fontSize:8, color:T.orange, marginTop:-2 }}>⚠️ {totalCT - scheduledCT} container{totalCT-scheduledCT!==1?"s":""} not scheduled in batches</div>;
                    return null;
                  })()}

                  {/* Payment terms */}
                  <div>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
                      {lbl("Payment Terms")}
                      <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ fontSize:8, fontWeight:700, color:ptSum===100?T.green:T.orange }}>{ptSum}%</span>
                        <button onClick={simAddTerm} style={{ background:T.accent+"15", border:`1px solid ${T.accent}40`, borderRadius:RADIUS.sm, color:T.accent, fontSize:8, fontWeight:700, padding:"1px 5px", cursor:"pointer", fontFamily:FONT }}>+</button>
                      </div>
                    </div>
                    {simOrder.paymentTerms.map((t,i) => (
                      <div key={i} style={{ display:"flex", gap:2, marginBottom:3, alignItems:"center", flexWrap:"wrap" }}>
                        <input value={t.label} onChange={e=>simPatchTerm(i,{label:e.target.value})} placeholder="Label" style={{ ...ipS, fontSize:10, width:55, flexShrink:0 }}/>
                        <input type="number" value={t.pct||""} placeholder="%" onChange={e=>simPatchTerm(i,{pct:Number(e.target.value)||0})} style={{ ...ipS, width:34, fontSize:10, textAlign:"center", flexShrink:0 }}/>
                        {i === 0 ? (
                          /* First term (Deposit) always shows date picker */
                          <DateInp value={t.date} onChange={v=>simPatchTerm(i,{date:v})} placeholder="Deposit date" style={dateS}/>
                        ) : (
                          <>
                            <select value={t.triggerType} onChange={e=>simPatchTerm(i,{triggerType:e.target.value})} style={{ ...ipS, fontSize:8, padding:"4px 1px", minWidth:0, width:70 }}>
                              <option value="days_after_order">After order</option>
                              <option value="days_after_arrival">After arrival</option>
                              <option value="days_after_departure">After depart.</option>
                              <option value="manual">Manual</option>
                            </select>
                            {t.triggerType==="manual"
                              ? <DateInp value={t.date} onChange={v=>simPatchTerm(i,{date:v})} style={dateS}/>
                              : <input type="number" value={t.daysAfter??""}  placeholder="0" onChange={e=>simPatchTerm(i,{daysAfter:e.target.value===""?0:Number(e.target.value)||0})} style={{ ...ipS, width:34, fontSize:10, textAlign:"center", flexShrink:0 }}/>
                            }
                          </>
                        )}
                        <button onClick={()=>simRemoveTerm(i)} style={{ background:"none", border:"none", color:T.red, cursor:"pointer", fontSize:10, padding:0, flexShrink:0 }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Col 2: Shipping ── */}
              <div style={{ borderRight:`1px solid ${T.border}`, padding:"8px 8px", overflowY:"auto" }}>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {/* Shipping cost */}
                  <div style={{ display:"flex", gap:4 }}>
                    <div style={{ flex:1 }}>
                      {lbl(`Ship/CT (${currSym()})`)}
                      <input type="number" value={simOrder.shippingCostPerContainer} placeholder="0" onChange={e=>setSimOrder(s=>({...s, shippingCostPerContainer:e.target.value}))} style={{ ...ipS, width:"100%" }}/>
                    </div>
                    <div style={{ flex:1 }}>
                      {lbl("Pay after")}
                      <select value={simOrder.shippingPaymentTrigger||"arrival"} onChange={e=>setSimOrder(s=>({...s, shippingPaymentTrigger:e.target.value}))} style={{ ...ipS, fontSize:9, padding:"4px 2px", width:"100%" }}>
                        <option value="arrival">Arrival</option>
                        <option value="departure">Departure</option>
                      </select>
                    </div>
                    <div style={{ width:40 }}>
                      {lbl("Days")}
                      <input type="number" value={simOrder.shippingPaymentDays} placeholder="0" onChange={e=>setSimOrder(s=>({...s, shippingPaymentDays:e.target.value}))} style={{ ...ipS, width:"100%", textAlign:"center" }}/>
                    </div>
                  </div>

                  {/* Batches */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    {lbl("Batches")}
                    <button onClick={()=>setSimOrder(s=>({...s, containerBatches:[...s.containerBatches, {containers:1,departureDate:"",arrivalDate:"",inStockMode:"days",inStockDays:7}]}))}
                      style={{ background:T.accent+"15", border:`1px solid ${T.accent}40`, borderRadius:RADIUS.sm, color:T.accent, fontSize:8, fontWeight:700, padding:"1px 5px", cursor:"pointer", fontFamily:FONT }}>+</button>
                  </div>
                  {simOrder.containerBatches.map((b,i) => (
                    <div key={i} style={{ background:T.surface, borderRadius:RADIUS.sm, padding:"6px 7px", border:`1px solid ${T.border}`, position:"relative" }}>
                      {simOrder.containerBatches.length > 1 && (
                        <button onClick={()=>setSimOrder(s=>({...s, containerBatches:s.containerBatches.filter((_,j)=>j!==i)}))}
                          style={{ position:"absolute", top:2, right:4, background:"none", border:"none", color:T.red, cursor:"pointer", fontSize:11, padding:0, fontWeight:700 }}>✕</button>
                      )}
                      <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                        <div style={{ flex:1 }}>
                          {lbl("Containers")}
                          <input type="number" value={b.containers??""}  placeholder="1" onChange={e=>simPatchBatch(i,{containers:e.target.value===""?null:Number(e.target.value)||0})} style={{ ...ipS, width:"100%", fontSize:10 }}/>
                        </div>
                        <div style={{ flex:1 }}>
                          {lbl("In-stock (days)")}
                          <input type="number" value={b.inStockDays||""} placeholder="7" onChange={e=>simPatchBatch(i,{inStockDays:Number(e.target.value)||0})} style={{ ...ipS, width:"100%", fontSize:10 }}/>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:4 }}>
                        <div style={{ flex:1 }}>
                          {lbl("Departure")}
                          <DateInp value={b.departureDate} onChange={v=>simPatchBatch(i,{departureDate:v})} style={dateS}/>
                        </div>
                        <div style={{ flex:1 }}>
                          {lbl("Arrival")}
                          <DateInp value={b.arrivalDate} onChange={v=>simPatchBatch(i,{arrivalDate:v})} style={dateS}/>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Col 3: Full Impact Dashboard ── */}
              <div style={{ padding:"6px 8px", overflowY:"auto", display:"flex", flexDirection:"column", gap:3 }}>
                {!hasMinInputs ? (
                  <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:T.textSub, fontSize:11, textAlign:"center", padding:12 }}>
                    Enter an order amount and dates to see the full impact
                  </div>
                ) : (()=>{
                  const units = parseInt(simOrder.units)||0;
                  const arrDate = simOrder.containerBatches[0]?.arrivalDate ? parseDate(simOrder.containerBatches[0].arrivalDate) : null;
                  const daysTilArrival = arrDate ? Math.max(0, Math.round((arrDate - TODAY) / 86400000)) : null;
                  const curTarget = (curM.target||0);
                  const dailySales = curTarget > 0 ? curTarget / 30 : 0;
                  const sellThroughDays = units > 0 && dailySales > 0 ? Math.round(units / dailySales) : null;
                  const currentStock = inventorySummary?.totalUnits || 0;
                  const stockAfter = currentStock + units;
                  const daysOfStockBefore = stockoutDate?.daysAway ?? null;
                  const daysOfStockAfter = dailySales > 0 ? Math.round(stockAfter / dailySales) : null;
                  const lowestPt = simAnalysis?.analysis?.overall?.lowestPoint;
                  // Current lowest (without sim)
                  const curLowest = dailyData.filter(d=>!d.isBeforeToday).reduce((min,d)=>d.balance<min?d.balance:min, Infinity);
                  // Runway after: walk modified balance
                  let simRunway = runway;
                  if (ready && simAnalysis?.analysis?.payments) {
                    const simPayDates = {};
                    for (const p of simAnalysis.analysis.payments) { if (p.date) { const dk=`${p.date.getFullYear()}-${p.date.getMonth()+1}-${p.date.getDate()}`; simPayDates[dk]=(simPayDates[dk]||0)+p.amount; } }
                    let simBal = todayBalance;
                    const future = dailyData.filter(d=>!d.isBeforeToday);
                    for (const d of future) { const dk=`${d.year}-${d.month}-${d.day}`; simBal=simBal+d.income-d.expenses-(simPayDates[dk]||0); if(simBal<bufferThreshold){ simRunway=Math.round((new Date(d.year,d.month-1,d.day)-TODAY)/86400000); break; } }
                    if (simBal>=bufferThreshold) simRunway=Infinity;
                  }
                  // Cash-to-cash: deposit date → sell-through end
                  const depositDate = simOrder.paymentTerms[0]?.date ? parseDate(simOrder.paymentTerms[0].date) : null;
                  const c2cDays = depositDate && sellThroughDays && daysTilArrival!=null ? daysTilArrival + sellThroughDays : null;
                  // Lead time
                  const ltAgg = aggregateLeadTimes(orders||[]);
                  const effectiveLT = parseInt(simLeadTime) || ltAgg.avg || 45;
                  // Reorder planner — uses simReorderInfo computed at top level
                  const coverN = parseInt(simCoverMonths)||0;
                  const reorderDate = simReorderInfo?.reorderDate || null;
                  const reorderUnits = simReorderInfo?.reorderUnits || 0;
                  const reorderCost = simReorderInfo?.reorderCost || 0;
                  // Build month breakdown for tooltip
                  const reorderMonthBreakdown = [];
                  if (coverN > 0 && simReorderInfo?.stockoutDate) {
                    let ry = simReorderInfo.stockoutDate.getFullYear(), rm = simReorderInfo.stockoutDate.getMonth()+1;
                    for (let i=0; i<coverN; i++) {
                      const k=mkKey(ry,rm);
                      const t = Math.round((months[k]?.target||curTarget));
                      reorderMonthBreakdown.push({ month:MONTH_NAMES[rm-1], year:ry, target:t });
                      rm++; if (rm>12){rm=1;ry++;}
                    }
                  }
                  const ml = { fontSize:8, color:T.textSub, textTransform:"uppercase", letterSpacing:0.3 };
                  const mktName = selMkts==="all"||!Array.isArray(selMkts) ? "All marketplaces" : filtMkts.map(m=>m.name).join(", ");
                  const arrow = (before,after,unit="") => <span style={{ fontSize:10 }}><span style={{ color:T.textSub }}>{before}{unit}</span> → <strong style={{ color:after<before?T.red:after>before?T.green:T.text }}>{after}{unit}</strong></span>;
                  return <>
                    {/* Marketplace indicator */}
                    <div style={{ fontSize:8, color:T.orange, textAlign:"center", fontWeight:600 }}>⚠️ Simulating for {mktName}</div>

                    {/* Before/After metrics */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:3 }}>
                      <div style={{ padding:"3px 6px", background:T.bg, borderRadius:RADIUS.sm, gridColumn:"span 2" }}>
                        <div style={ml}>Total outflow</div>
                        <div style={{ fontSize:13, fontWeight:700, color:T.red }}>{fmtFull(amt)}</div>
                      </div>
                      <div style={{ padding:"3px 6px", background:T.bg, borderRadius:RADIUS.sm }}>
                        <div style={ml}>Runway</div>
                        <div>{arrow(runway===Infinity?"365+":runway, simRunway===Infinity?"365+":simRunway, "d")}</div>
                      </div>
                      <div style={{ padding:"3px 6px", background:T.bg, borderRadius:RADIUS.sm }}>
                        <div style={ml}>Stock after</div>
                        <div style={{ fontSize:10, fontWeight:700, color:T.orange }}>{currentStock.toLocaleString()} → {stockAfter.toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                      {daysTilArrival!=null && <div style={{ padding:"2px 6px", background:T.surface, borderRadius:RADIUS.sm, fontSize:9, color:T.text, display:"flex", justifyContent:"space-between" }}>
                        <span>🚢 Arrives</span><strong>{daysTilArrival}d</strong>
                      </div>}
                      {sellThroughDays!=null && <div style={{ padding:"2px 6px", background:T.surface, borderRadius:RADIUS.sm, fontSize:9, color:T.text, display:"flex", justifyContent:"space-between" }}>
                        <span>📈 Sell-through</span><strong>~{sellThroughDays}d</strong>
                      </div>}
                      {lowestPt && <div style={{ padding:"2px 6px", background:T.surface, borderRadius:RADIUS.sm, fontSize:9, color:lowestPt.balance<0?T.red:T.text, display:"flex", justifyContent:"space-between" }}>
                        <span>📉 Lowest</span><strong>{fmtFull(lowestPt.balance)}</strong>
                      </div>}
                    </div>

                    {/* Payment schedule */}
                    {ready && simAnalysis?.analysis?.payments?.length > 0 && <>
                      <div style={ml}>Payments</div>
                      {simAnalysis.analysis.payments.map((p,i) => (
                        <div key={i} style={{ padding:"2px 6px", background:T.surface, borderRadius:RADIUS.sm, borderLeft:`2px solid ${p.aboveBuffer?T.green:p.balanceBefore===null?T.textMuted:T.red}`, fontSize:9 }}>
                          {p.balanceBefore===null ? <span style={{ color:T.textMuted }}>{p.label} — no date</span> : (
                            <div style={{ display:"flex", justifyContent:"space-between" }}>
                              <span style={{ fontWeight:600 }}>{p.label} {p.dateStr}</span>
                              <span>{fmtFull(p.balanceBefore)}→<strong style={{ color:p.aboveBuffer?T.green:T.red }}>{fmtFull(p.balanceAfter)}</strong></span>
                            </div>
                          )}
                        </div>
                      ))}
                    </>}

                    {/* Reorder planner */}
                    <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:3, marginTop:2 }}>
                      <div style={{ display:"flex", gap:4, alignItems:"center", marginBottom:3 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                            {lbl("Cover months")}
                            <UITooltip content={coverN>0&&reorderMonthBreakdown.length>0
                              ? `Covering ${coverN} months for ${mktName}:\n${reorderMonthBreakdown.map(m=>`${m.month} ${m.year}: ${m.target.toLocaleString()} units`).join("\n")}\nTotal: ${reorderUnits.toLocaleString()} units\nEst. cost: ${fmtFull(reorderCost)}`
                              : "How many months of targets to cover in the next order. Enter a number to see the reorder plan."}>
                              <span style={{ fontSize:10, cursor:"help", color:T.textSub }}>ℹ️</span>
                            </UITooltip>
                          </div>
                          <input type="number" value={simCoverMonths} placeholder="3" onChange={e=>setSimCoverMonths(e.target.value)} style={{ ...ipS, width:40, fontSize:10, textAlign:"center" }}/>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                            {lbl("Lead time")}
                            <UITooltip content={`Auto-calculated at ${effectiveLT}d from ${ltAgg.count||0} past orders${ltAgg.count>0?` (min ${ltAgg.min}d, max ${ltAgg.max}d, avg ${ltAgg.avg}d)`:""} for ${mktName}. Override if needed.`}>
                              <span style={{ fontSize:10, cursor:"help", color:T.textSub }}>ℹ️</span>
                            </UITooltip>
                          </div>
                          <input type="number" value={simLeadTime} placeholder={String(effectiveLT)} onChange={e=>setSimLeadTime(e.target.value)} style={{ ...ipS, width:40, fontSize:10, textAlign:"center" }}/>
                        </div>
                      </div>
                      {coverN > 0 && reorderDate && (
                        <div style={{ padding:"3px 6px", background:T.orange+"10", border:`1px solid ${T.orange}33`, borderRadius:RADIUS.sm, fontSize:9 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", color:T.orange, fontWeight:700 }}>
                            <span>🔄 Reorder by {reorderDate.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</span>
                            <span>{reorderUnits.toLocaleString()} units</span>
                          </div>
                          <div style={{ color:T.textSub, fontSize:8 }}>~{fmtFull(reorderCost)} · {coverN} months · lead {effectiveLT}d</div>
                        </div>
                      )}
                    </div>

                    {!ready && (
                      <div style={{ padding:"4px 8px", background:T.orange+"08", border:`1px solid ${T.orange}22`, borderRadius:RADIUS.sm, fontSize:9, color:T.orange, textAlign:"center" }}>
                        Set deposit date + arrival date for full impact
                      </div>
                    )}
                  </>;
                })()}
              </div>

            </div>
          </div>
          );
        })()}

        {/* Order Simulator KPI — toggle that expands the megasim panel inline (Targets-style) */}
        <div data-kpi-id="simulator" onClick={()=>setSimOpen(o=>!o)}
          style={{ background:T.card, border:`1px solid ${simOpen?T.orange:T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow, cursor:"pointer",
            transition:`all 0.2s ${SPRING}`, position:"relative", overflow:"hidden" }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=T.orange+"80";e.currentTarget.style.boxShadow=`0 0 20px ${T.orange}20`;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=simOpen?T.orange:T.border;e.currentTarget.style.boxShadow=T.shadow;}}>
          <style>{`
            @keyframes chBubA{0%{opacity:0;transform:translateY(0) scale(0)}15%{opacity:0.9;transform:translateY(-6px) scale(1)}85%{opacity:0.4;transform:translateY(-32px) scale(0.7)}100%{opacity:0;transform:translateY(-42px) scale(0)}}
            @keyframes chBubB{0%{opacity:0;transform:translateY(0) scale(0)}20%{opacity:0.85;transform:translateY(-8px) scale(0.9)}80%{opacity:0.3;transform:translateY(-30px) scale(0.5)}100%{opacity:0;transform:translateY(-40px) scale(0)}}
            @keyframes chBubC{0%{opacity:0;transform:translateY(0) scale(0)}25%{opacity:1;transform:translateY(-4px) scale(1.1)}75%{opacity:0.5;transform:translateY(-26px) scale(0.6)}100%{opacity:0;transform:translateY(-38px) scale(0)}}
            @keyframes chTwinkle{0%,100%{opacity:0;transform:scale(0) rotate(0deg)}50%{opacity:1;transform:scale(1) rotate(180deg)}}
            .ch-particle-field{position:absolute;inset:0;pointer-events:none;overflow:hidden}
            .ch-particle-field span{position:absolute}
          `}</style>
          {/* Particle field — bubbles and sparkles all over */}
          <div className="ch-particle-field">
            <span style={{ left:"8%",  bottom:"15%", fontSize:6, color:T.orange,  animation:"chBubA 3.2s ease 0s infinite" }}>●</span>
            <span style={{ left:"22%", bottom:"5%",  fontSize:5, color:"#FFD60A",animation:"chBubB 3.6s ease 0.4s infinite" }}>○</span>
            <span style={{ left:"38%", bottom:"20%", fontSize:7, color:T.accent, animation:"chBubC 2.8s ease 0.8s infinite" }}>●</span>
            <span style={{ left:"55%", bottom:"8%",  fontSize:5, color:T.orange,  animation:"chBubA 3.4s ease 1.2s infinite" }}>○</span>
            <span style={{ left:"70%", bottom:"18%", fontSize:6, color:"#FFD60A",animation:"chBubB 3s ease 1.6s infinite" }}>●</span>
            <span style={{ left:"85%", bottom:"12%", fontSize:5, color:T.accent, animation:"chBubC 3.8s ease 2s infinite" }}>○</span>
            <span style={{ left:"15%", top:"20%",   fontSize:7, color:T.orange,  animation:"chTwinkle 2.4s ease 0.2s infinite" }}>✦</span>
            <span style={{ left:"45%", top:"12%",   fontSize:6, color:"#FFD60A",animation:"chTwinkle 2.8s ease 0.6s infinite" }}>✧</span>
            <span style={{ left:"75%", top:"22%",   fontSize:7, color:T.accent, animation:"chTwinkle 2.6s ease 1.0s infinite" }}>✦</span>
            <span style={{ left:"30%", top:"45%",   fontSize:5, color:T.orange,  animation:"chTwinkle 3.2s ease 1.4s infinite" }}>✧</span>
            <span style={{ left:"62%", top:"50%",   fontSize:6, color:T.accent, animation:"chTwinkle 2.2s ease 1.8s infinite" }}>✦</span>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", position:"relative", zIndex:1 }}>
            <div style={{ marginBottom:8 }}>
              <span style={{ fontSize:28 }}>🧪</span>
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:simOpen?T.orange:T.text, letterSpacing:-0.2 }}>{simOpen?"Close":"Order Simulator"}</div>
            <div style={{ fontSize:11, color:T.textSub, marginTop:2 }}>{simOpen?"Hide simulator":"Test before you buy"}</div>
          </div>
        </div>

        {/* Next payment */}
        <div data-kpi-id="payments" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow, display:"flex", flexDirection:"column" }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:6 }}>
            <Label tip="nextPayments" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Next Payments</Label>
            <NoDataWarn condition={_noNextPayments} reason="No upcoming payments scheduled. Add a recurring expense, supplier order, or transaction so the calendar has something to project." />
            <KpiHistoryBtn kpiId="nextPayments" />
          </div>
          <div style={{ flex:1 }}>
          {(()=>{
            // Get next 3 payments from all sources.
            // Phase C — filter by filtMkts so the list reflects the marketplace
            // selector. Items with no marketplaceId (recurring expenses, manual
            // transactions, generic delivery) always pass; items tagged with a
            // marketplaceId are skipped if that marketplace isn't currently
            // selected. Order expenses are tagged via the order's
            // marketplaceIds[] (an order can be linked to multiple).
            const allPays = [];
            const oExps = generateOrderExpenses(orders||[]);
            const today = TODAY;
            const allowedMktIds = new Set(filtMkts.map(m => m.id));
            const orderMatchesFilter = (orderId) => {
              if (selMkts === "all" || !Array.isArray(selMkts) || !selMkts.length) return true;
              const order = (orders || []).find(o => o.id === orderId);
              if (!order) return true;
              const linked = order.marketplaceIds || [];
              if (linked.length === 0) return true;
              return linked.some(id => allowedMktIds.has(id));
            };
            const itemMktAllowed = (mktId) => {
              if (!mktId) return true;
              if (selMkts === "all" || !Array.isArray(selMkts) || !selMkts.length) return true;
              return allowedMktIds.has(mktId);
            };
            for (const [k,exps] of Object.entries(oExps)) {
              const mData = months[k] || {};
              for (const e of exps) {
                if ((mData.orderExpPaid||{})[e.id]) continue;
                if (!orderMatchesFilter(e.orderId)) continue;
                const d = parseDate(e.date);
                if (d && d >= today) allPays.push({ amount:e.amount, label:e.what?.split("—")[0]?.trim()||"Order", date:d, monthKey:k, id:e.id, _type:"order" });
              }
            }
            for (const [k,items] of Object.entries(recItems||{})) {
              const mData = months[k] || {};
              for (const item of items) {
                if (item.isIncome) continue;
                if (!itemMktAllowed(item.marketplaceId)) continue;
                const ov = (mData.recurringOverrides||{})[item.id||item.recurringId] || {};
                if (ov.paid || ov.deleted) continue;
                const d = parseDate(item.date);
                // Use item.id first (the full generated ID like "rec_XXX_2026-04")
                // which is what CashCalendar's TableRow uses as rowId.
                // The old code used recurringId (bare UUID) which didn't match.
                if (d && d >= today) allPays.push({ amount:ov.amount!==undefined?ov.amount:item.amount, label:item.what||item.name||"Recurring", date:d, monthKey:k, id:item.id||item.recurringId, _type:"recurring" });
              }
            }
            // Delivery expenses
            for (const [k,exps] of Object.entries(deliveryExp||{})) {
              const mData = months[k] || {};
              for (const e of exps) {
                if (!itemMktAllowed(e.marketplaceId)) continue;
                const dovr = (mData.deliveryOverrides||{})[e.id] || {};
                if (dovr.paid || dovr.deleted) continue;
                const d = parseDate(dovr.delayedDate || dovr.date || e.date);
                if (d && d >= today) allPays.push({ amount:e.amount, label:e.what||"Delivery", date:d, monthKey:k, id:e.id, _type:"delivery" });
              }
            }
            allPays.sort((a,b) => a.date - b.date);
            const top3 = allPays.slice(0,3);
            if (top3.length === 0) return <div style={{ fontSize:13, color:T.textMuted }}>No upcoming payments</div>;
            return top3.map((p,i) => (
              <div key={i} onClick={()=>onNavigate?.("cashflow",{month:p.monthKey,highlightId:p.id,highlightType:p._type||"order",ts:Date.now()})}
                style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:i<2?`1px solid ${T.border}`:"none", cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background=T.accent+"10"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{ flex:1, minWidth:0, textAlign:"left" }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.label}</div>
                  <div style={{ fontSize:12, color:T.textSub }}>{p.date.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</div>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:T.red, flexShrink:0, marginLeft:8, textAlign:"right" }}>{fmtFull(p.amount)}</div>
              </div>
            ));
          })()}
          </div>
          {/* Overdue indicator — pinned to bottom */}
          {overduePayments.length > 0 && (
            <DropWrapper open={overdueOpen} onClose={()=>setOverdueOpen(false)} width={300} align="right"
              trigger={<button onClick={()=>setOverdueOpen(o=>!o)} style={{ width:"100%", marginTop:"auto", padding:"5px 8px", borderRadius:RADIUS.sm, fontSize:12, fontFamily:FONT, cursor:"pointer", background:T.red+"12", border:`1px solid ${T.red}30`, color:T.red, fontWeight:700, textAlign:"center", position:"relative" }}>
                Overdue
                <span style={{ position:"absolute", top:-6, right:-6, width:18, height:18, borderRadius:"50%", background:T.red, color:"#fff", fontSize:10, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center" }}>{overduePayments.length}</span>
              </button>}>
              <div style={{ padding:"8px 12px 6px", fontSize:10, fontWeight:700, color:T.red, letterSpacing:0.8, textTransform:"uppercase" }}>Overdue Payments</div>
              {overduePayments.map((p,i) => (
                <button key={i} onClick={()=>{
                  const mKey = mkKey(p.dueDate.getFullYear(), p.dueDate.getMonth()+1);
                  onNavigate?.("cashflow",{month:mKey,highlightId:p.id||p.recurringId,highlightType:p.recurringId?"recurring":"order",ts:Date.now()});
                  setOverdueOpen(false);
                }} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 14px", width:"100%", background:"transparent", border:"none", cursor:"pointer", textAlign:"left", fontFamily:FONT, borderBottom:`1px solid ${T.border}` }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.red+"10"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <div><div style={{ fontSize:12, fontWeight:600, color:T.text }}>{p.label}</div><div style={{ fontSize:10, color:T.red }}>{p.dueDate.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</div></div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.red, marginLeft:12, flexShrink:0 }}>{fmtFull(p.amount)}</div>
                </button>
              ))}
              <div style={{ padding:"6px 14px", fontSize:11, color:T.textSub }}>Total: <strong style={{ color:T.red }}>{fmtFull(overduePayments.reduce((s,p)=>s+(p.amount||0),0))}</strong></div>
            </DropWrapper>
          )}
        </div>

        {/* Runway */}
        <div data-kpi-id="runway" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
            <Label tip="runway" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Runway</Label>
            <NoDataWarn condition={_noRunwayData} reason="Runway can't be computed yet — no projected balance flow. Add expenses, recurring items, or connect a marketplace so the cashflow forecast has data to walk forward." />
            <KpiHistoryBtn kpiId="runwayDays" />
          </div>
          <div className="kpi-content">
            <div>{runway===null||runway===Infinity?<span style={{fontSize:24,fontWeight:700,color:T.green,lineHeight:1}}>∞</span>:animVal(String(runway),(runway>90?T.green:runway>30?T.orange:T.red))}<span style={{ fontSize:12,marginLeft:4,color:T.textSub }}>days</span></div>
            <div style={{ fontSize:12,color:T.textSub }}>until {bufferThreshold > 0 ? `< ${fmt(bufferThreshold)}` : "< 0"}</div>
          </div>
        </div>

        {/* Current Month — mirrors Targets page Card 1 (pace, actual/target,
            status label, progress bar). Uses the marketplace-aware filtTarget
            and actualSold × selPct so it respects the marketplace selector. */}
        {(()=>{
          const dim = getDaysInMonth(TODAY.getFullYear(), TODAY.getMonth()+1);
          const dayOfMonth = TODAY.getDate();
          const rawTarget = curM.target || 0;
          // selPct: fraction of brand this selection represents (same as Targets)
          const selPct = filtMkts.reduce((s,m)=>s+(Number(m.pctOfTotal)||0),0);
          const fTarget = Math.round(rawTarget * selPct);
          const fActual = Math.round((curM.actualSold || 0) * selPct);
          const pace = dayOfMonth > 0 ? Math.round(fActual / dayOfMonth * dim) : 0;
          const progressPct = fTarget > 0 ? Math.min(100, Math.round(fActual / fTarget * 100)) : 0;
          const statusColor = fTarget === 0 ? T.textSub : pace >= fTarget ? T.green : pace >= fTarget * 0.7 ? T.orange : T.red;
          const statusLabel = fTarget === 0 ? "No target" : pace >= fTarget ? "On Track" : pace >= fTarget * 0.7 ? "Behind" : "Off Track";
          return (
          <div data-kpi-id="units" style={{ background:T.card, border:`1px solid ${statusColor}44`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <Label tip="unitsLeft" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Current Month</Label>
              <span style={{ fontSize:10, fontWeight:700, color:statusColor, padding:"2px 7px", borderRadius:RADIUS.pill, background:statusColor+"14", textTransform:"uppercase" }}>{statusLabel}</span>
            </div>
            <div className="kpi-content">
              <div>
                <span style={{ fontSize:22, fontWeight:800, color:T.text, letterSpacing:-0.5 }}>{fActual.toLocaleString()}</span>
                <span style={{ fontSize:12, color:T.textSub, marginLeft:4 }}>/ {fTarget.toLocaleString()}</span>
              </div>
              {fTarget > 0 && (
                <div style={{ marginTop:4, width:"100%" }}>
                  <div style={{ height:5, borderRadius:3, background:T.surfaceHi||T.border, overflow:"hidden" }}>
                    <div style={{ height:"100%", borderRadius:3, background:statusColor, width:`${progressPct}%`, transition:"width 0.6s ease" }}/>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.textSub, marginTop:2 }}>
                    <span>Pace: ~{pace.toLocaleString()}</span>
                    <span>Day {dayOfMonth}/{dim}</span>
                  </div>
                </div>
              )}
              <NoDataWarn condition={fTarget === 0} reason="No monthly target set. Open the Targets page and enter a units target for this month." />
            </div>
          </div>
          );
        })()}

        {/* Money blocked in inventory */}
        {(()=>{
          const hasOrderNoAmount = (orders||[]).some(o => !o.completed && (Number(o.amount)||0) === 0 && (o.containerBatches||[]).length > 0);
          const earliestArrival = (orders||[]).reduce((earliest, o) => {
            for (const cb of (o.containerBatches||[])) {
              const d = parseDate(cb.arrivalDate);
              if (d && d > TODAY && (!earliest || d < earliest)) earliest = d;
            }
            return earliest;
          }, null);
          // Compute units without COG. Walk every product across selected
          // marketplaces and check if the effective COG hierarchy produces > 0.
          // Units whose effective COG is 0 are "uncosted" — the Money in
          // Inventory value only reflects the portion with known costs.
          // Check EVERY product in the selection for missing COG, regardless of pool COG.
          // Pool COG only covers marketplaces IN that pool — other marketplaces need their own.
          let uncostedUnits = 0;
          if (inventorySummary.totalUnits > 0) {
            const selectedMktIds = new Set(filtMkts.map(m=>m.id));
            // Build a set of marketplace IDs covered by pools with COG > 0
            const poolCoveredMktIds = new Set();
            for (const pool of (inventoryPools || [])) {
              if (Number(pool.initialCostPerUnit) > 0) {
                for (const mid of (pool.marketplaceIds || [])) poolCoveredMktIds.add(mid);
              }
            }
            for (const mkt of marketplaces) {
              if (!selectedMktIds.has(mkt.id)) continue;
              // Skip disconnected marketplaces with no active products
              if (!mkt.apiConnection?.connected && (mkt.products||[]).filter(p=>!p._decoupled).length===0) continue;
              const coveredByPool = poolCoveredMktIds.has(mkt.id);
              if (coveredByPool) continue; // pool COG covers this marketplace
              const mktCog = Number(mkt.avgCogs) || 0;
              for (const p of mkt.products || []) {
                if (p._decoupled) continue;
                const stock = Number(p.currentStock) || Number(p.apiStock) || 0;
                if (stock <= 0) continue;
                if (mktCog > 0) continue; // mkt COG covers
                const groups = mkt.productGroups || [];
                const g = groups.find(gr => (gr.productIds||[]).includes(p.id));
                if (Number(g?.cogs) > 0) continue; // group COG covers
                const c = p.cogs || {};
                const prodCog = (Number(c.productCost)||0)+(Number(c.shipping)||0)+(Number(c.customs)||0)+(Number(c.prep)||0);
                if (prodCog > 0) continue; // product COG covers
                uncostedUnits += stock;
              }
            }
          }
          return (
          <div data-kpi-id="inventory" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
              <div style={{ display:"flex", alignItems:"center" }}>
                <Label tip="moneyInInventory" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Money in Inventory</Label>
                <NoDataWarn condition={_noStockData} reason="No stock data yet. Edit a pool's stock on the Marketplaces page (Inventory Pools tab), connect a marketplace API to sync inventory, or click 🧪 Try sample data on a marketplace card. Money in Inventory = current stock × cost-per-unit." />
                <KpiHistoryBtn kpiId="moneyInInventory" />
              </div>
              {hasOrderNoAmount && (
                <UITooltip content="Warning: One or more orders have no amount set. The blocked-in-transit calculation may be incomplete. Go to Orders and enter the order amount.">
                  <span style={{ fontSize:14, cursor:"help", animation:"sfPulse 2s ease infinite", textShadow:"0 0 8px #FF3B30" }}>⚠️</span>
                </UITooltip>
              )}
            </div>
            <div className="kpi-content">
              <div>{animVal(fmtFull(inventorySummary.totalValue + (inventorySummary.inTransitValue||0)), (inventorySummary.totalValue + (inventorySummary.inTransitValue||0)) > 0 ? T.orange : T.textMuted)}</div>
              <div style={{ fontSize:12, color:T.textSub }}>
                {inventorySummary.totalUnits > 0 ? `${inventorySummary.totalUnits.toLocaleString()} units` : "No stock data"}
                {(inventorySummary.inTransitValue||0) > 0 ? ` + ${fmtFull(inventorySummary.inTransitValue)} transit` : ""}
              </div>
              {/* COG coverage warning — when some units in stock have no cost
                  data, the displayed value understates the real inventory worth.
                  The user needs to fill in Avg COGS on the marketplace, group
                  cogs, or per-product COGS for the missing products. */}
              {uncostedUnits > 0 && (
                <UITooltip content={`${uncostedUnits.toLocaleString()} of ${inventorySummary.totalUnits.toLocaleString()} units have no COG set. The displayed value only reflects units with known costs. To fix: set Avg COGS/unit on the marketplace card, or group COG, or per-product COGS (productCost + shipping + customs + prep) on the Marketplaces page.`}>
                  <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:4, padding:"2px 8px", borderRadius:RADIUS.pill, background:T.orange+"12", border:`1px solid ${T.orange}40`, fontSize:11, color:T.orange, fontWeight:600, cursor:"help" }}>
                    ⚠️ COG missing for {uncostedUnits.toLocaleString()} units
                  </div>
                </UITooltip>
              )}
              {/* Edit Stock button removed — stock is now resolved through the
                  pool > marketplace > group > product hierarchy. To change a
                  stock value, the user goes to the source: pool stock on the
                  Marketplaces > Pools tab, marketplace-level on each marketplace
                  card, or per-product in the product editor. Adding an Edit
                  here would short-circuit the hierarchy in a confusing way. */}
            </div>
          </div>
          );
        })()}

        {/* Cash-to-Cash Cycle with animated timeline */}
        <style>{`@keyframes c2cReveal{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0 0 0)}}`}</style>
        <div data-kpi-id="cashToCash" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"10px 16px", boxShadow:T.shadow }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
            <Label tip="cashToCash" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Cash-to-Cash</Label>
            <NoDataWarn condition={_noCashCycleData} reason="Cash-to-Cash needs at least one of: (1) days-of-stock from a pool, (2) a marketplace payout delay, or (3) supplier-order lead times. When all three are missing the cycle would have to fabricate numbers (it used to default to 90 days). Add an order, set initial stock, or configure a marketplace payout delay." />
            <KpiHistoryBtn kpiId="cashToCash" />
          </div>
          {(()=>{
            // Reuse the hoisted cashToCashDays so this card and the Health
            // score read identical numbers. Also recompute the per-stage
            // breakdown for the timeline visual (which the hoisted value
            // alone can't supply).
            const sellDays = inventorySummary.daysOfStock || 0;
            const payoutDays = inventorySummary.avgPayoutDelay || 0;
            const leadAgg = aggregateLeadTimes(orders);
            const hasLead = leadAgg.avg != null;
            const avgLeadDays = hasLead ? leadAgg.avg : 0;
            const prodDays = hasLead ? Math.round(avgLeadDays * 0.6) : 0;
            const shipDays = hasLead ? Math.round(avgLeadDays * 0.4) : 0;
            const prepDays = hasLead ? 7 : 0;
            const totalDays = cashToCashDays;
            const c2cColor = totalDays <= 60 ? T.green : totalDays <= 120 ? T.orange : T.red;
            return <>
          <div style={{ textAlign:"center", marginBottom:6 }}>
            <span style={{ fontSize:24, fontWeight:700, color: c2cColor }}>
              {String(totalDays).split("").map((d,i)=><span key={i} style={{ display:"inline-block", animation:`sfDigitUp 0.45s cubic-bezier(0.22,1,0.36,1) both`, animationDelay:`${i*55}ms` }}>{d}</span>)}
            </span>
            <span style={{ fontSize:12, marginLeft:3, color:T.textSub }}>days</span>
          </div>
          {totalDays > 0 && (()=>{
            const stages = [
              { label:"Production", days:prodDays, icon:"🏭", color:"#ff9500" },
              { label:"Shipping", days:shipDays, icon:"🚢", color:"#06b6d4" },
              { label:"Prep", days:prepDays, icon:"📦", color:"#34c759" },
              { label:"Selling", days:sellDays, icon:"🎯", color:T.green },
              { label:"Payout", days:payoutDays, icon:"💰", color:"#FFD60A" },
            ];
            let cumPct = 0;
            return (
              <div>
                {/* Animated timeline bar — fills left to right */}
                <div style={{ display:"flex", alignItems:"center", gap:4, height:6, animation:"c2cReveal 1.8s cubic-bezier(0.22,1,0.36,1) both" }}>
                  {stages.map((st,i) => {
                    const pct = totalDays > 0 ? Math.max(5, (st.days / totalDays) * 100) : 20;
                    return <div key={i} style={{ flex:`${pct} 0 0`, height:"100%", background:st.color, borderRadius:3 }}/>;
                  })}
                </div>
                {/* Icons + days below line */}
                <div style={{ display:"flex", marginTop:4 }}>
                  {stages.map((st,i) => {
                    const pct = totalDays > 0 ? Math.max(5, (st.days / totalDays) * 100) : 20;
                    return <div key={i} style={{ flex:`${pct} 0 0`, textAlign:"center" }}>
                      <span style={{ fontSize:12 }}>{st.icon}</span>
                      <div style={{ fontSize:9, color:T.textSub }}>{st.days}d</div>
                    </div>;
                  })}
                </div>
              </div>
            );
          })()}
            </>;
          })()}
        </div>

        {/* Stockout Date */}
        <div data-kpi-id="stockout" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
            <Label tip="stockoutDate" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>Stockout Date</Label>
            <NoDataWarn condition={_noStockData} reason="No stock data yet — can't project a stockout date. Set initial stock on a pool, connect a marketplace API, or load sample data on a marketplace." />
            <KpiHistoryBtn kpiId="stockoutDays" />
          </div>
          <div className="kpi-content">
            {stockoutDate ? (<>
              <div>{animVal(stockoutDate.date.toLocaleDateString("en-GB",{day:"2-digit",month:"short"}), stockoutDate.daysAway<=30?T.red:stockoutDate.daysAway<=60?T.orange:T.green)}</div>
              <div style={{ fontSize:12, color:T.textSub }}>{stockoutDate.daysAway} days</div>
            </>) : (<>
              <div style={{ fontSize:24, fontWeight:700, color:T.green }}>—</div>
              <div style={{ fontSize:12, color:T.textSub }}>{inventorySummary.totalUnits > 0 ? "365+ days" : "No data"}</div>
            </>)}
          </div>
        </div>

        {/* Min. Sales KPI removed per user request — was too noisy and the
            user's workflow doesn't need a hard break-even floor on the
            Dashboard. Health Score covers the same risk signal more
            holistically. */}

        {/* Reserve Held — computes from 3 sources per marketplace:
            1. Manual override (mkt.reserveHeld with _overridden flag)
            2. API-synced (Amazon/Shopify — via mkt.reserveHeld after adapter copy)
            3. Rate-based estimate (mkt.reserveRate × recent 30d payouts)
            Respects marketplace/pool selection via filtMkts. */}
        {(()=>{
          // Compute effective reserve per marketplace with source tracking
          const reserveDetails = filtMkts.map(m => {
            const manual = Number(m.reserveHeld) || 0;
            const apiVal = Number(m.apiConnection?.reservesHeld) || 0;
            const rate = Number(m.reserveRate) || 0;
            let amount = 0;
            let source = "none";
            if (m._overridden?.reserveHeld && manual > 0) {
              amount = manual; source = "manual";
            } else if (manual > 0) {
              amount = manual; source = apiVal > 0 ? "api" : "manual";
            } else if (rate > 0) {
              // Estimate from recent payouts (last 30 days of incomeItems for this mkt)
              let recentPayouts = 0;
              const cutoff = new Date(TODAY.getTime() - 30 * 86400000);
              for (const [k, mData] of Object.entries(months || {})) {
                for (const inc of (mData.incomeItems || [])) {
                  if (inc.marketplaceId !== m.id) continue;
                  if (inc._apiSourced) {
                    const d = inc.date ? parseDate(inc.date) : null;
                    if (d && d >= cutoff) recentPayouts += Number(inc.amount) || 0;
                  }
                }
              }
              amount = Math.round(recentPayouts * rate);
              source = "estimated";
            }
            return { id: m.id, name: m.name, amount, source, rate };
          });
          const totalReserve = reserveDetails.reduce((s, r) => s + r.amount, 0);
          const withReserve = reserveDetails.filter(r => r.amount > 0);
          return (
          <div data-kpi-id="reserve" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"14px 16px", boxShadow:T.shadow }}>
            <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
              <Label tip="reserveHeld" style={{ fontSize:12, fontWeight:700, letterSpacing:0.4, textAlign:"left" }}>🔒 Reserve Held</Label>
              <KpiHistoryBtn kpiId="reserveHeld" />
            </div>
            <div className="kpi-content">
              <div>{animVal(fmtFull(totalReserve), totalReserve > 0 ? T.orange : T.textMuted)}</div>
              {withReserve.length > 0 ? (
                <div style={{ fontSize:11, color:T.textSub, lineHeight:1.5 }}>
                  {withReserve.map(r => (
                    <span key={r.id} style={{ display:"inline-flex", alignItems:"center", gap:3, marginRight:8 }}>
                      {r.name}
                      <span style={{
                        fontSize:8, padding:"1px 4px", borderRadius:4, fontWeight:700,
                        background: r.source==="api" ? T.green+"18" : r.source==="manual" ? T.accent+"18" : T.orange+"18",
                        color: r.source==="api" ? T.green : r.source==="manual" ? T.accent : T.orange,
                      }}>
                        {r.source==="api" ? "API" : r.source==="manual" ? "MANUAL" : `~${Math.round(r.rate*100)}%`}
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize:11, color:T.textSub }}>Set a reserve rate (%) on each marketplace or use API</div>
              )}
            </div>
          </div>
        ); })()}
      </div>

      {/* Chart + controls wrapper — fullscreen wraps everything */}
      {(fsEl => chartFullscreen ? createPortal(fsEl, document.body) : fsEl)(
      <div style={chartFullscreen ? { position:"fixed", inset:0, zIndex:9998, background:T.bg, display:"flex", flexDirection:"column", padding:12, gap:6, overflow:"hidden" } : {}}>
      {/* Controls row */}
      <div data-tour="chart-controls" className={isMobile?"sf-hscroll":""} style={{ display:"flex", gap:6, marginBottom:chartFullscreen?0:8, alignItems:"center", justifyContent:"center", flexShrink:0, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible" }}>

        {/* Issues + Overdue + Due moved to KPI cards */}

        {/* Marketplace + Pool selector — when shared pools exist, surface them
            as one-click shortcuts that pick all member marketplaces at once.
            This matches the user's mental model: a shared pool is a single
            inventory bucket, so the most natural unit of selection is the
            pool itself, not the individual marketplaces feeding into it. */}
        <DropWrapper open={mktOpen} onClose={()=>setMktOpen(false)} width={240}
          trigger={<CBtn label={mktLbl.length>22?mktLbl.slice(0,20)+"…":mktLbl} active={mktOpen} onClick={()=>{ setMktOpen(o=>!o); setViewOpen(false); setAlertOpen(false); setOverdueOpen(false); setPayDueOpen(false); }}/>}>
          {(() => {
            const sharedPools = (inventoryPools || []).filter(p => p.isShared && (p.marketplaceIds || []).length > 0);
            return (
              <>
                {sharedPools.length > 0 && (
                  <MSection label="🔗 Shared Pools">
                    {sharedPools.map(p => {
                      const ids = p.marketplaceIds || [];
                      // A pool is "selected" when the current selection
                      // exactly matches its marketplace set.
                      const sel = Array.isArray(selMkts)
                        && selMkts.length === ids.length
                        && ids.every(id => selMkts.includes(id));
                      return <MItem key={p.id} label={`${p.name} (${ids.length} mkts)`} selected={sel} radio={false}
                        onClick={() => ssm([...ids])} />;
                    })}
                  </MSection>
                )}
                <MSection label="Marketplace">
                  <MItem label="All Marketplaces" selected={selMkts==="all"||!Array.isArray(selMkts)||!selMkts.length} radio={false} onClick={()=>ssm("all")}/>
                  {marketplaces.map(m=>{ const sel=Array.isArray(selMkts)&&selMkts.includes(m.id); return <MItem key={m.id} label={m.name} selected={sel} radio={false} onClick={()=>{ if (selMkts==="all"||!Array.isArray(selMkts)) ssm([m.id]); else if (sel) { const n=selMkts.filter(x=>x!==m.id); ssm(n.length?n:"all"); } else ssm([...selMkts,m.id]); }}/> ; })}
                </MSection>
              </>
            );
          })()}
        </DropWrapper>

        {/* Marketplace filter notice — removed per user request */}

        {/* View */}
        <DropWrapper open={viewOpen} onClose={()=>setViewOpen(false)} width={188}
          trigger={<CBtn label={`View: ${viewLbl}`} active={viewOpen} onClick={()=>{ setViewOpen(o=>!o); setMktOpen(false); setAlertOpen(false); setOverdueOpen(false); setPayDueOpen(false); }}/>}>
          <MSection label="Granularity">
            {[["week","Week"],["month","Month"]].map(([v,l])=><MItem key={v} label={l} selected={view===v} onClick={()=>sv(v)}/>)}
          </MSection>
          <MenuSep/>
          <MSection label="Start from">
            {[["today","From Today"],["jan1","From Jan 1st"],["all","All Time"]].map(([v,l])=><MItem key={v} label={l} selected={chartFrom===v} onClick={()=>scf(v)}/>)}
          </MSection>
          <MenuSep/>
          <MSection label="Window">
            {[["90d","90 days"],["6m","6 months"],["1y","1 year"],["2y","2 years"],["all","All"]].map(([v,l])=><MItem key={v} label={l} selected={viewPeriod===v} onClick={()=>svp(v)}/>)}
          </MSection>
        </DropWrapper>

        {/* Planner toggle */}
        <button onClick={()=>{ ssd(!showDec); setMktOpen(false); setViewOpen(false); setAlertOpen(false); }} style={{ display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:RADIUS.md,fontSize:12,fontFamily:FONT,cursor:"pointer",background:showDec?"#bf5af218":"transparent",border:`1px solid ${showDec?"#bf5af2":T.border}`,color:showDec?"#bf5af2":T.textSub,fontWeight:showDec?700:400,transition:"all 0.2s" }}>
          <svg width="9" height="9" viewBox="0 0 9 9" style={{ flexShrink:0 }}><circle cx="4.5" cy="4.5" r="4.5" fill={showDec?"#bf5af2":T.textSub}/></svg>
          Planner {showDec?"ON":"OFF"}
          {showDec && decisions?.length > 0 && <span style={{ fontSize:10, opacity:0.7 }}>({decisions.filter(d=>d.impact).length} impacts)</span>}
        </button>

        {/* Cash buffer threshold */}
        <div style={{ display:"flex",alignItems:"center",gap:4 }} title="Cash Buffer — set a minimum cash balance to maintain. A red dashed line shows on the chart. Runway, alerts, and Min. Sales to Cover all warn at this level instead of zero. Most sellers keep 1-3 months of expenses as buffer.">
          <button onClick={()=>{
            const next = !showBuffer;
            setShowBuffer(next);
            if (!next) monthOps?.updateMonth("_appSettings", { ...(months?._appSettings||{}), bufferThreshold: 0 });
          }} style={{ display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:RADIUS.md,fontSize:12,fontFamily:FONT,cursor:"pointer",background:showBuffer?T.red+"18":"transparent",border:`1px solid ${showBuffer?T.red:T.border}`,color:showBuffer?T.red:T.textSub,fontWeight:showBuffer?700:400,transition:"all 0.2s" }}>
            <span style={{ fontSize:10 }}>---</span>
            Buffer {showBuffer?"ON":"OFF"}
          </button>
          {showBuffer && (
            <div style={{ display:"flex",alignItems:"center",gap:3 }}>
              <span style={{ fontSize:11,color:T.textSub }}>{currSym()}</span>
              <input type="number" value={bufferThreshold||""} placeholder="0"
                onChange={e => {
                  const v = parseFloat(e.target.value) || 0;
                  monthOps?.updateMonth("_appSettings", { ...(months?._appSettings||{}), bufferThreshold: v });
                }}
                style={{ width:70,background:T.surface,border:`1px solid ${T.red}`,borderRadius:RADIUS.sm,color:T.red,fontSize:12,fontWeight:700,padding:"4px 6px",outline:"none",fontFamily:FONT,textAlign:"center" }}/>
            </div>
          )}
        </div>

        {/* Orders dropdown */}
        <DropWrapper open={orderDropOpen} onClose={()=>setOrderDropOpen(false)} width={230} align="left"
          trigger={<CBtn label={`Orders: ${!showOrd ? "Off" : selectedOrderId==="all" ? "All" : orders.find(o=>o.id===selectedOrderId)?.number||"—"}`} active={orderDropOpen||showOrd} onClick={()=>{ setOrderDropOpen(o=>!o); setMktOpen(false); setViewOpen(false); setAlertOpen(false); }}/>}>
          <MSection label="Display">
            <MItem label="Off" selected={!showOrd} onClick={()=>{ sso(false); setSelectedOrderId("all"); setOrderDropOpen(false); }}/>
            <MItem label="All Orders" selected={showOrd && selectedOrderId==="all"} onClick={()=>{ sso(true); setSelectedOrderId("all"); setOrderDropOpen(false); }}/>
          </MSection>
          {orders.filter(o=>o.amount>0||o.orderDate).length > 0 && (
            <>
              <MenuSep/>
              <MSection label="Individual order">
                {orders.filter(o=>o.amount>0||o.orderDate).map(o=>{
                  const oc = oCM[o.id]||ORDER_COLORS[0];
                  const sel = showOrd && selectedOrderId===o.id;
                  return <MItem key={o.id} label={`${o.number||"Order"} · ${o.amount?currSym()+Math.round(o.amount).toLocaleString():""}`} selected={sel} color={oc.text} onClick={()=>{ sso(true); setSelectedOrderId(o.id); setOrderDropOpen(false); }}/>;
                })}
              </MSection>
            </>
          )}
        </DropWrapper>

        {/* Income Override removed — use Modifiers or Cash Calendar for manual adjustments */}

        {/* Scenario Planning */}
        <div style={{ display:"flex",alignItems:"center",gap:4 }}>
          <button onClick={()=>setScenarioOpen(!scenarioOpen)} style={{ display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:RADIUS.md,fontSize:12,fontFamily:FONT,cursor:"pointer",background:scenarioOpen?"#ef444418":"transparent",border:`1px solid ${scenarioOpen?"#ef4444":T.border}`,color:scenarioOpen?"#ef4444":T.textSub,fontWeight:scenarioOpen?700:400,transition:"all 0.2s" }}>
            📈 Scenarios {scenarioOpen?"ON":"OFF"}
          </button>
          {scenarioOpen && (<>
            {/* Mode toggle */}
            <div style={{ display:"flex",gap:2,background:T.surface,borderRadius:RADIUS.pill,padding:2 }}>
              {["brand","product"].map(m=>(
                <button key={m} onClick={()=>setScenarioMode(m)} style={{
                  padding:"4px 10px",borderRadius:RADIUS.pill,fontSize:10,fontWeight:700,
                  fontFamily:FONT,cursor:"pointer",border:"none",
                  background:scenarioMode===m?T.accent+"22":"transparent",
                  color:scenarioMode===m?T.accent:T.textSub,
                  transition:"all 0.15s",
                }}>{m==="brand"?"Brand-wide":"Per-product"}</button>
              ))}
            </div>
            {/* Brand-wide inputs */}
            {scenarioMode==="brand" && <>
              <div style={{ display:"flex",alignItems:"center",gap:3,fontSize:11 }}>
                <span style={{ color:T.red }}>Sales</span>
                <input type="number" value={scenarioSales} placeholder="-20" onChange={e=>setScenarioSales(e.target.value)}
                  style={{ width:45,background:T.surface,border:`1px solid ${T.red}`,borderRadius:RADIUS.sm,color:T.red,fontSize:11,fontWeight:700,padding:"3px 4px",outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                <span style={{ color:T.textSub }}>%</span>
              </div>
              <div style={{ display:"flex",alignItems:"center",gap:3,fontSize:11 }}>
                <span style={{ color:T.orange }}>Expenses</span>
                <input type="number" value={scenarioExpenses} placeholder="10" onChange={e=>setScenarioExpenses(e.target.value)}
                  style={{ width:45,background:T.surface,border:`1px solid ${T.orange}`,borderRadius:RADIUS.sm,color:T.orange,fontSize:11,fontWeight:700,padding:"3px 4px",outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                <span style={{ color:T.textSub }}>%</span>
              </div>
            </>}
            {/* Per-product adjustment builder */}
            {scenarioMode==="product" && <>
              <div style={{ display:"flex",alignItems:"center",gap:4,fontSize:11,flexWrap:"wrap" }}>
                <select value={adjMonth} onChange={e=>setAdjMonth(e.target.value)}
                  style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,fontSize:11,padding:"3px 4px",fontFamily:FONT }}>
                  <option value="">Month</option>
                  {visibleYears.slice().sort().flatMap(y =>
                    Array.from({length:12},(_,m)=>mkKey(y,m+1))
                  ).map(k=><option key={k} value={k}>{MONTH_NAMES[parseInt(k.split("-")[1])-1]} {k.split("-")[0]}</option>)}
                </select>
                <select value={adjMktId} onChange={e=>{setAdjMktId(e.target.value);setAdjProdId("")}}
                  style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,fontSize:11,padding:"3px 4px",fontFamily:FONT }}>
                  <option value="">Marketplace</option>
                  {(Array.isArray(selMkts)&&selMkts.length>0
                    ? marketplaces.filter(m=>selMkts.includes(m.id))
                    : marketplaces
                  ).map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                {adjMktId && (()=>{
                  const mkt=marketplaces.find(m=>m.id===adjMktId);
                  const prods=(mkt?.products||[]).filter(p=>p.pctOfMarket>0);
                  if(!prods.length) return <span style={{color:T.textSub}}>No products</span>;
                  return <select value={adjProdId} onChange={e=>setAdjProdId(e.target.value)}
                    style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,fontSize:11,padding:"3px 4px",fontFamily:FONT }}>
                    <option value="">Product</option>
                    {prods.map(p=><option key={p.id} value={p.id}>{p.name||p.id} ({Math.round(p.pctOfMarket*100)}%)</option>)}
                  </select>;
                })()}
                <input type="number" value={adjPct} placeholder="+30" onChange={e=>setAdjPct(e.target.value)}
                  style={{ width:42,background:T.surface,border:`1px solid ${T.accent}`,borderRadius:RADIUS.sm,color:T.accent,fontSize:11,fontWeight:700,padding:"3px 4px",outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                <span style={{ color:T.textSub }}>%</span>
                <button onClick={()=>{
                  if(!adjMonth||!adjMktId||!adjProdId||!adjPct) return;
                  if(productAdjustments.length>=10) return;
                  setProductAdjustments(a=>[...a,{id:Date.now(),monthKey:adjMonth,mktId:adjMktId,prodId:adjProdId,salesPct:parseFloat(adjPct)||0}]);
                  setAdjPct("");
                }} style={{
                  padding:"3px 8px",borderRadius:RADIUS.sm,fontSize:10,fontWeight:700,
                  fontFamily:FONT,cursor:"pointer",background:T.accent+"15",border:`1px solid ${T.accent}40`,color:T.accent,
                }}>+ Add</button>
              </div>
              {/* Active adjustment chips */}
              {productAdjustments.length>0 && (
                <div style={{ display:"flex",gap:4,flexWrap:"wrap",maxWidth:400 }}>
                  {productAdjustments.map(a=>{
                    const mkt=marketplaces.find(m=>m.id===a.mktId);
                    const prod=mkt?(mkt.products||[]).find(p=>p.id===a.prodId):null;
                    const moName=MONTH_NAMES[parseInt((a.monthKey||"").split("-")[1]||"1")-1]?.slice(0,3);
                    return <span key={a.id} style={{
                      display:"inline-flex",alignItems:"center",gap:3,
                      padding:"3px 8px",borderRadius:RADIUS.pill,fontSize:10,fontWeight:600,
                      background:a.salesPct>=0?T.green+"15":T.red+"15",
                      color:a.salesPct>=0?T.green:T.red,fontFamily:FONT,
                    }}>
                      {moName} · {prod?.name||a.prodId} · {a.salesPct>0?"+":""}{a.salesPct}%
                      <button onClick={()=>setProductAdjustments(arr=>arr.filter(x=>x.id!==a.id))}
                        style={{ background:"none",border:"none",color:"inherit",cursor:"pointer",fontSize:12,padding:0,lineHeight:1 }}>×</button>
                    </span>;
                  })}
                  {productAdjustments.length>=10 && <span style={{fontSize:9,color:T.orange}}>Max 10</span>}
                </div>
              )}
            </>}
          </>)}
        </div>

        {/* Simulate Order — moved to KPI grid */}

        {/* Add Transaction moved to Action Slot */}

        {/* PDF */}
        <button onClick={handlePDF} style={{ padding:"6px 12px",borderRadius:RADIUS.md,fontSize:12,fontFamily:FONT,cursor:"pointer",background:"transparent",border:`1px solid ${T.border}`,color:T.textSub }}>📄 PDF</button>

        {/* Export */}
        <button onClick={() => setExportOpen(true)} style={{ padding:"6px 12px",borderRadius:RADIUS.md,fontSize:12,fontFamily:FONT,cursor:"pointer",background:"transparent",border:`1px solid ${T.border}`,color:T.textSub }}>📤 Export</button>
        <button onClick={() => setChartFullscreen(f=>!f)} title={chartFullscreen?"Minimize chart (Esc)":"Expand chart to full screen"} style={{ padding:"6px 10px",borderRadius:RADIUS.md,fontSize:14,fontFamily:FONT,cursor:"pointer",background:chartFullscreen?T.accent+"18":"transparent",border:`1px solid ${chartFullscreen?T.accent:T.border}`,color:chartFullscreen?T.accent:T.textSub,lineHeight:1 }}>{chartFullscreen?"⊙":"⛶"}</button>
      </div>

      {/* Order warnings removed — no longer shown above chart */}

      {/* Chart — full viewport height, everything else scrolls below */}
      {activePanel==="chart" && (
        <div style={{ display:"flex", flexDirection:"column", gap:6, flex:chartFullscreen?"1 1 0%":undefined, minHeight:chartFullscreen?0:undefined }}>
          <style>{`@media(max-width:1024px)and(min-width:769px){.ch-chart-wrap:not(.ch-fs){height:45vh!important;min-height:280px!important}}@media(max-width:768px){.ch-chart-wrap:not(.ch-fs){height:40vh!important;min-height:240px!important;padding:8px 4px 4px!important;border-radius:8px!important}}`}</style>
          <div className={`ch-chart-wrap${chartFullscreen?" ch-fs":""}`} data-tour="chart" style={chartFullscreen ? { flex:"1 1 0%", minHeight:0, background:"transparent", border:"none", borderRadius:0, padding:0, overflow:"hidden", position:"relative" } : { height:"calc(100dvh - 280px)", minHeight:300, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"12px 8px 8px", overflow:"hidden", position:"relative" }}>
            <ChartWithMarkers
              chartData={finalChartData} decDots={decDots} orderDots={selectedOrderId==="all" ? orderDots : []}
              showDec={showDec} showOrd={showOrd}
              decisions={decisions} orders={orders}
              yearKeys={yearKeys} T={T}
              onNavigate={onNavigate}
              todayKey={todayKey}
              monthKeys={monthKeys}
              ghostLine={ghostLine}
              onGhostDecHover={setGhostDecId}
              onDecisionSelect={id => setSelectedDecId(id)}
              onDecisionClick={(id, x, y) => setSelectedDecPos({ x, y })}
              selectedOrderData={selectedOrderData}
              showBuffer={showBuffer}
              bufferThreshold={bufferThreshold}
              simOpen={simOpen}
              simAnalysis={simAnalysis}
              simReorderInfo={simReorderInfo}
              dateFormat={dateFormat}
              scenarioOpen={scenarioOpen}
            />
            {/* Decision affordability floating card — inside chart wrapper for positioning */}
            {selectedDecId && selectedDecPos && (()=>{
              const dec = decisions.find(d=>d.id===selectedDecId);
              if (!dec) return null;
              const analysis = analyzeDecisionAffordability({ decision:dec, dailyData, recurringExpenses, modifiers, months, marketplaces, bufferThreshold, today:TODAY });
              if (!analysis) return null;
              // Position: to the LEFT of the decision dot, clamped to chart area
              const cardW = 300;
              // Place card to the left of the dot. If not enough space on left, flip to right.
              const dotX = selectedDecPos.x;
              const containerW = typeof window !== "undefined" ? window.innerWidth : 800;
              const leftOfDot = dotX - cardW - 16;
              const rightOfDot = dotX + 16;
              const useLeft = leftOfDot >= 8;
              const cardLeft = useLeft ? Math.max(8, leftOfDot) : Math.min(rightOfDot, containerW - cardW - 8);
              return (
                <div style={{ position:"absolute", top:8, left:cardLeft, width:cardW, maxHeight:"calc(100% - 16px)", overflowY:"auto", background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, boxShadow:`0 8px 32px rgba(0,0,0,0.4)`, zIndex:50, padding:"14px 16px" }}
                  onClick={e=>e.stopPropagation()}>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:T.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{dec.icon||"📌"} {dec.name||"Decision"}</div>
                    <button onClick={()=>{setSelectedDecId(null);setSelectedDecPos(null);}} style={{ background:"none", border:"none", color:T.textSub, cursor:"pointer", fontSize:16, padding:"0 0 0 8px" }}>✕</button>
                  </div>
                  {(dec.date?.day||dec.date?.month||dec.date?.quarter) && <div style={{ fontSize:10, color:T.textSub, marginBottom:2 }}>📅 {dec.date?.prec==="day"?dec.date.day:dec.date?.prec==="month"?dec.date.month:dec.date?.quarter||""}</div>}
                  {dec.notes && <div style={{ fontSize:10, color:T.textSub, fontStyle:"italic", marginBottom:6, lineHeight:1.4 }}>{dec.notes.slice(0,120)}{dec.notes.length>120?"…":""}</div>}
                  {/* Verdict */}
                  <div style={{ padding:"8px 10px", background:analysis.canAfford?T.green+"12":T.red+"12", border:`1px solid ${analysis.canAfford?T.green:T.red}33`, borderRadius:RADIUS.sm, marginBottom:8 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:analysis.canAfford?T.green:T.red }}>
                      {analysis.canAfford ? "✅ You can afford this" : "❌ Drops below buffer"}
                    </div>
                    {!analysis.canAfford && analysis.breachDate && <div style={{ fontSize:10, color:T.red, marginTop:2 }}>Breaches on {analysis.breachDate.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</div>}
                    {analysis.lowestPoint && <div style={{ fontSize:10, color:T.textSub, marginTop:2 }}>Lowest: {fmtFull(analysis.lowestPoint.balance)} on {analysis.lowestPoint.date.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</div>}
                  </div>
                  {/* Ghost line impact — from hover data */}
                  {ghostLine && ghostLine.points.length > 0 && (()=>{
                    const isNetPos = ghostLine.netImpact >= 0;
                    return (
                      <div style={{ padding:"6px 10px", background:isNetPos?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)", border:`1px solid ${isNetPos?"rgba(16,185,129,0.2)":"rgba(239,68,68,0.2)"}`, borderRadius:RADIUS.sm, marginBottom:8 }}>
                        <div style={{ fontSize:9, fontWeight:700, color:isNetPos?T.green:T.red, textTransform:"uppercase", letterSpacing:0.3, marginBottom:4 }}>
                          {isNetPos ? "Profitable — removing would hurt" : "Cost — removing would help"}
                        </div>
                        <div style={{ display:"flex", gap:12 }}>
                          {ghostLine.totalIncome>0&&<div><div style={{ fontSize:9, color:T.textSub }}>Income</div><div style={{ fontSize:12, fontWeight:800, color:T.green }}>+{currSym()}{ghostLine.totalIncome.toLocaleString()}</div></div>}
                          {ghostLine.totalCost>0&&<div><div style={{ fontSize:9, color:T.textSub }}>Costs</div><div style={{ fontSize:12, fontWeight:800, color:T.red }}>−{currSym()}{ghostLine.totalCost.toLocaleString()}</div></div>}
                          <div><div style={{ fontSize:9, color:T.textSub }}>Net</div><div style={{ fontSize:12, fontWeight:800, color:ghostLine.netImpact>=0?T.green:T.red }}>{ghostLine.netImpact>=0?"+":"−"}{currSym()}{Math.abs(ghostLine.netImpact).toLocaleString()}</div></div>
                        </div>
                        <div style={{ fontSize:9, color:T.textSub, marginTop:3, fontStyle:"italic" }}>Dashed line = cashflow without this decision</div>
                      </div>
                    );
                  })()}
                  {/* Breakdown */}
                  {analysis.impactBreakdown.length > 0 && <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:4 }}>Impact</div>
                    {analysis.impactBreakdown.map((item,i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"4px 8px", background:T.surface, borderRadius:RADIUS.sm, marginBottom:2, borderLeft:`2px solid ${item.direction==="in"?T.green:T.red}`, fontSize:11 }}>
                        <span style={{ color:T.text }}>{item.label}</span>
                        <span style={{ fontWeight:700, color:item.direction==="in"?T.green:T.red }}>{item.direction==="in"?"+":"-"}{fmtFull(Math.abs(item.monthlyAmount))}{item.type.startsWith("one_time")?"":"/mo"}</span>
                      </div>
                    ))}
                  </div>}
                  {/* Net */}
                  <div style={{ display:"flex", gap:6, marginBottom:8 }}>
                    {analysis.monthlyExpenses>0&&<div style={{ flex:1, padding:"6px 8px", background:T.bg, borderRadius:RADIUS.sm }}>
                      <div style={{ fontSize:9, color:T.textSub, textTransform:"uppercase" }}>Expenses</div>
                      <div style={{ fontSize:13, fontWeight:700, color:T.red }}>-{fmtFull(analysis.monthlyExpenses)}/mo</div>
                    </div>}
                    <div style={{ flex:1, padding:"6px 8px", background:T.bg, borderRadius:RADIUS.sm }}>
                      <div style={{ fontSize:9, color:T.textSub, textTransform:"uppercase" }}>Net</div>
                      <div style={{ fontSize:13, fontWeight:700, color:analysis.netMonthly>=0?T.green:T.red }}>{analysis.netMonthly>=0?"+":""}{fmtFull(analysis.netMonthly)}/mo</div>
                    </div>
                  </div>
                  {/* How calculated */}
                  <details style={{ fontSize:10, color:T.textSub, marginBottom:8 }}>
                    <summary style={{ cursor:"pointer", fontWeight:600 }}>How is this calculated?</summary>
                    <div style={{ padding:"6px 0", lineHeight:1.6 }}>
                      Dashed line = cashflow without this decision. Monthly expenses = linked recurring costs. Income change = modifier % × targets × payout. Buffer: {bufferThreshold>0?fmtFull(bufferThreshold):"not set"}.
                    </div>
                  </details>
                  {/* Actions */}
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>{onNavigate?.("planner",{decisionId:dec.id});setSelectedDecId(null);setSelectedDecPos(null);}}
                      style={{ flex:1, padding:"7px", background:T.accent+"15", border:`1px solid ${T.accent}30`, borderRadius:RADIUS.sm, color:T.accent, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>View in Planner</button>
                    <button onClick={()=>{setSelectedDecId(null);setSelectedDecPos(null);}}
                      style={{ padding:"7px 14px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.textSub, fontSize:11, cursor:"pointer", fontFamily:FONT }}>Close</button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Breakdown table — hidden in fullscreen */}
          {!chartFullscreen && <div style={{ fontSize:14, fontWeight:700, color:T.text, letterSpacing:-0.3, marginBottom:8, marginTop:4, textAlign:"center", borderBottom:`1px solid ${T.border}`, paddingBottom:8 }}>Breakdown</div>}
          {!chartFullscreen && (view==="month" || view==="week" || view==="day") && (
            <div data-tour="monthly-table" style={{ border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, overflow:"hidden", marginBottom:16, background:T.card }}>
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
              <table id="ch-monthly-table" style={{ width:"100%", minWidth: isMobile ? 400 : "auto", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
                <thead>
                  <tr>
                    {[view==="month"?"Month":view==="week"?"Week":"Day","Income","Expenses","Balance","Δ"].map(h=>(
                      <th key={h} style={{ padding:"8px 14px",textAlign:h==="Month"||h==="Week"||h==="Day"?"left":"right",color:T.textSub,fontWeight:600,borderBottom:`1px solid ${T.border}`,fontSize:10,letterSpacing:0.5,textTransform:"uppercase",background:T.card }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((m,i,arr)=>{
                    const prev=i===0?availableToday:arr[i-1].balance, delta=m.balance-prev;
                    // Navigate to Cash Calendar when clicking a row
                    function handleRowClick() {
                      if (view === "month" && m.key) {
                        onNavigate?.("cashflow", { month: m.key, ts: Date.now() });
                      } else if (view === "week" && m.year && m.month) {
                        // Navigate to month containing this week, and select the week
                        const mKey = mkKey(m.year, m.month);
                        // Find which week number this is in the month (1-4)
                        const weekLabel = m.label || "";
                        const weekNum = parseInt(weekLabel.match(/W(\d+)/)?.[1]) || null;
                        onNavigate?.("cashflow", { month: mKey, week: weekNum, ts: Date.now() });
                      } else if (view === "day" && m.year && m.month && m.day) {
                        const mKey = mkKey(m.year, m.month);
                        const dayStr = `${String(m.day).padStart(2,"0")}.${String(m.month).padStart(2,"0")}.${m.year}`;
                        onNavigate?.("cashflow", { month: mKey, customDay: dayStr, ts: Date.now() });
                      }
                    }
                    return (
                      <GlowRow key={m.key||m.label||i} T={T} onClick={handleRowClick}>
                        <td style={{ padding:"7px 14px",color:T.text,fontWeight:600 }}>{m.label}</td>
                        <td style={{ padding:"7px 14px",textAlign:"right",color:T.green }}>{fmt(m.income)}</td>
                        <td style={{ padding:"7px 14px",textAlign:"right",color:T.red }}>{fmt(m.expenses)}</td>
                        <td style={{ padding:"7px 14px",textAlign:"right",color:m.balance>=0?T.accent:T.red,fontWeight:700 }}>{fmt(m.balance)}</td>
                        <td style={{ padding:"7px 14px",textAlign:"right",color:delta>=0?T.green:T.red }}>{delta>=0?"+":""}{fmt(delta)}</td>
                      </GlowRow>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}
      </div>)}{/* end fullscreen wrapper */}

      {/* Export modal */}
      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        months={months}
        orders={orders}
        marketplaces={marketplaces}
        visibleYears={visibleYears}
        recurringIncome={recurringIncome}
        recurringExpenses={recurringExpenses}
        partners={partners}
        deliveryCreditLimit={deliveryCreditLimit}
        fbmSettings={fbmSettings}
        account={account}
      />

      {/* Add Transaction Modal */}
      <AddTransactionModal
        open={addTxOpen}
        onClose={()=>setAddTxOpen(false)}
        defaultType="expense"
        defaultCategory="general"
        selectedMonth={currentKey}
        visibleYears={visibleYears}
        recurringIncomeOps={recurringIncomeOps}
        recurringExpenseOps={recurringExpenseOps}
        setPartners={setPartners}
        partners={partners}
        monthOps={monthOps}
        onAddDecision={onAddDecision}
      />

      {/* Units override delta prompt */}
      {/* Edit Initial Stock sheet */}
      <BottomSheet open={editInitialStockSheet} onClose={()=>setEditInitialStockSheet(false)}
        title="📦 Edit Initial Stock" height="auto" maxWidth={440}>
        <div style={{ padding:"4px 0 16px" }}>
          <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6, marginBottom:16 }}>
            This is the starting inventory you entered during setup. The app tracks stock automatically from here — adding units when supplier orders arrive and subtracting units as you sell. Only change this if your actual count has drifted significantly.
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Units in stock</div>
            <input autoFocus type="number" value={stockUnitsInput} onChange={e=>setStockUnitsInput(e.target.value)}
              placeholder="2000" style={{ width:"100%", background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:18, fontWeight:700, padding:"12px 14px", outline:"none", fontFamily:FONT, boxSizing:"border-box" }}/>
          </div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Cost per unit (all-in)</div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:18, color:T.orange, fontWeight:700 }}>{currSym()}</span>
              <input type="number" value={stockCostInput} onChange={e=>setStockCostInput(e.target.value)}
                placeholder="8.00" style={{ flex:1, background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:18, fontWeight:700, padding:"12px 14px", outline:"none", fontFamily:FONT }}/>
            </div>
          </div>
          <button onClick={()=>{ if(setInitialStock) setInitialStock(parseInt(stockUnitsInput)||0, parseFloat(stockCostInput)||0, "Manual correction"); setEditInitialStockSheet(false); }}
            style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, fontFamily:FONT, cursor:"pointer", boxShadow:"0 8px 24px rgba(139,92,246,0.35)" }}>
            Save
          </button>
        </div>
      </BottomSheet>

      {/* KPI snapshot history modal — opened from any KPI card's 📊 button */}
      <KpiHistoryModal
        open={!!kpiHistory}
        onClose={() => setKpiHistory(null)}
        kpiId={kpiHistory}
        dataKey={dataKey}
      />

      {/* Batch arrival confirmation — auto-pops when supplier-order container
          batches reach their arrivalDate. The user confirms once per batch
          so the daily stock snapshot can absorb the inflow without
          double-counting. */}
      <BatchArrivalDialog orders={orders} orderOps={orderOps} marketplaces={marketplaces} />

      {/* Available Today history */}
      <BottomSheet open={showBalanceHistory} onClose={()=>{setShowBalanceHistory(false);setHistoryLimit(50);}}
        title="💰 Available Today — History" height="70vh">
        <div style={{ padding:"4px 0 12px" }}>
          {availableTodayHistory.length === 0 ? (
            <div style={{ fontSize:13, color:T.textSub, textAlign:"center", padding:20 }}>No history yet. Changes are logged when you update your balance or mark expenses as paid.</div>
          ) : (
            <>
              {availableTodayHistory.slice(0, historyLimit).map((entry, i) => {
                const d = new Date(entry.date);
                const dateStr = isNaN(d.getTime()) ? "—" : `${d.getDate()}.${d.getMonth()+1}.${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
                const delta = entry.delta || 0;
                return (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:`1px solid ${T.border}22`, fontSize:12 }}>
                    <span style={{ color:T.textSub, fontSize:10, width:100, flexShrink:0, fontVariantNumeric:"tabular-nums" }}>{dateStr}</span>
                    <span style={{ flex:1, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{entry.reason || "Manual edit"}</span>
                    <span style={{ color:delta>=0?T.green:T.red, fontWeight:700, flexShrink:0, fontVariantNumeric:"tabular-nums" }}>
                      {delta>=0?"+":""}{currSym()}{Math.abs(delta).toLocaleString()}
                    </span>
                    <span style={{ color:T.textSub, fontSize:10, flexShrink:0, fontVariantNumeric:"tabular-nums" }}>
                      → {currSym()}{(entry.newValue||0).toLocaleString()}
                    </span>
                  </div>
                );
              })}
              {historyLimit < availableTodayHistory.length && (
                <button onClick={()=>setHistoryLimit(l=>Math.min(l+50, 200))}
                  style={{ display:"block", margin:"12px auto 0", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, padding:"8px 20px", fontSize:12, color:T.accent, cursor:"pointer", fontFamily:FONT, fontWeight:600 }}>
                  Load more ({Math.min(availableTodayHistory.length - historyLimit, 50)} remaining)
                </button>
              )}
            </>
          )}
        </div>
      </BottomSheet>

      {/* Adjust Available Today sheet — DELTA-based correction. The user
          enters how much to ADD to (or subtract from) the current balance,
          NOT a new total. e.g. "+1000" pushes today's balance up by 1000.
          A negative number subtracts. The new total is computed and saved. */}
      <BottomSheet open={editInitialSheet} onClose={()=>setEditInitialSheet(false)}
        title="🔧 Adjust Available Today" height="auto" maxWidth={440}>
        <div style={{ padding:"4px 0 16px" }}>
          <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6, marginBottom:16 }}>
            By how much do you want to adjust the balance across all your bank accounts? Enter a positive number to add (e.g. <strong style={{ color:T.text }}>1000</strong> means +{currSym()}1000) or a negative number to subtract. The current balance is updated and the change is saved in history.
          </div>
          <div style={{ marginBottom:12, padding:10, background:T.surface, borderRadius:10, border:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13 }}>
            <span style={{ color:T.textSub }}>Current balance:</span>
            <span style={{ fontWeight:700, color:availableToday >= 0 ? T.green : T.red, fontFamily:FONT }}>
              {currSym()}{Number(availableToday || 0).toLocaleString()}
            </span>
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Adjustment amount</div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:18, color:T.accent, fontWeight:700 }}>{currSym()}</span>
              <input autoFocus type="number" value={balanceInput} onChange={e=>setBalanceInput(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){
                  const delta = parseFloat(balanceInput) || 0;
                  if (delta !== 0) setAvailableToday(Number(availableToday || 0) + delta, `Manual adjustment ${delta>0?"+":""}${currSym()}${delta}`);
                  setBalanceInput("");
                  setEditInitialSheet(false);
                }}}
                placeholder="e.g. 1000 or -250"
                style={{ flex:1, background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:20, fontWeight:700, padding:"12px 14px", outline:"none", fontFamily:FONT }}/>
            </div>
          </div>
          {balanceInput && parseFloat(balanceInput) !== 0 && (
            <div style={{ marginBottom:14, padding:10, background:T.accent+"10", border:`1px solid ${T.accent}40`, borderRadius:10, fontSize:13, textAlign:"center" }}>
              <span style={{ color:T.textSub }}>New balance: </span>
              <span style={{ fontWeight:800, color:T.accent }}>
                {currSym()}{(Number(availableToday || 0) + parseFloat(balanceInput)).toLocaleString()}
              </span>
            </div>
          )}
          <button onClick={()=>{
            const delta = parseFloat(balanceInput) || 0;
            if (delta !== 0) setAvailableToday(Number(availableToday || 0) + delta, `Manual adjustment ${delta>0?"+":""}${currSym()}${delta}`);
            setBalanceInput("");
            setEditInitialSheet(false);
          }}
            style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, fontFamily:FONT, cursor:"pointer", boxShadow:"0 8px 24px rgba(139,92,246,0.35)" }}>
            Apply Adjustment
          </button>
        </div>
      </BottomSheet>

      {/* Order Simulator */}
      {/* Legacy Order Simulator BottomSheet — REMOVED. Sim now lives inline as the megasim KPI card. */}
      {false && <BottomSheet open={false} onClose={()=>{}} title="🧪 Order Simulator" height="90vh" maxWidth={560}>
        <div style={{ display:"flex", flexDirection:"column", gap:14, padding:"4px 0 16px" }}>
          <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6 }}>
            Sketch a hypothetical order to see if you can afford it. No data is saved until you create it.
          </div>

          {/* Amount + Split */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Order Amount</div>
              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:12, color:T.textSub }}>{currSym()}</span>
                <input type="number" value={simOrder.amount} placeholder="20000" onChange={e=>setSimOrder(s=>({...s, amount:e.target.value}))}
                  style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:15, fontWeight:700, padding:"8px 10px", outline:"none", fontFamily:FONT }}/>
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Payment Split</div>
              <div style={{ display:"flex", gap:4 }}>
                {[["30/70",30,70],["50/50",50,50],["100/0",100,0]].map(([lbl,a,b])=>(
                  <button key={lbl} onClick={()=>setSimOrder(s=>({...s, split:lbl, pt1Pct:a, pt2Pct:b}))}
                    style={{ flex:1, padding:"8px 4px", borderRadius:RADIUS.sm, fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:simOrder.split===lbl?700:400,
                      background:simOrder.split===lbl?T.accent+"18":"transparent", border:`1px solid ${simOrder.split===lbl?T.accent:T.border}`, color:simOrder.split===lbl?T.accent:T.textSub }}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Deposit Date ({simOrder.pt1Pct}%)</div>
              <DateInp value={simOrder.pt1Date} onChange={v=>setSimOrder(s=>({...s, pt1Date:v}))}/>
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Arrival Date</div>
              <DateInp value={simOrder.arrivalDate} onChange={v=>setSimOrder(s=>({...s, arrivalDate:v}))}/>
            </div>
          </div>

          {simOrder.pt2Pct > 0 && <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Balance Payment ({simOrder.pt2Pct}%)</div>
              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                <input type="number" value={simOrder.pt2Days} onChange={e=>setSimOrder(s=>({...s, pt2Days:e.target.value}))}
                  style={{ width:50, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, padding:"8px 6px", outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                <span style={{ fontSize:11, color:T.textSub }}>days after arrival</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>In-Stock Delay</div>
              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                <input type="number" value={simOrder.inStockDays} onChange={e=>setSimOrder(s=>({...s, inStockDays:e.target.value}))}
                  style={{ width:50, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, padding:"8px 6px", outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                <span style={{ fontSize:11, color:T.textSub }}>days after arrival</span>
              </div>
            </div>
          </div>}

          {/* Units + Sell-through */}
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Units in Order</div>
            <input type="number" value={simOrder.units} placeholder="1000" onChange={e=>setSimOrder(s=>({...s, units:e.target.value}))}
              style={{ width:120, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:15, fontWeight:700, padding:"8px 10px", outline:"none", fontFamily:FONT }}/>
          </div>

          {parseInt(simOrder.units) > 0 && (
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>When do you expect to sell these? (units per month)</div>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {Array.from({length:12}, (_,i) => {
                  const d = new Date(TODAY.getFullYear(), TODAY.getMonth() + i, 1);
                  const k = mkKey(d.getFullYear(), d.getMonth()+1);
                  const v = simOrder.sellMonths[k] || 0;
                  return (
                    <div key={k} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                      <span style={{ fontSize:9, color:T.textSub }}>{MONTH_NAMES[d.getMonth()].slice(0,3)}</span>
                      <input type="number" value={v||""} placeholder="0"
                        onChange={e=>setSimOrder(s=>({...s, sellMonths:{...s.sellMonths, [k]:parseInt(e.target.value)||0}}))}
                        style={{ width:42, background:v?T.green+"12":T.surface, border:`1px solid ${v?T.green:T.border}`, borderRadius:RADIUS.sm, color:v?T.green:T.textSub, fontSize:11, fontWeight:700, padding:"4px 2px", outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize:10, color:T.textSub, marginTop:4 }}>Total planned: {Object.values(simOrder.sellMonths).reduce((s,v)=>s+(v||0),0)} of {simOrder.units} units</div>
            </div>
          )}

          {/* Results */}
          {simAnalysis?.analysis && (
            <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:14, display:"flex", flexDirection:"column", gap:10 }}>
              {/* Verdict */}
              <div style={{ padding:"12px 16px", background:simAnalysis.analysis.overall.canAfford?T.green+"12":T.red+"12", border:`1px solid ${simAnalysis.analysis.overall.canAfford?T.green:T.red}33`, borderRadius:RADIUS.md }}>
                <div style={{ fontSize:15, fontWeight:700, color:simAnalysis.analysis.overall.canAfford?T.green:T.red }}>
                  {simAnalysis.analysis.overall.canAfford ? "✅ Yes, you can afford this order" : "❌ No — some payments exceed your buffer"}
                </div>
              </div>

              {/* Cost vs Income */}
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                <div style={{ flex:1, minWidth:120, padding:"8px 12px", background:T.bg, borderRadius:RADIUS.sm }}>
                  <div style={{ fontSize:10, color:T.textSub, textTransform:"uppercase" }}>Total Cost</div>
                  <div style={{ fontSize:16, fontWeight:700, color:T.red }}>{fmtFull(simAnalysis.totalCost)}</div>
                </div>
                {simAnalysis.sellTotal > 0 && <div style={{ flex:1, minWidth:120, padding:"8px 12px", background:T.bg, borderRadius:RADIUS.sm }}>
                  <div style={{ fontSize:10, color:T.textSub, textTransform:"uppercase" }}>Expected Revenue</div>
                  <div style={{ fontSize:16, fontWeight:700, color:T.green }}>{fmtFull(simAnalysis.sellTotal)}</div>
                  <div style={{ fontSize:12, color:T.textSub }}>{simOrder.units} units × {currSym()}{simAnalysis.weightedPayout}/unit</div>
                </div>}
                <div style={{ flex:1, minWidth:120, padding:"8px 12px", background:T.bg, borderRadius:RADIUS.sm }}>
                  <div style={{ fontSize:10, color:T.textSub, textTransform:"uppercase" }}>Net Outcome</div>
                  <div style={{ fontSize:16, fontWeight:700, color:(simAnalysis.sellTotal-simAnalysis.totalCost)>=0?T.green:T.red }}>
                    {(simAnalysis.sellTotal-simAnalysis.totalCost)>=0?"+":""}{fmtFull(simAnalysis.sellTotal-simAnalysis.totalCost)}
                  </div>
                </div>
              </div>

              {/* Per-payment */}
              {simAnalysis.analysis.payments.map((p,i) => p.balanceBefore===null ? (
                <div key={i} style={{ padding:"8px 12px", background:T.surface, borderRadius:RADIUS.sm, borderLeft:`3px solid ${T.textMuted}` }}>
                  <div style={{ fontSize:12, fontWeight:600, color:T.textSub }}>{p.label}</div>
                  <div style={{ fontSize:11, color:T.textMuted }}>Date not set</div>
                </div>
              ) : (
                <div key={i} style={{ padding:"10px 12px", background:T.surface, borderRadius:RADIUS.sm, borderLeft:`3px solid ${p.aboveBuffer?T.green:T.red}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:T.text }}>{p.label} — {p.dateStr}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{fmtFull(p.amount)}</span>
                  </div>
                  <div style={{ fontSize:11, color:T.textSub }}>
                    Balance: {fmtFull(p.balanceBefore)} → <strong style={{ color:p.aboveBuffer?T.green:T.red }}>{fmtFull(p.balanceAfter)}</strong>
                    {p.aboveBuffer ? <span style={{ color:T.green, marginLeft:6 }}>✓ above buffer</span> : <span style={{ color:T.red, marginLeft:6 }}>✗ {fmtFull(p.shortfall)} short</span>}
                  </div>
                  {p.suggestedDate && <div style={{ fontSize:11, color:T.accent, marginTop:4 }}>💡 Better date: {p.suggestedDate.dateStr} (balance {fmtFull(p.suggestedDate.balance)})</div>}
                </div>
              ))}

              {/* Suggestions */}
              {simAnalysis.analysis.suggestions.length > 0 && (
                <div style={{ padding:"10px 12px", background:T.accent+"08", borderRadius:RADIUS.sm, border:`1px solid ${T.accent}22` }}>
                  <div style={{ fontSize:10, fontWeight:700, color:T.accent, textTransform:"uppercase", marginBottom:6 }}>Suggestions</div>
                  {simAnalysis.analysis.suggestions.map((s,i) => <div key={i} style={{ fontSize:12, color:T.text, lineHeight:1.5, marginBottom:4 }}>💡 {s.message}</div>)}
                </div>
              )}

              {/* How is this calculated */}
              <details style={{ fontSize:11, color:T.textSub }}>
                <summary style={{ cursor:"pointer", fontWeight:600 }}>How is this calculated?</summary>
                <div style={{ padding:"8px 0", lineHeight:1.7 }}>
                  • Balance projection from your current Dashboard cashflow<br/>
                  • Buffer threshold: {bufferThreshold>0?fmtFull(bufferThreshold):"not set (checking against "+currSym()+"0)"}<br/>
                  • This is a simulation — your actual data is not modified<br/>
                  • "Balance before" = your projected balance on that date if this order didn't exist<br/>
                  • Revenue = sell-through units × {currSym()}{simAnalysis.weightedPayout} weighted payout per unit<br/>
                  • Suggested dates scan ±14 days for the day with highest projected balance
                </div>
              </details>

              {/* Create order button */}
              <button onClick={()=>{ onNavigate?.("orders"); setSimOpen(false); }}
                style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, fontFamily:FONT, cursor:"pointer", boxShadow:"0 8px 24px rgba(139,92,246,0.35)", marginTop:4 }}>
                Create This Order →
              </button>
            </div>
          )}

          {!simAnalysis?.analysis && parseFloat(simOrder.amount) > 0 && (
            <div style={{ padding:"12px 16px", background:T.surface, borderRadius:RADIUS.md, fontSize:12, color:T.textSub, textAlign:"center" }}>
              Enter deposit date and arrival date to see the affordability analysis
            </div>
          )}
        </div>
      </BottomSheet>}

      {/* Decision affordability panel is now rendered as floating card inside chart wrapper */}
    </div>
  );
}
